// Expansion Intelligence — W3 Consultant Brain layer.
//
// Three-tier architecture per the W3 directive:
//   1) Executive Intelligence — conclusions a business owner needs
//   2) Market Intelligence    — what the catchment looks like
//   3) Evidence & Sources     — raw data + ledger (auditable)
//
// Every conclusion in the Executive tier is composed deterministically
// from the same live Google datasets shown in the Evidence tier. No
// generic AI text; every line cites a number a consultant would point at.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Sparkles, ShieldCheck, Gauge, MapPin, Search, RefreshCw,
  AlertTriangle, CheckCircle2, Database, ExternalLink,
  TrendingUp, Target, Lightbulb, AlertCircle, Briefcase, FileText,
  ClipboardList, Pencil, Save, X, BarChart3, ArrowLeft, ArrowRight,
  MoreVertical, Trash2,
} from 'lucide-react';
// iter216 — Restaurant Intelligence Layer (Click Intel Card + Deep Dive + Influence + Sparkline + SSE)
import {
  computeInfluenceScore, CompetitorIntelCard, CompetitorDeepDive,
  InfluenceSparkline, useEnrichStream,
} from '../components/expansion/CompetitorIntelligence';
// iter221 — Apify enrichment + provider status panel
import { ApifyEnrichmentPanel } from '../components/expansion/ApifyEnrichmentPanel';
// iter225 — Enriched competitor visibility (table + drawer)
import EnrichedCompetitorsView from '../components/expansion/EnrichedCompetitorsView';
// ITER264 — Shared Expansion Project session for City Intel ↔ Site Intel continuity.
import {
  getSession, clearSession,
} from '../utils/expansionSession';
// ITER283c — Sprint 1c: consolidated ranked brief-form (cuisines, formats, segments).
import BriefRankedFieldsAdapter from '../components/brief/BriefRankedFieldsAdapter';

const API = process.env.REACT_APP_BACKEND_URL;
const TIERS = [
  { key: 'market',    label: 'Market Intelligence',
    sub: 'Competitors · cuisine density · anchors · DNA',  icon: Target },
  { key: 'project',   label: 'Concept ↔ Market Fit',
    sub: 'Does the concept fit the market?',               icon: Target },
  { key: 'executive', label: 'Executive Summary',
    sub: 'Consultant + investor conclusions',              icon: Briefcase },
  { key: 'feasibility', label: 'Project Feasibility',
    sub: 'Can the project realistically be built?',        icon: ShieldCheck },
  { key: 'financial', label: 'Financial Viability',
    sub: 'Will this project make money?',                  icon: BarChart3 },
  { key: 'evidence',  label: 'Evidence & Sources',
    sub: 'Raw datasets · connectivity · ledger',           icon: Database },
];

function Pill({ tone = 'neutral', children, testid }) {
  const map = {
    ok:'bg-emerald-50 text-emerald-700 border-emerald-200',
    warn:'bg-amber-50 text-amber-700 border-amber-200',
    err:'bg-rose-50 text-rose-700 border-rose-200',
    neutral:'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${map[tone] || map.neutral}`}>
      {children}
    </span>
  );
}

/* ─── LANDING GATE (iter211) ───────────────────────────────────────
   Project Hub shown before workspace entry. Lists existing projects
   with status badges (Draft / In Progress / Analysed) for one-click
   resume, alongside "Start New Analysis". */
function LandingGate({ API, headers, onStartNew, onOpenSwitcher, onOpenProject }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(false);
  // Delete-affordance state — kept minimal on purpose. `menuFor` holds
  // the project_id whose three-dot menu is open; `confirmFor` holds
  // the project object pending destructive confirmation.
  const [menuFor,    setMenuFor]    = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [deleteErr,  setDeleteErr]  = useState(null);
  const [showAll,    setShowAll]    = useState(false);

  useEffect(() => {
    if (!API) return;
    setLoading(true);
    axios.get(`${API}/api/expansion-intelligence/projects`, { headers })
      .then(r => setProjects((r.data?.data?.projects || []).filter(p => p.status !== 'archived')))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [API, headers]);

  async function handleDelete(p) {
    setDeleting(true); setDeleteErr(null);
    try {
      await axios.delete(
        `${API}/api/expansion-intelligence/projects/${p.project_id}`,
        { headers },
      );
      // Optimistic + confirmed removal without a page reload.
      setProjects(prev => prev.filter(x => x.project_id !== p.project_id));
      setConfirmFor(null);
      setMenuFor(null);
    } catch (e) {
      setDeleteErr(
        e?.response?.data?.detail
        || e?.message
        || 'Delete failed — please try again.',
      );
    } finally {
      setDeleting(false);
    }
  }

  function deriveStatus(p) {
    if (!p) return 'DRAFT';
    if (p.status === 'archived') return 'ARCHIVED';
    if (p.has_analysis) return 'ANALYSED';
    if (p.address) return 'IN PROGRESS';
    return 'DRAFT';
  }
  const STATUS_STYLES = {
    'DRAFT':       'bg-slate-100 text-slate-700 border-slate-200',
    'IN PROGRESS': 'bg-sky-50 text-sky-700 border-sky-200',
    'ANALYSED':    'bg-emerald-50 text-emerald-700 border-emerald-200',
    'ARCHIVED':    'bg-rose-50 text-rose-700 border-rose-200',
  };
  const visibleProjects = showAll ? projects : projects.slice(0, 9);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm" data-testid="ei-landing-gate">
      {/* Header — heading + one-line subtitle (Stage-1/Stage-2 sentence
          compressed to a subtitle so the three action cards are the
          visual centre of the page). */}
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-2xl bg-slate-900 text-amber-300"><Sparkles className="w-6 h-6"/></div>
        <div className="flex-1">
          <h2 className="text-2xl font-semibold text-slate-900" data-testid="ei-landing-heading">
            Welcome to Expansion Intelligence
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-testid="ei-landing-subtitle">
            Two-stage decision tool — Stage 1 answers concept-fit, Stage 2 answers financial viability.
          </p>
        </div>
      </div>

      {/* Three action cards — equal-height 3-column grid at desktop, stack at
          mobile. Each card: icon + title + one-sentence description +
          bottom-anchored action button. All three buttons sit on the same
          horizontal baseline regardless of description length. Discover by
          City is the recommended primary action → primary button style at
          the bottom; card backgrounds identical across all three. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6 items-stretch"
            data-testid="ei-landing-action-grid">
        {/* Card 1 — Discover by City (primary entry point; visually strongest).
            "Start here" marker + dark/amber primary button treatment. */}
        <div className="flex flex-col rounded-xl border-2 border-slate-300 bg-white p-5 h-full min-h-[220px] relative shadow-sm"
              data-testid="ei-gate-card-city-intel">
          <span className="absolute top-3 right-3 text-[9px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5"
                data-testid="ei-gate-start-here-badge">
            Start here
          </span>
          <div className="flex-1">
            <div className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-slate-700" /> Discover by City
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              Start here. Enter your concept and target city, get a ranked
              shortlist of neighbourhoods with demand, competition, and
              emerging-market analysis. This is where every expansion
              decision begins.
            </div>
          </div>
          <a href="/admin/expansion-intelligence/city"
             data-testid="ei-gate-city-intel"
             className="mt-4 w-full px-4 py-2 rounded-lg bg-slate-900 text-amber-300 text-sm font-semibold text-center hover:bg-slate-800 transition no-underline">
            Discover neighbourhoods
          </a>
        </div>

        {/* Card 2 — Site Intelligence (renamed from "Start New Analysis";
            in-depth site/catchment validation once a neighbourhood is chosen). */}
        <div className="flex flex-col rounded-xl border-2 border-slate-200 bg-white p-5 h-full min-h-[220px]"
              data-testid="ei-gate-card-site-intel">
          <div className="flex-1">
            <div className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-slate-700" /> Site Intelligence
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              Analyse a specific site or catchment in depth — footfall,
              competition, PFR (Prospective Financial Report), and
              financial viability. Use this once you&apos;ve picked a
              neighbourhood and want to validate an actual location before
              signing a lease.
            </div>
          </div>
          <button onClick={onStartNew} data-testid="ei-gate-start-new"
            className="mt-4 w-full px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 hover:border-slate-400 transition">
            Open Site Intelligence
          </button>
        </div>

        {/* Card 3 — Open Existing Project (unchanged). */}
        <div className="flex flex-col rounded-xl border-2 border-slate-200 bg-white p-5 h-full min-h-[220px]"
              data-testid="ei-gate-card-open-existing">
          <div className="flex-1">
            <div className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <ClipboardList className="w-4 h-4 text-slate-700" /> Open Existing Project
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              Jump back into any of your saved projects. All previous
              analyses are listed below and remain available until you
              explicitly delete them.
            </div>
          </div>
          <button onClick={onOpenSwitcher} data-testid="ei-gate-open-existing"
            className="mt-4 w-full px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 hover:border-slate-400 transition">
            Browse all projects
          </button>
        </div>
      </div>

      {/* Recent Projects — full persistent list, no auto-purge. */}
      <div className="mt-7" data-testid="ei-gate-project-hub">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Recent projects</div>
            <div className="text-[11px] text-slate-400">
              All your projects — persisted until you delete them explicitly.
            </div>
          </div>
          <span className="text-[11px] text-slate-500" data-testid="ei-gate-project-count">
            {loading
              ? 'Loading…'
              : `${projects.length} project${projects.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {projects.length === 0 && !loading && (
          <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-lg p-4 text-center"
               data-testid="ei-gate-no-projects">
            No saved projects yet. Click <strong>Start new project</strong> above to create your first one.
          </div>
        )}
        {projects.length > 0 && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ei-gate-projects-list">
              {visibleProjects.map(p => {
                const st = deriveStatus(p);
                return (
                  <div key={p.project_id} className="relative">
                    <button
                      onClick={() => onOpenProject(p.project_id)}
                      data-testid={`ei-gate-project-${p.project_id}`}
                      className="w-full text-left rounded-lg border border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/30 transition p-3 pr-8">
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-sm text-slate-800 truncate flex-1">{p.name || 'Untitled Project'}</div>
                        <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${STATUS_STYLES[st]}`}
                              data-testid={`ei-gate-project-status-${p.project_id}`}>{st}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">{p.address || '— no address yet'}</div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {p.updated_at ? new Date(p.updated_at).toLocaleString('en-IN', { hour12: false }) : '—'}
                      </div>
                    </button>
                    {/* Three-dot menu → Delete */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuFor(menuFor === p.project_id ? null : p.project_id);
                      }}
                      className="absolute top-2 right-2 p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                      data-testid={`ei-gate-project-menu-${p.project_id}`}
                      aria-label="Project actions">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {menuFor === p.project_id && (
                      <div className="absolute top-8 right-2 z-10 min-w-[140px] bg-white border border-slate-200 rounded-lg shadow-lg py-1"
                           data-testid={`ei-gate-project-menu-panel-${p.project_id}`}
                           onMouseLeave={() => setMenuFor(null)}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmFor(p); setMenuFor(null); }}
                          data-testid={`ei-gate-project-delete-${p.project_id}`}
                          className="w-full text-left px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50 flex items-center gap-2">
                          <Trash2 className="w-3.5 h-3.5" /> Delete project
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {projects.length > 9 && (
              <div className="mt-3 text-center">
                <button
                  onClick={() => setShowAll(v => !v)}
                  data-testid="ei-gate-view-all-projects"
                  className="text-xs font-medium text-emerald-700 hover:text-emerald-800 hover:underline">
                  {showAll
                    ? `Show fewer (9 most recent)`
                    : `View all ${projects.length} projects`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation dialog — small, focused, destructive-red confirm. */}
      {confirmFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
             data-testid="ei-gate-delete-dialog"
             onClick={() => { if (!deleting) setConfirmFor(null); }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 rounded-lg bg-rose-50 text-rose-700">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900">
                  Delete &lsquo;{confirmFor.name || 'Untitled Project'}&rsquo;?
                </h3>
                <p className="text-xs text-slate-600 mt-1">
                  This cannot be undone. All analysis, brief data, and CAPEX
                  configuration for this project will be permanently removed.
                </p>
              </div>
            </div>
            {deleteErr && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mt-2"
                   data-testid="ei-gate-delete-error">
                {String(deleteErr)}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => { if (!deleting) setConfirmFor(null); }}
                disabled={deleting}
                data-testid="ei-gate-delete-cancel"
                className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmFor)}
                disabled={deleting}
                data-testid="ei-gate-delete-confirm"
                className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExpansionIntelligenceHome() {
  const { getHeaders } = useAuth();
  const headers = useMemo(() => getHeaders(), [getHeaders]);

  const [config, setConfig]     = useState(null);
  const [gHealth, setGHealth]   = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const [address, setAddress]           = useState('Indiranagar, Bengaluru');
  const [fnbRadius, setFnbRadius]       = useState(800);
  const [anchorRadius, setAnchorRadius] = useState(1500);
  // ITER251 — Dense competitor scan (opt-in, paid/committed reports).
  // Off by default — multiplies Google API calls.
  const [denseMode, setDenseMode] = useState(false);
  // Per-run override for the brief's persisted competitor_search_terms.
  // Empty string ⇒ use brief defaults; comma-separated ⇒ override.
  const [searchTermsInput, setSearchTermsInput] = useState('');
  const [analysis, setAnalysis]         = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeErr, setAnalyzeErr]     = useState(null);
  // ITER273 — Recent Analyses panel (24h history, owner-scoped).
  const [recentJobs, setRecentJobs]   = useState([]);
  const [recentOpen, setRecentOpen]   = useState(false);

  const [sources, setSources] = useState([]);
  const [tier, setTier]       = useState('executive');
  // iter211 — Two-stage workflow. 'concept' = Location Intelligence (Section 1
  // sufficient). 'feasibility' = Full feasibility study (Section 2 required).
  // Persisted in sessionStorage so navigating tabs doesn't reset the stage.
  const [stage, setStage] = useState(() => {
    try { return sessionStorage.getItem('ei_stage') || 'concept'; } catch { return 'concept'; }
  });
  const updateStage = useCallback((s) => {
    setStage(s);
    try { sessionStorage.setItem('ei_stage', s); } catch { /* noop */ }
  }, []);
  // Guard against stale `tier` when stage flips back from 'feasibility' → 'concept'.
  useEffect(() => {
    if (stage === 'concept' && (tier === 'feasibility' || tier === 'financial')) {
      setTier('executive');
    }
  }, [stage, tier]);

  const [brief, setBrief]     = useState(null);
  const [briefLoaded, setBriefLoaded] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);

  // iter210 — Landing gate (Start New / Open Existing). The biggest UX issue
  // before this was the workspace mounting with a stale brief + a stale
  // address, silently carrying forward the previous tenant analysis. The
  // gate gives the user an unambiguous entry choice and, when "Start New"
  // is chosen, hard-resets the brief + analysis + address to a clean slate
  // and provisions a new project_id immediately so subsequent saves never
  // overwrite the previous project.
  const [gatePassed, setGatePassed] = useState(() => {
    try { return sessionStorage.getItem('ei_gate_passed') === '1'; } catch { return false; }
  });
  const passGate = useCallback((mode) => {
    if (mode === 'new') {
      // Fresh project_id (browser-side; backend will persist on first save).
      const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID() : `ei_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setActiveProjectId(newId);
      setBrief({});                 // empty brief, no prefill
      setAnalysis(null);            // no stale analysis
      setAddress('');               // empty address
      setLastAnalyzedAt(null);
      hasAnalyzedRef.current = false;
      setTier('executive');
      updateStage('concept');       // iter211 — fresh projects always start at Stage 1
      // ITER264 — Start New must NEVER inherit the City Intelligence brief.
      clearSession();
      setFromCityIntel(false);
    }
    setGatePassed(true);
    try { sessionStorage.setItem('ei_gate_passed', '1'); } catch { /* noop */ }
  }, [updateStage]);
  // iter211 — Back-to-Projects: returns to the landing gate WITHOUT discarding
  // the current brief / analysis / project_id. The workspace state is
  // preserved exactly so the user can re-enter via the gate.
  const goBackToProjects = useCallback(() => {
    setGatePassed(false);
    try { sessionStorage.removeItem('ei_gate_passed'); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    // ITER264 — When arriving from City Intelligence the city-intel
    // brief is the source of truth for this session. Do NOT overwrite
    // it with the tenant's saved brief — that would silently revert
    // the analyst's just-entered concept. We still flip briefLoaded so
    // the ProjectBriefCard renders.
    const fromCityIntelSession = !!getSession()?.origin
                                  && getSession().origin === 'city_intel';
    if (fromCityIntelSession) {
      setBriefLoaded(true);
      return;
    }
    axios.get(`${API}/api/expansion-intelligence/brief`, { headers })
      .then(r => setBrief(r.data.data || null))
      .catch(() => setBrief(null))
      .finally(() => setBriefLoaded(true));
  }, [headers]);

  // ITER264 — Shared Expansion Project session. When the user arrives
  // from City Intelligence the session carries the full brief, the
  // selected neighbourhood (address) and the seed result. We hydrate
  // BOTH the address and the entire brief so the workspace appears as
  // if everything had been entered manually — no duplicate typing.
  // For direct entry (no session) this is a complete no-op.
  const [fromCityIntel, setFromCityIntel] = useState(false);
  useEffect(() => {
    const s = getSession();
    if (s && s.origin === 'city_intel') {
      if (s.address) setAddress(s.address);
      if (s.brief) {
        // Merge city-intel brief into the legacy brief shape. Fields not
        // collected in City Intelligence (area_sqft / rent / etc.) stay
        // empty so Stage 2 can still be filled by the analyst.
        setBrief(prev => ({
          ...(prev || {}),
          business_objective:          s.brief.business_objective || '',
          preferred_cuisines:          s.brief.preferred_cuisines  || [],
          preferred_formats:           s.brief.preferred_formats   || [],
          price_for_two:               s.brief.price_for_two,
          target_segments:             s.brief.target_segments     || [],
          target_audience_description: s.brief.target_audience_description || '',
        }));
      }
      setGatePassed(true);
      setFromCityIntel(true);
      try { sessionStorage.setItem('ei_gate_passed', '1'); } catch { /* noop */ }
    }
  }, []);

  // ITER264 — "← Back to City Intelligence" preserves the session so the
  // City Intelligence page re-hydrates exactly where the user left off.
  const backToCityIntel = useCallback(() => {
    window.location.assign('/admin/expansion-intelligence/city');
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/expansion-intelligence/config`, { headers })
      .then(r => setConfig(r.data.data)).catch(() => {});
  }, [headers]);

  const refreshHealth = useCallback(() => {
    setHealthLoading(true);
    axios.get(`${API}/api/expansion-intelligence/google-health`, { headers })
      .then(r => setGHealth(r.data.data))
      .catch(e => setGHealth({ error: e?.response?.data?.detail || e.message }))
      .finally(() => setHealthLoading(false));
  }, [headers]);

  const refreshSources = useCallback(() => {
    axios.get(`${API}/api/expansion-intelligence/probe/sources?limit=30`, { headers })
      .then(r => setSources(r.data.data.rows || []))
      .catch(() => setSources([]));
  }, [headers]);

  // ITER273 — Recent Analyses fetch (24h history, both city + site
  // intel kinds returned by the unified endpoint).
  const refreshRecent = useCallback(() => {
    axios.get(`${API}/api/expansion-intelligence/jobs/recent?hours=24&limit=15`, { headers })
      .then(r => setRecentJobs(r.data?.data?.jobs || []))
      .catch(() => setRecentJobs([]));
  }, [headers]);

  useEffect(() => { refreshHealth(); refreshSources(); refreshRecent(); },
            [refreshHealth, refreshSources, refreshRecent]);

  // iter194 UX-stability: track whether a first analyze has happened using
  // a ref (avoids stale-closure on `analysis` inside runAnalyze useCallback).
  const hasAnalyzedRef = useRef(false);

  // ITER273 — Async polling state for Site Intelligence. Preserves the
  // existing UX: one boolean loader, single `analyzeErr` string surface.
  // Adds a `analyzeStage` string the (existing) progress UI can read.
  // sessionStorage tracks the active job_id so a tab reload resumes the
  // poll instead of restarting the 60-120s engine run.
  const [analyzeStage,     setAnalyzeStage]     = useState(null);
  const [analyzeJobId,     setAnalyzeJobId]     = useState(null);
  const [analyzeFailure,   setAnalyzeFailure]   = useState(null);  // structured
  const [analyzeTimings,   setAnalyzeTimings]   = useState(null);  // ms breakdown
  const analyzePollRef = useRef({ cancelled: false });

  // Resume an in-flight job after page reload.
  useEffect(() => {
    const persisted = sessionStorage.getItem('ei.site_intel.job_id');
    if (persisted) {
      setAnalyzeJobId(persisted);
      setAnalyzeLoading(true);
      pollSiteIntelJob(persisted).catch(() => {});
    }
  }, []);

  // ITER273 — Poll a Site Intelligence async job until terminal state.
  // Returns nothing; mutates component state (loading, error, stage,
  // analysis). NEVER throws — every failure is captured in component
  // state so the existing UI surfaces stay simple.
  const pollSiteIntelJob = useCallback(async (jobId) => {
    analyzePollRef.current = { cancelled: false };
    const start = Date.now();
    let backoffMs = 800;
    while (!analyzePollRef.current.cancelled) {
      try {
        const r = await axios.get(
          `${API}/api/expansion-intelligence/probe/analyze/jobs/${jobId}`,
          { headers },
        );
        const job = r.data?.data || {};
        setAnalyzeStage(job.stage || null);
        if (job.status === 'done') {
          setAnalysis(job.result || null);
          setAnalyzeTimings(job.result?.meta?.trace || null);
          if (!hasAnalyzedRef.current) {
            setTier('market');
            hasAnalyzedRef.current = true;
          }
          setLastAnalyzedAt(new Date());
          refreshSources();
          refreshRecent();
          sessionStorage.removeItem('ei.site_intel.job_id');
          setAnalyzeLoading(false);
          return;
        }
        if (job.status === 'failed') {
          const err = job.error || {};
          setAnalyzeFailure(err);
          setAnalyzeErr(err.message || 'Analysis failed.');
          sessionStorage.removeItem('ei.site_intel.job_id');
          refreshRecent();
          setAnalyzeLoading(false);
          return;
        }
      } catch (e) {
        // 404 → job expired / was cleared. Stop polling.
        if (e?.response?.status === 404) {
          sessionStorage.removeItem('ei.site_intel.job_id');
          setAnalyzeFailure({
            code: 'job_not_found', retryable: false,
            message: 'This analysis job has expired or was cleared.',
            user_action: 'Run a fresh analysis.',
          });
          setAnalyzeErr('Job no longer available.');
          setAnalyzeLoading(false);
          return;
        }
        // Other errors — keep polling (transient network blips OK).
      }
      // Hard ceiling — bail after 5 minutes even if backend is still
      // working, so we never spin forever.
      if (Date.now() - start > 5 * 60 * 1000) {
        setAnalyzeFailure({
          code: 'poll_timeout', retryable: true,
          message: 'Analysis is taking longer than 5 minutes. The job '
                   + 'may still be running — you can refresh the page '
                   + 'to resume polling.',
          user_action: 'Refresh the page or start a new analysis.',
        });
        setAnalyzeErr('Polling timeout.');
        setAnalyzeLoading(false);
        return;
      }
      await new Promise(res => setTimeout(res, backoffMs));
      backoffMs = Math.min(2500, Math.round(backoffMs * 1.2));
    }
  }, [headers, API, refreshSources, refreshRecent]);

  const runAnalyze = useCallback(async () => {
    setAnalyzeLoading(true);
    setAnalyzeErr(null);
    setAnalyzeFailure(null);
    setAnalyzeStage('queued');
    setAnalyzeTimings(null);
    try {
      // ITER251 — Parse the optional custom competitor search terms.
      // Empty input → omit from payload so backend falls back to the
      // brief's persisted defaults. Non-empty → split on comma/newline.
      const customTerms = searchTermsInput.trim()
        ? searchTermsInput.split(/[,\n]/).map(s => s.trim()).filter(Boolean)
        : null;
      // ITER273 — Async path: start job, then poll until terminal.
      const r = await axios.post(
        `${API}/api/expansion-intelligence/probe/analyze/start`,
        { address, fnb_radius_m: Number(fnbRadius), anchor_radius_m: Number(anchorRadius),
          project_id: activeProjectId || null,
          dense_mode: !!denseMode,
          ...(customTerms ? { competitor_search_terms: customTerms } : {}) },
        { headers },
      );
      const jobId = r.data?.data?.job_id;
      if (!jobId) throw new Error('Backend did not return a job_id.');
      setAnalyzeJobId(jobId);
      sessionStorage.setItem('ei.site_intel.job_id', jobId);
      await pollSiteIntelJob(jobId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setAnalyzeErr(typeof d === 'string' ? d : JSON.stringify(d || e.message));
      setAnalyzeLoading(false);
    }
  }, [address, fnbRadius, anchorRadius, denseMode, searchTermsInput,
       activeProjectId, headers, API, pollSiteIntelJob]);

  // ITER273 — Resume a failed-but-retryable Site Intelligence job.
  const resumeAnalyze = useCallback(async () => {
    if (!analyzeJobId) return;
    setAnalyzeLoading(true);
    setAnalyzeErr(null);
    setAnalyzeFailure(null);
    setAnalyzeStage('queued');
    try {
      await axios.post(
        `${API}/api/expansion-intelligence/probe/analyze/jobs/${analyzeJobId}/resume`,
        {}, { headers });
      sessionStorage.setItem('ei.site_intel.job_id', analyzeJobId);
      await pollSiteIntelJob(analyzeJobId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setAnalyzeErr(typeof d === 'string' ? d : JSON.stringify(d || e.message));
      setAnalyzeLoading(false);
    }
  }, [analyzeJobId, headers, API, pollSiteIntelJob]);

  // iter204 — Single-source-of-truth helper used by both UI Save and the
  // export buttons. Persists the CURRENT in-memory analysis + brief snapshot
  // to ei_projects so the PDF/Excel renderers can never read stale data.
  // Returns the project_id that the export endpoints should reference.
  const persistSnapshot = useCallback(async () => {
    if (!API) return activeProjectId;
    const payload = {
      project_id: activeProjectId || undefined,
      name: (activeProjectId ? undefined :
             `${address} · ${new Date().toLocaleDateString('en-IN')}`),
      address, fnb_radius_m: Number(fnbRadius), anchor_radius_m: Number(anchorRadius),
      brief_snapshot:    brief || null,
      analysis_snapshot: analysis || null,
    };
    const r = await axios.post(
      `${API}/api/expansion-intelligence/projects`, payload, { headers });
    const saved = r.data?.data;
    if (saved?.project_id && saved.project_id !== activeProjectId) {
      setActiveProjectId(saved.project_id);
    }
    return saved?.project_id || activeProjectId;
  }, [API, activeProjectId, address, fnbRadius, anchorRadius, brief, analysis, headers]);

  const geoOk    = gHealth?.geocoding?.reachable;
  const placesOk = gHealth?.places?.reachable;
  const liveOk   = geoOk && placesOk;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="ei-home-page">
      {/* iter210 — Landing gate: forces an explicit Start New / Open Existing
          choice on first entry. Bypassed (sessionStorage) within a session so
          the user never re-sees it while navigating tabs. */}
      {!gatePassed && (
        <LandingGate
          API={API}
          headers={headers}
          onStartNew={() => passGate('new')}
          onOpenSwitcher={() => passGate('open')}
          onOpenProject={async (pid) => {
            try {
              const r = await axios.get(`${API}/api/expansion-intelligence/projects/${pid}`, { headers });
              const d = r.data.data;
              setActiveProjectId(pid);
              if (d?.address) setAddress(d.address);
              if (d?.brief_snapshot) setBrief(d.brief_snapshot);
              if (d?.analysis_snapshot) {
                setAnalysis(d.analysis_snapshot);
                hasAnalyzedRef.current = true;
                setTier('market');
                updateStage(d.analysis_snapshot?.financial_viability ? 'feasibility' : 'concept');
              } else {
                setAnalysis(null);
                updateStage('concept');
              }
              const at = d?.analysis_snapshot?.analysis_context?.analysed_at_utc
                          || d?.updated_at || d?.created_at;
              if (at) {
                const dt = new Date(at);
                if (!isNaN(dt.getTime())) setLastAnalyzedAt(dt);
              }
              setGatePassed(true);
              try { sessionStorage.setItem('ei_gate_passed', '1'); } catch { /* noop */ }
            } catch (e) { /* swallow */ }
          }}
        />
      )}
      {gatePassed && (
      <>
      {/* Header */}
      <header className="flex items-start gap-4">
        <div className="p-3 rounded-2xl bg-slate-900 text-amber-300"><Sparkles className="w-7 h-7" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">Expansion Intelligence</h1>
            {/* iter211 — Stage indicator pill */}
            <span data-testid="ei-stage-pill"
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium uppercase tracking-wide border ${
                    stage === 'feasibility'
                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                      : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  }`}>
              {stage === 'feasibility' ? 'Stage 2 · Feasibility' : 'Stage 1 · Location Intelligence'}
            </span>
          </div>
          <p className="text-base text-slate-600 mt-2 max-w-3xl">
            {stage === 'feasibility'
              ? <>Stage 2 — Can this project be financially successful? Section 2 (Project & Feasibility) inputs are required to run the full CAPEX + Financial Viability model.</>
              : <>Stage 1 — Will this concept work in this market? Section 1 (Concept & Market) inputs alone are sufficient — feasibility comes later.</>}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Pill tone="ok" testid="ei-status-pill"><ShieldCheck className="w-3.5 h-3.5" /> Module reachable</Pill>
            {config && (
              <Pill tone={config.credit_metering_enabled ? 'ok' : 'warn'}>
                <Gauge className="w-3.5 h-3.5" />
                {config.credit_metering_enabled ? 'Credit metering ON' : 'Credit metering OFF (validation mode)'}
              </Pill>
            )}
            {config && <Pill testid="ei-cfg-engine">Engine {config.engine_version}</Pill>}
            {gHealth && (
              <Pill tone={liveOk ? 'ok' : 'err'} testid="ei-live-pill">
                {liveOk ? 'Live data reachable' : 'Live data degraded'}
              </Pill>
            )}
          </div>
        </div>
        {/* iter211 — Back to Projects (preserves state) */}
        <div className="flex flex-col items-end gap-2 shrink-0 mt-1">
          {fromCityIntel && (
            <Button variant="outline" size="sm"
                    onClick={backToCityIntel}
                    data-testid="ei-back-to-city-intel"
                    className="border-slate-900 text-slate-900 hover:bg-slate-50">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to City Intelligence
            </Button>
          )}
          <Button variant="outline" size="sm"
                  onClick={goBackToProjects}
                  data-testid="ei-back-to-projects">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Projects
          </Button>
        </div>
      </header>

      {/* Project Brief Intake */}
      {briefLoaded && (
        <ProjectBriefCard brief={brief} headers={headers}
                          recomputing={recomputing}
                          lastAnalyzedAt={lastAnalyzedAt}
                          address={address}
                          onSaved={(b) => {
                            // iter194 UX-stability: brief save does NOT auto-recompute.
                            // User must explicitly press Recalculate.
                            setBrief(b);
                          }} />
      )}

      {/* Analyse */}
      <Card data-testid="ei-analyze-card">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-500" />
            {stage === 'feasibility' ? 'Analyse the project' : 'Analyse a location'}
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            {stage === 'feasibility'
              ? 'Stage 2 — running the full Feasibility + Financial Viability model. Make sure Section 2 (Project & Feasibility) is filled.'
              : 'Stage 1 — geocodes the address, retrieves live competitors / anchors, and produces a Concept ↔ Market Fit verdict. Feasibility inputs are NOT required at this stage.'}
          </p>
          <div className="grid sm:grid-cols-12 gap-3 mt-4">
            <div className="sm:col-span-8">
              <label className="text-xs uppercase tracking-wide text-slate-500">Address</label>
              <Input value={address} onChange={e => setAddress(e.target.value)}
                     placeholder="e.g. 100 Feet Road, Indiranagar, Bengaluru"
                     data-testid="ei-analyze-address" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-500" title="How far customers realistically travel to this concept. Sets the competitor catchment.">
                Competitor catchment (m)
              </label>
              <Input type="number" value={fnbRadius} onChange={e => setFnbRadius(e.target.value)}
                     min={100} max={10000}
                     data-testid="ei-analyze-fnb-radius" />
              <p className="text-[10px] text-slate-400 mt-0.5">100–10,000 m</p>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-500" title="Reach for office / mall / transit / education anchors.">
                Anchor radius (m)
              </label>
              <Input type="number" value={anchorRadius} onChange={e => setAnchorRadius(e.target.value)}
                     min={200} max={10000}
                     data-testid="ei-analyze-anchor-radius" />
              <p className="text-[10px] text-slate-400 mt-0.5">200–10,000 m</p>
            </div>
          </div>
          {/* ITER251 — Dense competitor scan controls. OFF by default —
              dense mode multiplies Google API calls (grid + brief-text
              + broad-at-full) so only enable for paid/committed reports. */}
          <div className="mt-3 grid sm:grid-cols-12 gap-3 items-start">
            <div className="sm:col-span-4">
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={denseMode}
                  onChange={e => setDenseMode(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="ei-analyze-dense-mode"
                />
                <span className="font-medium">Dense competitor scan</span>
              </label>
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                Tiles the catchment + runs brief-derived text searches so
                small-format venues (udupi, darshini, tiffin) aren&apos;t dropped
                by Google&apos;s 20-result cap. <strong>Costs ~4× the API calls.</strong>{' '}
                Requires a saved project.
              </p>
            </div>
            {denseMode && (
              <div className="sm:col-span-8">
                <label className="text-xs uppercase tracking-wide text-slate-500" title="Optional local-vocab terms to broaden discovery. Auto-derived from the brief's cuisines/formats already; use this to add terms the brief can't express.">
                  Custom search terms (optional)
                </label>
                <Input
                  value={searchTermsInput}
                  onChange={e => setSearchTermsInput(e.target.value)}
                  placeholder="darshini, sagar, tiffin centre, military hotel"
                  data-testid="ei-analyze-search-terms"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Comma-separated. Empty ⇒ uses brief&apos;s saved terms. Filled
                  ⇒ overrides the brief for this run only.
                </p>
              </div>
            )}
          </div>
          {/* iter211 — Stage-aware CTAs */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {stage === 'concept' ? (
              <Button onClick={runAnalyze} disabled={analyzeLoading || !address}
                      data-testid="ei-analyze-btn"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Search className="w-4 h-4 mr-1.5" />
                {analyzeLoading ? 'Running…' : 'Run Location Intelligence'}
              </Button>
            ) : (
              <Button onClick={runAnalyze} disabled={analyzeLoading || !address}
                      data-testid="ei-analyze-btn"
                      className="bg-amber-700 hover:bg-amber-800 text-white">
                <BarChart3 className="w-4 h-4 mr-1.5" />
                {analyzeLoading ? 'Running…' : 'Run Feasibility & Financial Analysis'}
              </Button>
            )}
            {/* iter211 — Stage 1 → Stage 2 transition */}
            {stage === 'concept' && analysis && (
              <Button variant="outline"
                      onClick={() => { updateStage('feasibility'); setTier('feasibility'); }}
                      data-testid="ei-continue-to-feasibility"
                      className="border-amber-400 text-amber-800 hover:bg-amber-50">
                Continue to Feasibility Study <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
            {stage === 'feasibility' && (
              <button
                onClick={() => updateStage('concept')}
                data-testid="ei-back-to-stage1"
                className="text-xs text-slate-500 hover:text-slate-800 underline">
                ← Back to Stage 1 (concept only)
              </button>
            )}
          </div>
          {/* ITER273 — In-flight stage indicator (preserves the
              existing UX; just adds visibility into what's happening). */}
          {analyzeLoading && analyzeStage && (
            <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2"
                 data-testid="ei-analyze-stage">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Analysis in progress —{' '}
                <strong>{analyzeStage.replace(/_/g, ' ')}</strong>
              </span>
            </div>
          )}
          {/* ITER273 — Structured failure card. Shows code, why-it-failed,
              what the user can do, and a Resume button when retryable. */}
          {analyzeErr && (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
                 data-testid="ei-analyze-error">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold">
                    Analysis didn&apos;t complete
                    {analyzeFailure?.code && (
                      <span className="ml-2 text-xs font-normal text-rose-600 uppercase tracking-wide">
                        · {analyzeFailure.code.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <div className="mt-1">
                    {analyzeFailure?.message || analyzeErr}
                  </div>
                  {analyzeFailure?.user_action && (
                    <div className="mt-2 text-xs text-rose-700">
                      <strong>What to do:</strong> {analyzeFailure.user_action}
                    </div>
                  )}
                  {analyzeFailure?.retryable && analyzeJobId && (
                    <div className="mt-3">
                      <Button onClick={resumeAnalyze}
                              disabled={analyzeLoading}
                              data-testid="ei-analyze-resume-btn"
                              className="bg-rose-600 hover:bg-rose-700 text-white text-xs">
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                        Resume analysis
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* ITER273 — Recent Analyses (24h, owner-scoped). Lightweight
              expander so users can re-open completed/running/failed
              analyses without repeating work. */}
          {recentJobs.length > 0 && (
            <div className="mt-4 rounded-md border border-slate-200 bg-white"
                 data-testid="ei-recent-jobs">
              <button onClick={() => setRecentOpen(o => !o)}
                       data-testid="ei-recent-jobs-toggle"
                       className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <span className="flex items-center gap-2">
                  <ClipboardList className="w-3.5 h-3.5" />
                  Recent Analyses
                  <span className="text-xs text-slate-500">
                    ({recentJobs.length} in the last 24h)
                  </span>
                </span>
                <ArrowRight className={`w-3.5 h-3.5 transition-transform ${recentOpen ? 'rotate-90' : ''}`} />
              </button>
              {recentOpen && (
                <div className="border-t border-slate-100">
                  {recentJobs.map(j => (
                    <div key={j.job_id}
                         data-testid={`ei-recent-job-${j.job_id}`}
                         className="flex items-center justify-between px-3 py-2 text-xs border-b border-slate-50 last:border-b-0 hover:bg-slate-50">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={
                          j.status === 'done'   ? 'inline-block w-2 h-2 rounded-full bg-emerald-500' :
                          j.status === 'failed' ? 'inline-block w-2 h-2 rounded-full bg-rose-500'    :
                                                    'inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse'
                        } />
                        <span className="font-medium text-slate-800 truncate" title={j.label}>
                          {j.label}
                        </span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500">{j.kind.replace('_', ' ')}</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500">{j.stage}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {j.started_at && (
                          <span className="text-slate-400 text-[10px]">
                            {new Date(j.started_at).toLocaleTimeString('en-IN',
                              { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {j.kind === 'site_intel' && j.status === 'failed'
                         && j.error?.retryable && (
                          <button onClick={() => { setAnalyzeJobId(j.job_id);
                                                     setTimeout(resumeAnalyze, 0); }}
                                  data-testid={`ei-recent-resume-${j.job_id}`}
                                  className="text-rose-600 hover:underline">
                            Resume
                          </button>
                        )}
                        {j.kind === 'site_intel' && j.status === 'running' && (
                          <button onClick={() => { setAnalyzeJobId(j.job_id);
                                                     sessionStorage.setItem('ei.site_intel.job_id', j.job_id);
                                                     setAnalyzeLoading(true);
                                                     pollSiteIntelJob(j.job_id); }}
                                  data-testid={`ei-recent-watch-${j.job_id}`}
                                  className="text-emerald-700 hover:underline">
                            Watch
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <ProjectsBar API={API} headers={headers} analysis={analysis} brief={brief}
                       address={address} fnbRadius={fnbRadius} anchorRadius={anchorRadius}
                       projects={projects} setProjects={setProjects}
                       activeProjectId={activeProjectId} setActiveProjectId={setActiveProjectId}
                       onProjectOpened={({ address: a, brief: b, analysis: an, analysedAt }) => {
                         if (a) setAddress(a);
                         if (b) setBrief(b);
                         if (an) {
                           setAnalysis(an);
                           hasAnalyzedRef.current = true;  // prevent runAnalyze from resetting tier
                           setTier('market');
                         }
                         if (analysedAt) {
                           const d = new Date(analysedAt);
                           if (!isNaN(d.getTime())) setLastAnalyzedAt(d);
                         }
                       }} />
        </CardContent>
      </Card>

      {/* Three-tier switcher */}
      {analysis && (
        <div data-testid="ei-tier-tabs" className="bg-white border border-slate-200 rounded-2xl p-1 inline-flex gap-1">
          {TIERS.filter(t => stage === 'feasibility' ? true : !['feasibility', 'financial'].includes(t.key)).map(t => {
            const Icon = t.icon;
            const active = tier === t.key;
            return (
              <button key={t.key} onClick={() => setTier(t.key)}
                data-testid={`ei-tier-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-colors ${
                  active ? 'bg-slate-900 text-amber-300' : 'text-slate-700 hover:bg-slate-100'}`}>
                <Icon className="w-4 h-4" />
                <span className="font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {analysis && (
        <TabContextHeader analysis={analysis} lastAnalyzedAt={lastAnalyzedAt}
                          recomputing={recomputing}
                          activeProjectId={activeProjectId}
                          onOpenGlossary={() => setGlossaryOpen(true)}
                          onRecalculate={async () => {
                            setRecomputing(true);
                            try { await runAnalyze(); } finally { setRecomputing(false); }
                          }}
                          onDownloadExcel={async () => {
                            if (!analysis) {
                              alert('Run an analysis first.');
                              return;
                            }
                            try {
                              // iter204 — guarantee UI ↔ Excel parity by
                              // persisting the live in-memory snapshot before
                              // the renderer runs.
                              const pid = await persistSnapshot();
                              const resp = await axios.post(
                                `${API}/api/expansion-intelligence/export/excel`,
                                { project_id: pid },
                                { headers, responseType: 'blob' },
                              );
                              const blob = new Blob([resp.data], {
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                              });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const cd = resp.headers['content-disposition'] || '';
                              const m = /filename="?([^"]+)"?/.exec(cd);
                              a.download = m ? m[1] : `EI_${pid}.xlsx`;
                              document.body.appendChild(a); a.click();
                              document.body.removeChild(a); URL.revokeObjectURL(url);
                            } catch (e) {
                              const d = e?.response?.data;
                              alert(`Excel export failed: ${typeof d === 'string' ? d : (d?.detail || e.message)}`);
                            }
                          }}
                          onDownloadPdf={async (opts) => {
                            if (!analysis) {
                              alert('Run an analysis first.');
                              return;
                            }
                            try {
                              // iter204 — guarantee UI ↔ PDF parity by
                              // persisting the live in-memory snapshot before
                              // the renderer runs.
                              const pid = await persistSnapshot();
                              const resp = await axios.post(
                                `${API}/api/expansion-intelligence/export/pdf`,
                                { project_id: pid, bump_major: !!opts?.bumpMajor },
                                { headers, responseType: 'blob' },
                              );
                              const blob = new Blob([resp.data], { type: 'application/pdf' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const cd = resp.headers['content-disposition'] || '';
                              const m = /filename="?([^"]+)"?/.exec(cd);
                              a.download = m ? m[1] : `EI_${pid}.pdf`;
                              document.body.appendChild(a); a.click();
                              document.body.removeChild(a); URL.revokeObjectURL(url);
                              const v = resp.headers['x-snapshot-version'];
                              if (v) console.log(`PDF snapshot frozen as ${v}`);
                            } catch (e) {
                              const d = e?.response?.data;
                              alert(`PDF export failed: ${typeof d === 'string' ? d : (d?.detail || e.message)}`);
                            }
                          }}
                          onDownloadMemo={async () => {
                            if (!analysis) {
                              alert('Run an analysis first.');
                              return;
                            }
                            try {
                              const pid = await persistSnapshot();
                              const resp = await axios.post(
                                `${API}/api/expansion-intelligence/export/cover-memo`,
                                { project_id: pid },
                                { headers, responseType: 'blob' },
                              );
                              const blob = new Blob([resp.data], { type: 'application/pdf' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const cd = resp.headers['content-disposition'] || '';
                              const m = /filename="?([^"]+)"?/.exec(cd);
                              a.download = m ? m[1] : `EI_Memo_${pid || 'project'}.pdf`;
                              document.body.appendChild(a); a.click();
                              document.body.removeChild(a); URL.revokeObjectURL(url);
                            } catch (e) {
                              const d = e?.response?.data;
                              alert(`Memo export failed: ${typeof d === 'string' ? d : (d?.detail || e.message)}`);
                            }
                          }} />
      )}

      {analysis && tier === 'executive' && (
        <ExecutiveTier data={analysis}
                       api={API} headers={headers}
                       activeProjectId={activeProjectId} stage={stage}
                       onStage2Updated={(stage2Data) => {
                         setAnalysis(prev => prev ? {
                           ...prev,
                           executive_recommendation_stage2: stage2Data,
                         } : prev);
                       }} />
      )}
      {analysis && tier === 'project'   && <ProjectIntelligenceTier data={analysis} brief={brief} />}
      {analysis && tier === 'feasibility' && (
        <ProjectFeasibilityTier data={analysis} brief={brief}
          capexHandlers={{
            brief, headers, API,
            onPersistedLines: async (lines) => {
              const next = { ...(brief || {}), capex_line_items: lines };
              try {
                const r = await axios.post(`${API}/api/expansion-intelligence/brief`,
                                            next, { headers });
                setBrief(r.data.data);
              } catch (e) { /* swallow — UI keeps showing latest typed values */ }
            },
            // ITER228 P0-1 — Fast recalc (skips Apify + AI). < 3 seconds target.
            onFastRecalc: async (patch) => {
              if (!activeProjectId) return null;
              try {
                const r = await axios.post(
                  `${API}/api/expansion-intelligence/projects/${activeProjectId}/capex/recalculate`,
                  patch || {}, { headers });
                const data = r.data?.data || {};
                // Splice the new PFR.capex into the analysis snapshot so
                // header KPI / Adequacy / Gap / Investor Snapshot refresh
                // instantly without a full pipeline rerun.
                setAnalysis(prev => {
                  if (!prev) return prev;
                  const next = { ...prev };
                  const pf = { ...(next.project_feasibility || {}) };
                  const sections = { ...(pf.sections || {}) };
                  sections.capex = {
                    ...(sections.capex || {}),
                    capex_total_final:    data.capex_total_final,
                    required_inr:         data.capex_total_final,
                    subtotal_inr:         data.subtotal_inr,
                    pre_opening_inr:      data.pre_opening_inr,
                    line_items:           data.line_items,
                    addendum_lines:       data.addendum_lines,
                    funding_gap_inr:      data.funding_gap_inr,
                    budget_adequacy_pct:  data.adequacy_pct,
                    investment_inputs:    data.investment_inputs,
                    totals:               { ...(sections.capex?.totals || {}),
                                            franchise_fee_inr: data.franchise_fee_inr },
                  };
                  pf.sections = sections;
                  next.project_feasibility = pf;
                  next.financial_viability = {
                    ...(next.financial_viability || {}),
                    assumptions: data.fvm_assumptions,
                  };
                  next.verdict = data.verdict;
                  return next;
                });
                return data;
              } catch (e) { return null; }
            },
            onTriggerRecompute: async () => {
              setRecomputing(true);
              try { await runAnalyze(); } finally { setRecomputing(false); }
            },
          }} />
      )}
      {analysis && tier === 'financial' && (
        <FinancialViabilityTier data={analysis} brief={brief} recomputing={recomputing}
          fvmHandlers={{
            API, headers,
            onSaved: (b) => setBrief(b),
            onTriggerRecompute: async () => {
              setRecomputing(true);
              try { await runAnalyze(); } finally { setRecomputing(false); }
            },
          }} />
      )}
      {analysis && tier === 'market'    && (
        <>
          {/* iter221 — Apify enrichment + provider status (stub-mode by default) */}
          <ApifyEnrichmentPanel projectId={activeProjectId} headers={headers} />
          <MarketTier data={analysis} projectId={activeProjectId}/>
        </>
      )}
      {analysis && tier === 'evidence'  && (
        <EvidenceTier data={analysis} gHealth={gHealth} healthLoading={healthLoading}
                      refreshHealth={refreshHealth} sources={sources} refreshSources={refreshSources} />
      )}

      <GlossaryDrawer open={glossaryOpen} onClose={() => setGlossaryOpen(false)}
                      API={API} headers={headers} />

      {!analysis && (
        <Card><CardContent className="p-8 text-center text-slate-500">
          Enter an address above and click Run to begin a consultant-grade analysis.
        </CardContent></Card>
      )}
      </>
      )}
    </div>
  );
}

/* ─── EXECUTIVE TIER ─────────────────────────────────────────────────── */
/* ─── EXECUTIVE RECOMMENDATION CARD (iter212) ──────────────────────
   LLM-driven consultant view sitting ABOVE the deterministic Executive
   Summary. Stage 1 is auto-fetched from `analysis.executive_recommendation`;
   Stage 2 is triggered on-demand via the project-scoped endpoint. */
function ExecutiveRecommendationCard({ analysis, api, headers, activeProjectId, stage, onStage2Updated }) {
  const er1 = analysis?.executive_recommendation || {};
  const r1 = er1.reasoning || null;
  const er2 = analysis?.executive_recommendation_stage2 || null;
  const r2 = er2?.reasoning || null;

  const [busy, setBusy]   = useState(false);
  const [err,  setErr]    = useState(null);
  const brand = er1.brand_match || er2?.brand_match;

  async function generateStage2() {
    if (!activeProjectId) {
      setErr('Save the project before generating an investment recommendation.');
      return;
    }
    setBusy(true); setErr(null);
    try {
      const url = `${api}/api/expansion-intelligence/projects/${activeProjectId}/executive-recommendation/stage2`;
      const r = await axios.post(url, {}, { headers, timeout: 120000 });
      const d = r.data?.data;
      if (d && onStage2Updated) onStage2Updated(d);
      if (!d?.available) {
        setErr('LLM did not return a usable recommendation. Check Universal Key balance and try again.');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  // Nothing to render if both stages absent
  if (!r1 && !r2 && er1.available === false && !activeProjectId) return null;

  return (
    <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50/40 to-white"
          data-testid="ei-executive-recommendation">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-violet-900 text-violet-200">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-violet-700">
                LLM-Powered · Evidence-Cited
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Executive Recommendation</h2>
              <div className="text-xs text-slate-500 mt-0.5">
                Consultant-grade interpretation above the deterministic engines. Every claim cites underlying evidence keys.
              </div>
            </div>
          </div>
          {brand && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5"
                 data-testid="ei-er-brand-match">
              <div className="font-semibold text-amber-900 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Known brand · {brand.brand}
              </div>
              <div className="text-[10px] text-amber-700/80">{brand.tier} · {brand.category}</div>
            </div>
          )}
        </div>

        {/* STAGE 1 — Location Consultant */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4 space-y-3"
             data-testid="ei-er-stage1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-800">
              Stage 1 · Location Intelligence Consultant
            </span>
            {er1.available === false && (
              <Pill tone="warn">Unavailable — LLM offline or budget exceeded</Pill>
            )}
            {er1.available && (
              <Pill tone="ok" testid="ei-er-stage1-available">Generated · {er1.model || 'LLM'}</Pill>
            )}
          </div>
          {r1 && (
            <div className="space-y-3">
              <div data-testid="ei-er-verdict">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Executive Verdict</div>
                <div className="text-sm font-medium text-slate-900 leading-relaxed">{r1.executive_verdict}</div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Why this market works</div>
                  <div className="text-sm text-slate-700 leading-relaxed">{r1.why_this_market_works}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-1">Key risks</div>
                  <div className="text-sm text-slate-700 leading-relaxed">{r1.key_risks}</div>
                </div>
              </div>
              {Array.isArray(r1.management_recommendations) && r1.management_recommendations.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Management recommendations</div>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                    {r1.management_recommendations.map((m, i) => <li key={i}>{m}</li>)}
                  </ol>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-emerald-100">
                <Pill tone={r1.expansion_confidence === 'high' ? 'ok' : r1.expansion_confidence === 'low' ? 'err' : 'warn'}
                      testid="ei-er-confidence">
                  Confidence · {r1.expansion_confidence || '—'}
                </Pill>
                {r1.confidence_rationale && (
                  <span className="text-[11px] text-slate-500">{r1.confidence_rationale}</span>
                )}
              </div>
              {r1.recommended_next_step && (
                <div className="rounded-lg bg-emerald-100/60 border border-emerald-300 px-3 py-2 text-sm text-emerald-900"
                     data-testid="ei-er-next-step">
                  <span className="font-semibold">Next step:</span> {r1.recommended_next_step}
                </div>
              )}
            </div>
          )}
          {!r1 && er1.available === false && (
            <div className="text-xs text-slate-500">
              Run analyze again with the Universal Key key topped up to generate the consultant view.
            </div>
          )}
        </div>

        {/* STAGE 2 — Investment Consultant (on-demand) */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-4 space-y-3"
             data-testid="ei-er-stage2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-amber-800">
              Stage 2 · Investment Consultant
            </span>
            {!r2 && (
              <Button size="sm" variant="outline" onClick={generateStage2} disabled={busy || !activeProjectId}
                      data-testid="ei-er-generate-stage2"
                      className="border-amber-400 text-amber-900 hover:bg-amber-100">
                {busy ? 'Generating…' : 'Generate Investment Recommendation'}
              </Button>
            )}
            {r2 && (
              <Pill tone="ok" testid="ei-er-stage2-available">Generated · {er2?.model || 'LLM'}</Pill>
            )}
          </div>
          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
                 data-testid="ei-er-stage2-error">
              {err}
            </div>
          )}
          {!r2 && !err && (
            <div className="text-xs text-slate-500">
              {!activeProjectId
                ? 'Save the project first to unlock the on-demand Investment Consultant.'
                : (!analysis?.financial_viability
                    ? 'Run a Stage 2 analysis (with Feasibility + Financial Viability) to unlock this recommendation.'
                    : 'Click to generate the institutional GO/NO-GO line backed by FVM + CAPEX evidence.')}
            </div>
          )}
          {r2 && (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Investment thesis</div>
                <div className="text-sm text-slate-900 leading-relaxed">{r2.investment_thesis}</div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-1">Capital risks</div>
                  <div className="text-sm text-slate-700 leading-relaxed">{r2.capital_risks}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-1">Commercial risks</div>
                  <div className="text-sm text-slate-700 leading-relaxed">{r2.commercial_risks}</div>
                </div>
              </div>
              {Array.isArray(r2.sensitivity_drivers) && r2.sensitivity_drivers.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Sensitivity drivers</div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    {r2.sensitivity_drivers.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-amber-100">
                <Pill tone={
                  r2.investor_recommendation === 'PROCEED' ? 'ok' :
                  r2.investor_recommendation === 'DECLINE' ? 'err' : 'warn'
                } testid="ei-er-investor-rec">
                  {r2.investor_recommendation}
                </Pill>
                {r2.recommendation_rationale && (
                  <span className="text-[11px] text-slate-500">{r2.recommendation_rationale}</span>
                )}
              </div>
              {r2.final_verdict && (
                <div className="rounded-lg bg-amber-100/60 border border-amber-300 px-3 py-2 text-sm text-amber-900"
                     data-testid="ei-er-final-verdict">
                  <span className="font-semibold">Final verdict:</span> {r2.final_verdict}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ExecutiveTier({ data, api, headers, activeProjectId, stage, onStage2Updated }) {
  const ei = data.executive_intelligence;
  if (!ei) return <p className="text-slate-500">Executive Intelligence layer unavailable.</p>;

  const dna     = ei.market_dna;
  const demand  = ei.demand_drivers;
  const reality = ei.competitive_reality;
  const pressure= ei.market_pressure;
  const white   = ei.white_space;
  const opps    = ei.opportunities;
  const cv      = ei.consultant_view;
  const iv      = ei.investor_view;
  const mgmt    = ei.management_summary;

  return (
    <div className="space-y-6" data-testid="ei-tier-executive-panel">
      {/* iter212 — Executive Recommendation (LLM-driven) sits ABOVE the
          existing Management Summary. Pure interpretation layer — every
          claim is evidence-cited against the deterministic engines. */}
      <ExecutiveRecommendationCard
        analysis={data} api={api} headers={headers}
        activeProjectId={activeProjectId} stage={stage}
        onStage2Updated={onStage2Updated} />
      {/* Management Summary — the one-page TL;DR */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-500"/> Management Summary
            </h2>
            <Pill tone={mgmt.attractiveness === 'High' ? 'ok' : mgmt.attractiveness === 'Moderate' ? 'warn' : 'err'}
                  testid="ei-attractiveness">
              {mgmt.attractiveness} attractiveness
            </Pill>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 mt-4 text-sm">
            <SummaryRow label="What this market is" value={mgmt.what_this_market_is} />
            <SummaryRow label="How crowded"          value={mgmt.how_crowded} />
            <SummaryRow label="Who it serves"        items={mgmt.who_it_serves} />
            <SummaryRow label="Concepts to consider" items={mgmt.concepts_to_consider} testid="ei-summary-consider"/>
            <SummaryRow label="Risks"                items={mgmt.risks} tone="warn"/>
            <SummaryRow label="Concepts to avoid"    items={mgmt.concepts_to_avoid} tone="err"/>
            <SummaryRow label="Next steps"           items={mgmt.next_steps} className="md:col-span-2"/>
          </div>
        </CardContent>
      </Card>

      {/* Consultant's View */}
      <Card><CardContent className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-slate-500"/> Consultant&rsquo;s View
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-800"
           data-testid="ei-consultant-view"
           dangerouslySetInnerHTML={{
             __html: cv.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
      </CardContent></Card>

      {/* W4 Regional & Cultural context — placed prominently so it overrides reading order */}
      <W4RegionalCard ctx={ei.regional_context}/>

      {/* Investor View */}
      <Card><CardContent className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-500"/> Investor View
        </h3>
        <div className="grid md:grid-cols-2 gap-4 mt-3 text-sm">
          <BulletList title="Upsides"           items={iv.upsides}            icon={<CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5"/>} />
          <BulletList title="Risks"             items={iv.risks}              icon={<AlertCircle className="w-4 h-4 text-rose-600 mt-0.5"/>} />
          <BulletList title="Capital cautions"  items={iv.capital_cautions}   icon={<AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5"/>} />
          <BulletList title="Must be validated" items={iv.assumptions_to_validate} icon={<Target className="w-4 h-4 text-sky-600 mt-0.5"/>} />
        </div>
      </CardContent></Card>

      {/* Market DNA + Pressure + Reality grid */}
      <div className="grid md:grid-cols-3 gap-4">
        <DnaCard dna={dna}/>
        <PressureCard pressure={pressure}/>
        <RealityCard reality={reality}/>
      </div>

      {/* Demand drivers */}
      <Card><CardContent className="p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2">Demand Drivers</h3>
        <p className="text-sm text-slate-700 mb-3" data-testid="ei-demand-narrative">{demand.narrative}</p>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <BulletList title="Primary audiences" items={demand.primary_audiences} />
          <BulletList title="Dayparts"          items={demand.dayparts} />
          <BulletList title="Spend patterns"    items={demand.spend_patterns} />
        </div>
      </CardContent></Card>

      {/* White Space + Opportunities */}
      <Card><CardContent className="p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-500"/> White Space &amp; Opportunities
        </h3>
        <p className="text-sm text-slate-700 mb-3" data-testid="ei-whitespace-summary">{white.summary}</p>
        <div className="grid md:grid-cols-3 gap-3">
          <OpportunityList title="Best opportunities" items={opps.best}      tone="ok"   testid="ei-opps-best"/>
          <OpportunityList title="Moderate"           items={opps.moderate}  tone="warn" testid="ei-opps-moderate"/>
          <OpportunityList title="Avoid"              items={opps.avoid}     tone="err"  testid="ei-opps-avoid"/>
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
          <BulletList title="Missing cuisines"     items={(white.missing_cuisines || []).map(c => c.cuisine)} />
          <BulletList title="Missing formats"      items={white.missing_formats} />
          <BulletList title="Underrepresented (<5%)" items={(white.underrepresented || []).map(c => `${c.cuisine} (${(c.share*100).toFixed(1)}%)`)} />
          <BulletList title="Daypart / price gaps" items={[...(white.daypart_gaps || []), ...(white.price_gaps || [])]} />
        </div>
      </CardContent></Card>

      {/* Customer behaviour */}
      <Card><CardContent className="p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2">Customer Behaviour Hypotheses</h3>
        <ul className="space-y-2 text-sm" data-testid="ei-behaviour-list">
          {(ei.customer_behaviour?.hypotheses || []).map((h, i) => (
            <li key={i} className="rounded-lg border border-slate-200 p-3">
              <div className="text-slate-800">{h.hypothesis}</div>
              <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-2">
                {(h.evidence || []).map((e, j) => (
                  <span key={j} className="px-2 py-0.5 rounded bg-slate-100">
                    {e.label}: {String(e.value)}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </CardContent></Card>

      {/* W4 Demand intelligence + frustrations + expectations + know-vs-assume */}
      <W4DemandIntelCard di={ei.demand_intelligence}/>

      <p className="text-xs text-slate-500 italic" data-testid="ei-traceability">{ei.evidence_traceability}</p>
    </div>
  );
}

function W4RegionalCard({ ctx }) {
  if (!ctx || (!ctx.state && !ctx.city)) return null;
  const reg = ctx.regulatory || {};
  const cul = ctx.cultural || {};
  return (
    <Card data-testid="ei-regional-card"><CardContent className="p-6">
      <h3 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
        <Target className="w-5 h-5 text-slate-500"/> Regional &amp; Cultural Context
        <Pill tone={ctx.confidence === 'high' ? 'ok' : ctx.confidence === 'medium' ? 'warn' : 'err'}>
          {ctx.confidence} confidence
        </Pill>
      </h3>
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <div><div className="text-xs uppercase tracking-wide text-slate-500">State</div>
             <div className="text-slate-900">{ctx.state_display || '—'}</div></div>
        <div><div className="text-xs uppercase tracking-wide text-slate-500">City</div>
             <div className="text-slate-900">{ctx.city_display || '—'}</div></div>
        <div><div className="text-xs uppercase tracking-wide text-slate-500">Veg dominance</div>
             <div className="text-slate-900">{cul.veg_dominance || '—'}</div></div>
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Alcohol</div>
          <div className={`text-sm ${reg.dry_state ? 'text-rose-700 font-medium' : 'text-slate-800'}`}
               data-testid="ei-regional-alcohol">
            {reg.dry_state && <strong>Dry state. </strong>}{reg.alcohol_summary}
          </div>
        </div>
      </div>
      {(ctx.warnings || []).length > 0 && (
        <div className="mt-3" data-testid="ei-regional-warnings">
          <div className="text-xs uppercase tracking-wide text-amber-700 mb-1">Local warnings</div>
          <ul className="space-y-1 text-sm text-amber-800">
            {ctx.warnings.map((w, i) => (
              <li key={i} className="flex gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {(ctx.religious_sensitivity || []).length > 0 && (
        <div className="mt-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Religious / cultural sensitivity</div>
          <ul className="space-y-1 text-sm text-slate-700">
            {ctx.religious_sensitivity.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </div>
      )}
      <div className="mt-2 text-xs text-slate-400">{ctx.source}</div>
    </CardContent></Card>
  );
}

function W4DemandIntelCard({ di }) {
  if (!di) return null;
  const csd = (di.cuisine_supply_demand || {}).items || [];
  const fp  = di.format_positioning || {};
  const mb  = (di.market_behaviour || {}).behaviours || [];
  const fr  = (di.customer_frustrations || {}).items || [];
  const exp = (di.customer_expectations || {}).items || [];
  const know = di.knowledge_split || {};
  const zs  = di.zomato_swiggy || {};
  return (
    <>
      <Card data-testid="ei-demand-card"><CardContent className="p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-500"/> Demand Intelligence — supply ≠ demand
        </h3>
        <p className="text-xs text-slate-500 mb-3">{(di.cuisine_supply_demand || {}).note}</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-3">Cuisine</th>
              <th className="py-2 pr-3">Supply</th>
              <th className="py-2 pr-3">Σ reviews</th>
              <th className="py-2 pr-3">Median rev</th>
              <th className="py-2 pr-3">Mean rating</th>
              <th className="py-2 pr-3">Engagement</th>
              <th className="py-2 pr-3">Classification</th>
              <th className="py-2 pr-3">Reasoning</th>
            </tr></thead>
            <tbody data-testid="ei-cuisine-sd-body">
              {csd.map((c, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 text-slate-800 font-medium">{c.cuisine}</td>
                  <td className="py-1.5 pr-3">{c.supply_count}</td>
                  <td className="py-1.5 pr-3">{c.demand_reviews}</td>
                  <td className="py-1.5 pr-3">{c.median_reviews}</td>
                  <td className="py-1.5 pr-3">{c.mean_rating}</td>
                  <td className="py-1.5 pr-3">{c.engagement}</td>
                  <td className="py-1.5 pr-3">
                    <Pill tone={
                      c.classification === 'Underrepresented' ? 'ok' :
                      c.classification === 'Emerging' ? 'warn' :
                      c.classification === 'Oversupplied' ? 'err' : 'neutral'}>
                      {c.classification}
                    </Pill>
                  </td>
                  <td className="py-1.5 pr-3 text-slate-600">{c.reasoning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardContent className="p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-2">Format positioning</h3>
          <ul className="text-sm space-y-1" data-testid="ei-positioning">
            {(fp.distribution || []).map((d, i) => (
              <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                <span>{d.position}</span><span className="font-medium">{d.count}</span>
              </li>
            ))}
            {fp.unknown > 0 && <li className="text-xs text-slate-500">{fp.unknown} operators have no Google price level</li>}
          </ul>
          {fp.dominant && <div className="mt-2 text-sm text-slate-700">Dominant tier: <strong>{fp.dominant}</strong></div>}
        </CardContent></Card>

        <Card><CardContent className="p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-2">Market behaviour</h3>
          <ul className="space-y-2 text-sm" data-testid="ei-behaviour-cards">
            {mb.map((b, i) => (
              <li key={i} className="rounded-lg border border-slate-200 p-3">
                <div className="font-medium text-slate-800">{b.label}</div>
                <div className="text-xs text-slate-500">
                  {(b.evidence || []).map(e => `${e.label}: ${e.value}`).join(' · ')}
                </div>
                <div className="text-xs text-slate-700 mt-1">{b.implication}</div>
              </li>
            ))}
          </ul>
        </CardContent></Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardContent className="p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-2">Customer frustrations <span className="text-xs font-normal text-slate-500">(expected patterns)</span></h3>
          <p className="text-xs text-amber-700 mb-2">{(di.customer_frustrations || {}).method} — {(di.customer_frustrations || {}).live_review_nlp}</p>
          <ul className="space-y-2 text-sm" data-testid="ei-frustrations">
            {fr.map((f, i) => (
              <li key={i} className="border-l-2 border-rose-300 pl-3">
                <div className="font-medium text-rose-800">{f.theme} <span className="text-xs text-slate-500">({f.likelihood})</span></div>
                <div className="text-xs text-slate-600">{f.category} · {f.evidence}</div>
              </li>
            ))}
            {fr.length === 0 && <li className="text-slate-400 text-xs">No structural frustration signals at this stage.</li>}
          </ul>
        </CardContent></Card>

        <Card><CardContent className="p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-2">Customer expectations</h3>
          <ul className="space-y-2 text-sm" data-testid="ei-expectations">
            {exp.map((e, i) => (
              <li key={i} className="border-l-2 border-emerald-300 pl-3">
                <div className="font-medium text-emerald-800">{e.expectation}</div>
                <div className="text-xs text-slate-600">{e.rationale}</div>
              </li>
            ))}
          </ul>
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2">What we know vs what we assume</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div data-testid="ei-know">
            <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1">Data-backed</div>
            <ul className="space-y-1">
              {(know.data_backed || []).map((k, i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0"/>{k}</li>
              ))}
            </ul>
          </div>
          <div data-testid="ei-assume">
            <div className="text-xs uppercase tracking-wide text-amber-700 mb-1">Requires validation</div>
            <ul className="space-y-1">
              {(know.requires_validation || []).map((a, i) => (
                <li key={i} className="flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"/>{a}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-500" data-testid="ei-zs-status">
          <div>
            <strong>External delivery-platform connectivity</strong>
            <span className="ml-2 text-[11px] uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
              {zs.mode || 'reachability_probe'}
            </span>
          </div>
          <div className="mt-1">
            Zomato landing: <strong>{(zs.zomato || {}).status}</strong>
            {' · '}Swiggy landing: <strong>{(zs.swiggy || {}).status}</strong>
          </div>
          <div className="text-slate-400">{zs.attribution}</div>
        </div>
        <ZomatoEnrichmentBlock enrichment={zs.enrichment} />
      </CardContent></Card>
    </>
  );
}

function ZomatoEnrichmentBlock({ enrichment }) {
  if (!enrichment || !enrichment.zomato) return null;
  const z = enrichment.zomato;
  const tone = z.coverage_pct >= 50 ? 'ok' : z.coverage_pct > 0 ? 'warn' : 'err';
  return (
    <div className="mt-4 border-t border-slate-200 pt-4" data-testid="ei-zomato-enrichment">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-medium text-slate-800">
          Zomato JSON-LD enrichment (best-effort, no proxy)
        </div>
        <Pill tone={tone} testid="ei-zomato-coverage">
          {z.attempted > 0
            ? `${z.matched}/${z.attempted} matched · ${z.coverage_pct}% coverage`
            : '0 attempted'}
        </Pill>
      </div>
      <div className="text-xs text-slate-600 mt-2">{z.method}</div>
      <div className="text-xs text-amber-700 mt-1">{z.note}</div>
      {z.matched > 0 && (
        <div className="mt-3 text-xs">
          <div className="text-slate-500 uppercase tracking-wide mb-1">Fields filled (matched competitors)</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(z.fields_filled || {}).map(([k, v]) => (
              <span key={k} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                {k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
      {(z.samples || []).length > 0 && (
        <details className="mt-3 text-xs" data-testid="ei-zomato-samples">
          <summary className="cursor-pointer text-slate-500">Show attempts ({(z.samples || []).length})</summary>
          <ul className="mt-2 space-y-1">
            {z.samples.map((s, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-2">
                <strong className="text-slate-800">{s.name}</strong>
                {s.extracted ? (
                  <span className="ml-2 text-emerald-700">
                    matched · rating {s.extracted.rating ?? '—'} · reviews {s.extracted.review_count ?? '—'} ·
                    price {s.extracted.price_range || '—'}
                  </span>
                ) : (
                  <span className="ml-2 text-rose-700">no match ({(s.attempts || []).length} URL{s.attempts && s.attempts.length === 1 ? '' : 's'} tried)</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      {(enrichment.swiggy || {}).note && (
        <div className="mt-3 text-xs text-slate-500">
          <strong>Swiggy:</strong> {enrichment.swiggy.note}
        </div>
      )}
    </div>
  );
}

function DnaCard({ dna }) {
  return (
    <Card><CardContent className="p-5">
      <div className="text-xs uppercase tracking-wide text-slate-500">Market DNA</div>
      <div className="text-2xl font-semibold text-slate-900 mt-1" data-testid="ei-dna-type">{dna.type}</div>
      {dna.secondary && <div className="text-xs text-slate-500">secondary: {dna.secondary}</div>}
      <p className="text-sm text-slate-700 mt-2">{dna.why}</p>
      <div className="mt-2 text-[11px] uppercase text-slate-500">Confidence: {dna.confidence}</div>
    </CardContent></Card>
  );
}
function PressureCard({ pressure }) {
  const tone = pressure.label === 'Healthy' ? 'ok' : pressure.label === 'Emerging' ? 'warn'
             : pressure.label === 'Oversaturated' ? 'err' : 'neutral';
  return (
    <Card><CardContent className="p-5">
      <div className="text-xs uppercase tracking-wide text-slate-500">Market Pressure</div>
      <div className="mt-1"><Pill tone={tone} testid="ei-pressure-label">{pressure.label}</Pill></div>
      <p className="text-sm text-slate-700 mt-2">{pressure.description}</p>
      <p className="text-xs text-slate-500 mt-1">{pressure.concentration_note}</p>
    </CardContent></Card>
  );
}
function RealityCard({ reality }) {
  return (
    <Card><CardContent className="p-5">
      <div className="text-xs uppercase tracking-wide text-slate-500">Competitive Reality</div>
      <div className="text-xl font-semibold text-slate-900 mt-1" data-testid="ei-reality-maturity">{reality.maturity}</div>
      <p className="text-sm text-slate-700 mt-1"><strong>Guest expectation:</strong> {reality.guest_expectation}</p>
      <p className="text-sm text-slate-700 mt-1">{reality.wedge_required}</p>
    </CardContent></Card>
  );
}

function SummaryRow({ label, value, items, tone, testid, className = '' }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      {value && <div className="text-sm text-slate-800 mt-1">{value}</div>}
      {items && (
        <ul className="text-sm mt-1 space-y-0.5">
          {items.map((it, i) => (
            <li key={i} className={tone === 'err' ? 'text-rose-700' : tone === 'warn' ? 'text-amber-700' : 'text-slate-800'}>
              • {it}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
function BulletList({ title, items, icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{title}</div>
      <ul className="space-y-1.5 text-sm">
        {(items || []).map((t, i) => (
          <li key={i} className="flex gap-2"><span className="mt-0.5">{icon || '•'}</span><span>{t}</span></li>
        ))}
        {(!items || items.length === 0) && <li className="text-slate-400 text-xs">—</li>}
      </ul>
    </div>
  );
}
function OpportunityList({ title, items, tone, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
        <Pill tone={tone}>{(items || []).length}</Pill>
      </div>
      <ul className="mt-2 space-y-2 text-sm">
        {(items || []).map((c, i) => (
          <li key={i} className="border-t border-slate-100 pt-2">
            <div className="font-medium text-slate-800">{c.name} <span className="text-xs text-slate-500">({c.kind})</span></div>
            <div className="text-xs text-slate-600 mt-0.5">{c.rationale}</div>
          </li>
        ))}
        {(!items || items.length === 0) && <li className="text-slate-400 text-xs">none flagged</li>}
      </ul>
    </div>
  );
}

/* ─── MARKET TIER (existing W2 visuals) ─────────────────────────────── */

/* ─── PROJECT INTELLIGENCE TIER (W5) ──────────────────────────────────── */
const FIT_DIMENSIONS = [
  { k: 'cuisine_fit',              l: 'Cuisine Fit' },
  { k: 'format_fit',               l: 'Format Fit' },
  { k: 'delivery_vs_dinein_fit',   l: 'Delivery vs Dine-In' },
  { k: 'veg_fit',                  l: 'Veg / Non-Veg' },
  { k: 'alcohol_fit',              l: 'Alcohol Compatibility' },
  { k: 'differentiation',          l: 'Competitive Differentiation' },
  { k: 'market_entry_probability', l: 'Market Entry Probability' },
  { k: 'demand_match',             l: 'Demand Match' },
];
const USP_SCORE_LABELS = {
  usp_fit:                  'USP Fit',
  differentiation_strength: 'Differentiation Strength',
  competitive_advantage:    'Competitive Advantage',
  defensibility:            'Defensibility',
  customer_relevance:       'Customer Relevance',
  execution_complexity:     'Execution Complexity',
  uniqueness:               'Uniqueness',
  relevance:                'Relevance',
  market_need:              'Market Need',
};
const PFR_SECTIONS = [
  { k: 'capex',       l: 'A · CAPEX Model' },
  { k: 'property',    l: 'B · Property Feasibility' },
  { k: 'execution',   l: 'C · Execution Feasibility' },
  { k: 'regulatory',  l: 'D · Regulatory Feasibility' },
];
function VerdictBadge({ decision, testid }) {
  const tone = decision === 'GO' ? 'bg-emerald-600' :
               decision === 'NO_GO' ? 'bg-rose-600' :
               'bg-amber-500';
  const label = decision === 'NO_GO' ? 'NO-GO' : decision;
  return (
    <span className={`text-white text-sm font-semibold px-3 py-1.5 rounded-full ${tone}`}
          data-testid={testid}>
      {label}
    </span>
  );
}
function ScoreBar({ score, band }) {
  const tone = band === 'excellent' ? 'bg-emerald-500' :
               band === 'strong'    ? 'bg-emerald-400' :
               band === 'moderate'  ? 'bg-amber-400' :
               band === 'weak'      ? 'bg-orange-500' :
                                      'bg-rose-500';
  return (
    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full ${tone}`} style={{ width: `${Math.max(2, score)}%` }} />
    </div>
  );
}
function FitTile({ dim, fit }) {
  if (!fit) return null;
  const hb = fit.hard_blocker;
  const evidenceWeak = fit.evidence_sufficient === false;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4"
         data-testid={`ei-pi-fit-${dim.k}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-slate-800">{dim.l}</div>
        <div className="flex items-center gap-2">
          {hb && (
            <span className="text-[10px] uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
              hard blocker
            </span>
          )}
          {evidenceWeak && !hb && (
            <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                  title="Score derived from fallback / partial evidence">
              low-evidence
            </span>
          )}
          <span className="text-2xl font-semibold text-slate-900"
                data-testid={`ei-pi-fit-score-${dim.k}`}>{fit.score}</span>
        </div>
      </div>
      <div className="mt-2"><ScoreBar score={fit.score} band={fit.band} /></div>
      <div className="text-xs text-slate-500 mt-2 leading-relaxed">{fit.reason}</div>
      <details className="mt-3 text-[11px]" data-testid={`ei-pi-fit-trace-${dim.k}`}>
        <summary className="cursor-pointer text-slate-500 select-none">Show formula &amp; inputs</summary>
        <div className="mt-2 space-y-1.5">
          {fit.formula && (
            <div>
              <div className="uppercase tracking-wide text-slate-400">Formula</div>
              <div className="text-slate-700">{fit.formula}</div>
            </div>
          )}
          {fit.inputs && Object.keys(fit.inputs).length > 0 && (
            <div>
              <div className="uppercase tracking-wide text-slate-400 mt-1.5">Inputs consumed</div>
              <pre className="text-[10px] text-slate-700 bg-slate-50 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(fit.inputs, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
function UspScoreCard({ k, value }) {
  const evidenceWeak = value?.evidence_sufficient === false;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`ei-pi-usp-${k}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-700">{USP_SCORE_LABELS[k] || k}</div>
        <div className="flex items-center gap-2">
          {evidenceWeak && (
            <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              low-evidence
            </span>
          )}
          <div className="text-xl font-semibold text-slate-900">{value?.score}</div>
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-1">{value?.reason}</div>
      <details className="mt-2 text-[11px]">
        <summary className="cursor-pointer text-slate-500 select-none">Show formula &amp; inputs</summary>
        <div className="mt-1.5 space-y-1">
          {value?.formula && (
            <div className="text-slate-700"><strong>Formula:</strong> {value.formula}</div>
          )}
          {value?.inputs && (
            <pre className="text-[10px] text-slate-700 bg-slate-50 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(value.inputs, null, 2)}
            </pre>
          )}
        </div>
      </details>
    </div>
  );
}
function RiskBreakdownCard({ breakdown, aggregate }) {
  if (!breakdown) return null;
  return (
    <Card data-testid="ei-pi-risk-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-900">Investment Risk — 5-Component Breakdown</h3>
          {aggregate && (
            <span className="ml-auto text-xs text-slate-500">
              Aggregate = <strong className="text-slate-800">{aggregate.score}</strong> ({aggregate.band})
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Risk is no longer a single bucket. Each component is independent so a well-capitalised
          investor can carry LOW capitalisation risk even when the catchment carries a hard regulatory blocker.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[].map(c => {
            const v = breakdown[c.k];
            if (!v) return null;
            return (
              <div key={c.k} className="rounded-lg border border-slate-200 bg-white p-3"
                   data-testid={`ei-pi-risk-${c.k}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-700">{c.l}</div>
                  <div className="text-xl font-semibold text-slate-900">{v.score}</div>
                </div>
                <div className="mt-2"><ScoreBar score={v.score} band={v.band} /></div>
                <div className="text-xs text-slate-500 mt-1.5">{v.reason}</div>
                {v.capital_efficiency_note && (
                  <div className="text-xs text-amber-700 mt-1.5">{v.capital_efficiency_note}</div>
                )}
                <details className="mt-2 text-[11px]">
                  <summary className="cursor-pointer text-slate-500 select-none">Inputs &amp; formula</summary>
                  <div className="mt-1 space-y-1">
                    {v.formula && <div className="text-slate-700"><strong>Formula:</strong> {v.formula}</div>}
                    {v.inputs && (
                      <pre className="text-[10px] bg-slate-50 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(v.inputs, null, 2)}
                      </pre>
                    )}
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
function ProjectIntelligenceTier({ data, brief }) {
  const pi = data?.project_intelligence;
  if (!pi) {
    return (
      <Card data-testid="ei-pi-empty">
        <CardContent className="p-8 text-center text-slate-600 space-y-2">
          <Target className="w-8 h-8 text-slate-400 mx-auto" />
          <div className="text-base font-medium text-slate-800">
            Add a Project Brief to unlock Project Intelligence
          </div>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Project Intelligence evaluates THIS specific project against THIS specific market —
            cuisine fit, budget fit, alcohol &amp; veg compatibility, differentiation, demand match,
            and an overall GO / MODIFY / NO-GO verdict.
          </p>
          <p className="text-xs text-slate-400 mt-3">
            Tip: fill the Project Brief card at the top of this page (with at least an objective +
            cuisine + budget + USPs) and re-run Analyse.
          </p>
        </CardContent>
      </Card>
    );
  }
  const v = pi.verdict;
  return (
    <div className="space-y-6" data-testid="ei-pi-tier">
      <Card data-testid="ei-pi-verdict-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Project Intelligence verdict</div>
              <div className="flex items-center gap-3 mt-1">
                <VerdictBadge decision={v.decision} testid="ei-pi-verdict" />
                <div className="text-lg text-slate-800">
                  Success probability:{' '}
                  <span className="font-semibold" data-testid="ei-pi-success-pct">
                    {v.success_probability_pct}%
                  </span>
                </div>
              </div>
              {brief?.business_objective && (
                <div className="text-sm text-slate-500 mt-2">
                  Brief: <span className="text-slate-700">{brief.business_objective}</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-slate-500">Overall fit avg</div>
              <div className="text-3xl font-semibold text-slate-900 mt-1" data-testid="ei-pi-fit-avg">
                {pi.fit_summary?.average ?? '—'}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                hard blockers: <strong>{pi.fit_summary?.hard_blockers ?? 0}</strong>
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-3" data-testid="ei-pi-rationale">{v.rationale}</p>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <VerdictBucket title="What works" tone="emerald" items={v.what_works} testid="ei-pi-what-works" />
            <VerdictBucket title="Requires modification" tone="amber" items={v.requires_modification} testid="ei-pi-modify" />
            <VerdictBucket title="Risky" tone="orange" items={v.risky} testid="ei-pi-risky" />
            <VerdictBucket title="Avoid (hard blockers)" tone="rose" items={v.avoid} testid="ei-pi-avoid" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="ei-pi-fit-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Project ↔ Market Fit</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            11 deterministic fit dimensions. Each score uses transparent rules — no LLM, no opaque weights.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FIT_DIMENSIONS.map(d => (
              <FitTile key={d.k} dim={d} fit={pi.fit?.[d.k]} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="ei-pi-usp-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-slate-500" />
              <h3 className="text-base font-semibold text-slate-900">USP / Competitive-Advantage Intelligence</h3>
            </div>
            <div className="text-xs text-slate-500">
              {(pi.usp?.advantages || []).length} USP(s) declared
              {pi.usp?.description_provided && ' · narrative provided'}
            </div>
          </div>
          {pi.usp?.classification?.active_classes?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="ei-pi-usp-classification">
              {pi.usp.classification.active_classes.map(c => (
                <span key={c} className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  c === 'Commodity / No USP'
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>{c}</span>
              ))}
            </div>
          )}
          {pi.usp?.classification?.commodity_flag && (
            <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2.5"
                 data-testid="ei-pi-commodity-warning">
              Commodity narrative detected — concept reads as &ldquo;no real USP&rdquo;. The engine has capped
              all USP scores until a real differentiator is described.
            </div>
          )}
          {pi.usp?.customer_choice_reason && (
            <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3"
                 data-testid="ei-pi-customer-choice-reason">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Why customers will choose this concept</div>
              <em>“{pi.usp.customer_choice_reason}”</em>
            </div>
          )}
          {(pi.usp?.advantages || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="ei-pi-usp-tags">
              {pi.usp.advantages.map(a => (
                <span key={a} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">{a}</span>
              ))}
            </div>
          )}
          {pi.usp?.description && (
            <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3"
                 data-testid="ei-pi-usp-description">
              <em>“{pi.usp.description}”</em>
            </div>
          )}
          <p className="text-sm text-slate-600 mt-3" data-testid="ei-pi-usp-summary">{pi.usp?.summary}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {Object.entries(pi.usp?.scores || {}).map(([k, v]) => (
              <UspScoreCard key={k} k={k} value={v} />
            ))}
          </div>
          {(pi.usp?.per_usp || []).length > 0 && (
            <details className="mt-4 text-xs" data-testid="ei-pi-usp-per-usp">
              <summary className="cursor-pointer text-slate-500">Per-USP catchment scan ({pi.usp.per_usp.length})</summary>
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-3">USP</th>
                    <th className="py-1.5 pr-3">Competitor overlap</th>
                    <th className="py-1.5 pr-3">Catchment relevance</th>
                    <th className="py-1.5 pr-3">Execution complexity</th>
                    <th className="py-1.5">Notes</th>
                  </tr></thead>
                  <tbody>
                    {pi.usp.per_usp.map((p, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5 pr-3 font-medium text-slate-700">{p.usp}</td>
                        <td className="py-1.5 pr-3">
                          {p.competitor_overlap_pct == null ? '—' : `${p.competitor_overlap_pct}%`}
                          {(p.competitor_match_names || []).length > 0 && (
                            <span className="text-slate-400"> ({p.competitor_match_names.slice(0,2).join(', ')}{p.competitor_match_names.length > 2 ? '…' : ''})</span>
                          )}
                        </td>
                        <td className="py-1.5 pr-3">
                          <span className={p.relevance_ok ? 'text-emerald-700' : 'text-rose-700'}>
                            {p.relevance_ok ? 'OK' : 'mismatch'}
                          </span>
                        </td>
                        <td className="py-1.5 pr-3">{p.execution_complexity}</td>
                        <td className="py-1.5 text-slate-500">{(p.relevance_notes || []).join('; ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </CardContent>
      </Card>

      <RiskBreakdownCard breakdown={null} aggregate={null} />

      <div className="text-xs text-slate-400 px-2" data-testid="ei-pi-consumed-layers">
        Inputs: {pi.consumed_layers?.join(' · ')}
      </div>
    </div>
  );
}

/* ─── PROJECT FEASIBILITY REPORT (PFR) ─────────────────────────────── */
function PFRVerdictBadge({ decision, testid }) {
  const m = {
    'Feasible':     'bg-emerald-600',
    'Conditional':  'bg-amber-500',
    'High Risk':    'bg-orange-500',
    'Not Feasible': 'bg-rose-600',
  };
  return (
    <span className={`text-white text-sm font-semibold px-3 py-1.5 rounded-full ${m[decision] || 'bg-slate-500'}`}
          data-testid={testid}>
      {decision}
    </span>
  );
}
/* ─── PROJECTS BAR (iter193 — Save / Open / Duplicate / Archive) ── */
function ProjectsBar({ API, headers, analysis, brief, address, fnbRadius, anchorRadius,
                       projects, setProjects, activeProjectId, setActiveProjectId,
                       onProjectOpened }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');

  const refresh = useCallback(async () => {
    if (!API) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/expansion-intelligence/projects`, { headers });
      setProjects(r.data.data?.projects || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [API, headers, setProjects]);

  useEffect(() => { refresh(); }, [refresh]);

  async function saveCurrent() {
    if (!API) return;
    setSaving(true);
    try {
      const payload = {
        project_id: activeProjectId || undefined,
        name:    name || `${address} · ${new Date().toLocaleDateString('en-IN')}`,
        address, fnb_radius_m: Number(fnbRadius), anchor_radius_m: Number(anchorRadius),
        brief_snapshot:    brief || null,
        analysis_snapshot: analysis || null,
      };
      const r = await axios.post(`${API}/api/expansion-intelligence/projects`, payload, { headers });
      const saved = r.data.data;
      if (saved?.project_id) setActiveProjectId(saved.project_id);
      setName('');
      await refresh();
    } catch (e) { /* ignore */ }
    finally { setSaving(false); }
  }
  async function openProject(pid) {
    if (!API) return;
    try {
      const r = await axios.get(`${API}/api/expansion-intelligence/projects/${pid}`, { headers });
      const d = r.data.data;
      setActiveProjectId(pid);
      onProjectOpened({
        address: d?.address,
        brief: d?.brief_snapshot,
        analysis: d?.analysis_snapshot,
        analysedAt: d?.analysis_snapshot?.analysis_context?.analysed_at_utc
                    || d?.updated_at || d?.created_at,
      });
    } catch (e) { /* ignore */ }
  }
  async function duplicate(pid) {
    if (!API) return;
    try {
      await axios.post(`${API}/api/expansion-intelligence/projects/${pid}/duplicate`, {}, { headers });
      await refresh();
    } catch (e) { /* ignore */ }
  }
  async function archive(pid) {
    if (!API) return;
    try {
      await axios.post(`${API}/api/expansion-intelligence/projects/${pid}/archive`, {}, { headers });
      await refresh();
    } catch (e) { /* ignore */ }
  }

  const active = projects.filter(p => p.status !== 'archived');
  // iter211 — Derive a user-facing status from the project doc.
  function deriveStatus(p) {
    if (!p) return 'DRAFT';
    if (p.status === 'archived') return 'ARCHIVED';
    if (p.has_analysis) return 'ANALYSED';
    if (p.address) return 'IN PROGRESS';
    return 'DRAFT';
  }
  const STATUS_STYLES = {
    'DRAFT':       'bg-slate-100 text-slate-700 border-slate-200',
    'IN PROGRESS': 'bg-sky-50 text-sky-700 border-sky-200',
    'ANALYSED':    'bg-emerald-50 text-emerald-700 border-emerald-200',
    'ARCHIVED':    'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="ei-projects-bar">
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardList className="w-4 h-4 text-slate-500" />
        <span className="text-xs uppercase tracking-wide text-slate-500">Projects</span>
        <Input value={name} onChange={e => setName(e.target.value)}
               placeholder={activeProjectId ? '(rename or leave blank)' : 'Project name…'}
               className="text-xs h-8 max-w-xs" data-testid="ei-project-name" />
        {/* iter211 — Save Draft (works without analysis) and Update/Analysed-save */}
        <Button size="sm" variant="outline" onClick={saveCurrent}
                disabled={saving}
                data-testid="ei-project-save"
                title={analysis ? 'Saves the analysis snapshot too' : 'Saves the current brief as a draft project'}>
          <Save className="w-3 h-3 mr-1" />
          {analysis
            ? (activeProjectId ? 'Update Project' : 'Save Project')
            : (activeProjectId ? 'Update Draft'   : 'Save Draft')}
        </Button>
        {activeProjectId && (
          <Button size="sm" variant="ghost" onClick={() => { setActiveProjectId(null); setName(''); }}
                  data-testid="ei-project-clear-active">
            New project
          </Button>
        )}
        <span className="ml-auto text-[11px] text-slate-500">
          {loading ? 'Loading…' : `${active.length} project${active.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {active.length > 0 && (
        <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ei-projects-list">
          {active.slice(0, 9).map(p => {
            const st = deriveStatus(p);
            return (
            <div key={p.project_id}
                 className={`rounded border p-2 text-xs ${activeProjectId === p.project_id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                 data-testid={`ei-project-card-${p.project_id}`}>
              <div className="flex items-start gap-2">
                <div className="font-medium text-slate-800 truncate flex-1">{p.name}</div>
                <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${STATUS_STYLES[st]}`}
                      data-testid={`ei-project-status-${p.project_id}`}>{st}</span>
              </div>
              <div className="text-[10px] text-slate-500 truncate">{p.address || '—'}</div>
              <div className="text-[10px] text-slate-400">
                {p.updated_at ? new Date(p.updated_at).toLocaleString('en-IN', { hour12: false }) : '—'}
              </div>
              <div className="mt-1 flex gap-1">
                <button onClick={() => openProject(p.project_id)}
                        className="text-[10px] text-blue-600 hover:underline"
                        data-testid={`ei-project-open-${p.project_id}`}>Open</button>
                <button onClick={() => duplicate(p.project_id)}
                        className="text-[10px] text-slate-600 hover:underline"
                        data-testid={`ei-project-dupe-${p.project_id}`}>Duplicate</button>
                <button onClick={() => archive(p.project_id)}
                        className="text-[10px] text-rose-600 hover:underline ml-auto"
                        data-testid={`ei-project-archive-${p.project_id}`}>Archive</button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── FVM ASSUMPTION WORKSPACE (iter193) ─────────────────────────── */
function FVMAssumptionWorkspace({ workspace, brief, headers, API, onSaved, onTriggerRecompute }) {
  const [draft, setDraft] = useState({});
  const [savedSig, setSavedSig] = useState('{}');
  // Reset draft when workspace re-renders (after explicit recompute)
  useEffect(() => {
    const cur = (brief && brief.fvm_overrides) || {};
    setDraft(cur);
    setSavedSig(JSON.stringify(cur));
  }, [brief, workspace]);

  const dirty = JSON.stringify(draft) !== savedSig;

  function update(key, val) {
    setDraft(prev => ({ ...prev, [key]: val === '' || val == null ? null : Number(val) }));
  }
  function resetRow(key) {
    setDraft(prev => { const next = { ...prev }; delete next[key]; return next; });
  }
  function resetAll() {
    const cur = (brief && brief.fvm_overrides) || {};
    setDraft(cur);
  }
  async function commit() {
    const cleaned = {};
    Object.entries(draft).forEach(([k, v]) => {
      if (v !== null && v !== '' && v !== undefined) cleaned[k] = Number(v);
    });
    const nextBrief = { ...(brief || {}), fvm_overrides: cleaned };
    setSavedSig(JSON.stringify(cleaned));
    try {
      const r = await axios.post(`${API}/api/expansion-intelligence/brief`, nextBrief, { headers });
      if (onSaved) onSaved(r.data.data);
      if (onTriggerRecompute) await onTriggerRecompute();
    } catch (e) { /* swallow */ }
  }

  if (!workspace?.rows?.length) return null;
  return (
    <Card data-testid="ei-fvm-assumption-workspace">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Assumption Workspace</h3>
            <p className="text-xs text-slate-500">Every FVM assumption is editable. Edit locally, then click Save &amp; Recalculate to cascade through verdict + payback.</p>
          </div>
          <div className="flex items-center gap-2">
            {workspace.overrides_present && !dirty && (
              <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                Overrides active
              </span>
            )}
            {dirty && (
              <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                    data-testid="ei-fvm-dirty">unsaved</span>
            )}
            {dirty && (
              <Button size="sm" variant="ghost" onClick={resetAll}
                      className="text-rose-700"
                      data-testid="ei-fvm-discard">Discard changes</Button>
            )}
            <Button size="sm" variant={dirty ? 'default' : 'outline'}
                    onClick={commit} disabled={!dirty}
                    data-testid="ei-fvm-save-recalc">
              <Save className="w-3 h-3 mr-1" /> Save &amp; Recalculate
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="py-1.5 px-2">Assumption</th>
                <th className="py-1.5 px-2 text-right">System</th>
                <th className="py-1.5 px-2 text-right">Override</th>
                <th className="py-1.5 px-2 text-right">Final</th>
                <th className="py-1.5 px-2">Source</th>
                <th className="py-1.5 px-2">Conf</th>
                <th className="py-1.5 px-2"> </th>
              </tr>
            </thead>
            <tbody>
              {workspace.rows.map(r => {
                const ov = draft[r.key] ?? r.override_value;
                const isOverridden = ov !== null && ov !== undefined && ov !== '';
                const finalVal = isOverridden ? ov : r.system_value;
                return (
                  <tr key={r.key} className="border-t border-slate-100"
                      data-testid={`ei-fvm-row-${r.key}`}>
                    <td className="py-1.5 px-2 font-medium">{r.label}
                      <div className="text-[10px] text-slate-400 truncate" title={r.reason}>{r.reason}</div>
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-slate-500">{r.system_value ?? '—'}</td>
                    <td className="py-1.5 px-2 text-right">
                      <input type="number" value={ov ?? ''}
                             onChange={e => update(r.key, e.target.value)}
                             placeholder="—"
                             className="w-24 text-right font-mono bg-transparent focus:bg-yellow-50 px-1 rounded border border-slate-200"
                             data-testid={`ei-fvm-override-${r.key}`} />
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono font-semibold">{finalVal ?? '—'}</td>
                    <td className="py-1.5 px-2 text-[11px] text-slate-600">{r.source}</td>
                    <td className="py-1.5 px-2"><PFRConfidenceChip conf={r.confidence} /></td>
                    <td className="py-1.5 px-2">
                      {isOverridden && (
                        <button onClick={() => resetRow(r.key)}
                                className="text-[10px] text-blue-600 hover:underline"
                                data-testid={`ei-fvm-reset-${r.key}`}>reset</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[11px] text-slate-400">
          Local edits do not auto-save. Click <strong>Save &amp; Recalculate</strong> to persist overrides into the brief and cascade through verdict + payback.
        </div>
      </CardContent>
    </Card>
  );
}
const CAPEX_UOM_LABELS = {
  sqft: 'sqft', running_ft: 'Running Ft', nos: 'Nos', seats: 'Seats',
  tons: 'Tons', kw: 'kW', hp: 'HP', litres: 'Litres', tr: 'TR',
  counters: 'Counters', bays: 'Bays', rooms: 'Rooms', months: 'Months',
  manpower: 'Man-months', licenses: 'Licenses', lump_sum: 'Lump Sum',
};
const CAPEX_UOMS = Object.keys(CAPEX_UOM_LABELS);

function CapexWorkspace({ payload, onPersistedLines, onTriggerRecompute, onFastRecalc, brief, headers: _h, API: _a }) {
  // Local editable copy of the line items (live edits don't wait for round-trip)
  const [draft, setDraft] = useState(() => payload.line_items || []);
  const [savedSig, setSavedSig] = useState(() => JSON.stringify(payload.line_items || []));
  // Re-seed when server payload changes (after explicit recompute)
  useEffect(() => {
    setDraft(payload.line_items || []);
    setSavedSig(JSON.stringify(payload.line_items || []));
  }, [payload]);

  // Catalog for custom-line quick-add (loaded once from benchmark library)
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    if (!_a) return;
    axios.get(`${_a}/api/expansion-intelligence/capex/benchmarks`, { headers: _h })
      .then(r => setCatalog(r.data.data?.custom_catalog || []))
      .catch(() => {});
  }, [_a, _h]);

  // ITER228 P0-1 — Debounced auto-save + fast recalc on every edit.
  // Two-state badge: "Saving…" (in-flight) → "Saved HH:MM:SS" (idle).
  const dirty = JSON.stringify(draft) !== savedSig;
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const debounceRef = useRef(null);

  // Schedule an auto-save 1.5 s after the last edit. Cancelled by every
  // new keystroke. Calls onPersistedLines THEN onFastRecalc — the fast
  // recalc endpoint skips Apify + Stage1/Stage2 AI for sub-3-sec turnaround.
  useEffect(() => {
    if (!dirty) return;
    if (!onPersistedLines || !onFastRecalc) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      (async () => {
        try {
          setSaveStatus('saving');
          const toPersist = draft
            .filter(li => li.user_override || li.is_custom || li.is_optional)
            .map(li => ({
              line_id: li.line_id, category: li.category, label: li.label,
              uom: li.uom,
              quantity: Number(li.quantity) || 0,
              rate_inr: Number(li.rate_inr) || 0,
              is_custom: !!li.is_custom,
              user_override: !!li.user_override,
            }));
          setSavedSig(JSON.stringify(draft));
          await onPersistedLines(toPersist);
          await onFastRecalc({ capex_line_items: toPersist });
          setSaveStatus('saved');
          setSavedAt(new Date());
        } catch (e) {
          setSaveStatus('error');
        }
      })();
    }, 1500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [draft]);

  function commit(triggerRecompute) {
    if (!onPersistedLines) return;
    const toPersist = draft
      .filter(li => li.user_override || li.is_custom)
      .map(li => ({
        line_id:  li.line_id,
        category: li.category,
        label:    li.label,
        uom:      li.uom,
        quantity: Number(li.quantity) || 0,
        rate_inr: Number(li.rate_inr) || 0,
        is_custom: !!li.is_custom,
        user_override: !!li.user_override,
      }));
    setSavedSig(JSON.stringify(draft));
    (async () => {
      await onPersistedLines(toPersist);
      if (triggerRecompute && onTriggerRecompute) await onTriggerRecompute();
    })();
  }
  function resetAll() {
    setDraft(payload.line_items || []);
  }

  function editLine(idx, patch) {
    setDraft(prev => {
      const next = [...prev];
      const li = { ...next[idx], ...patch };
      li.quantity = Number(li.quantity) || 0;
      li.rate_inr = Number(li.rate_inr) || 0;
      li.amount_inr = li.quantity * li.rate_inr;
      li.user_override = true;
      next[idx] = li;
      return next;
    });
  }
  function addCustomLine(seed = {}) {
    setDraft(prev => [...prev, {
      line_id: `custom_${Date.now()}`,
      category: 'custom',
      label: seed.label || 'Custom line',
      uom: seed.uom || 'lump_sum',
      quantity: 1,
      rate_inr: seed.default_rate || 0,
      amount_inr: seed.default_rate || 0,
      formula: `1 ${seed.uom || 'lump_sum'} × ₹${(seed.default_rate || 0).toLocaleString('en-IN')}/${seed.uom || 'lump_sum'}`,
      assumption: `User-defined line · ${seed.label || 'Custom line'}`,
      benchmark_inr: seed.default_rate || 0,
      benchmark_source: seed.label ? 'RestroPulse Custom Catalog' : 'User-supplied (custom)',
      confidence: 'medium',
      is_custom: true, user_override: true, auto_seeded: false,
    }]);
  }
  function deleteLine(idx) {
    setDraft(prev => prev.filter((_, i) => i !== idx));
  }
  function resetLine(idx) {
    setDraft(prev => {
      const next = [...prev];
      const li = { ...next[idx] };
      if (li.auto_seeded) {
        li.rate_inr = li.benchmark_inr;
        li.user_override = false;
        li.amount_inr = (li.quantity || 0) * li.rate_inr;
      }
      next[idx] = li;
      return next;
    });
  }

  // ITER227 P0-1 — Backend (capex_engine.v2) is the SoT. The local
  // `draft` math below is shown ONLY as a transient preview when the
  // workspace has unsaved edits. The canonical Required CAPEX is
  // always payload.capex_total_final (server-fed).
  // ITER233 — Required CAPEX is now subtotal ONLY. Pre-opening Reserve
  // and Franchise Fee live in the Investment Inputs layer.
  const subtotal = draft.reduce((s, li) => s + (Number(li.amount_inr) || 0), 0);
  const draftRequired = subtotal;
  // Canonical numbers from the backend engine
  const canonicalRequired = payload.capex_total_final ?? payload.required_inr ?? null;
  const canonicalSubtotal = payload.subtotal_inr ?? null;
  // Required to render: prefer canonical; fall back to local while dirty.
  const required = canonicalRequired ?? draftRequired;
  const adequacy = payload.budget_adequacy_pct ?? null;

  return (
    <div className="mt-3" data-testid="ei-pfr-capex-workspace">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-wide text-slate-400">
          CAPEX workspace — edits auto-save & recalculate in seconds. Click <b>Force Full Recalculate</b> to also refresh AI + market intel.
        </div>
        <div className="flex items-center gap-2">
          {/* ITER228 P0-1 — save-status badge */}
          {saveStatus === 'saving' && (
            <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200 animate-pulse"
                  data-testid="ei-capex-saving">Saving…</span>
          )}
          {saveStatus === 'saved' && !dirty && savedAt && (
            <span className="text-[10px] uppercase bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200"
                  data-testid="ei-capex-saved">
              Saved {savedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-[10px] uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded border border-rose-200"
                  data-testid="ei-capex-save-error">Save failed — retry</span>
          )}
          <select
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
            data-testid="ei-capex-add-custom"
            onChange={e => {
              const item = catalog.find(c => c.key === e.target.value);
              if (item) addCustomLine(item);
              e.target.value = '';
            }}
            defaultValue=""
          >
            <option value="">+ Add line…</option>
            {catalog.map(c => (
              <option key={c.key} value={c.key}>{c.label} ({CAPEX_UOM_LABELS[c.uom]})</option>
            ))}
            <option value="" disabled>──────────</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => addCustomLine({})}
                  data-testid="ei-capex-add-blank">+ Blank line</Button>
          {dirty && (
            <Button size="sm" variant="ghost" onClick={resetAll}
                    data-testid="ei-capex-reset-all"
                    className="text-rose-700">Discard changes</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => commit(true)}
                  data-testid="ei-capex-save-recalc"
                  title="Re-runs the full pipeline (Apify + Stage-1 + Stage-2 AI). Slow. Only use when AI / market intel needs refreshing.">
            <Save className="w-3 h-3 mr-1" /> Force Full Recalculate
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-500">
              <th className="py-1.5 px-2">Line</th>
              <th className="py-1.5 px-2">UOM</th>
              <th className="py-1.5 px-2 text-right">Qty</th>
              <th className="py-1.5 px-2 text-right">Rate ₹</th>
              <th className="py-1.5 px-2 text-right">Amount</th>
              <th className="py-1.5 px-2 text-right">Benchmark ₹</th>
              <th className="py-1.5 px-2">Conf</th>
              <th className="py-1.5 px-2"> </th>
            </tr>
          </thead>
          <tbody>
            {draft.map((li, i) => (
              <tr key={li.line_id || i} className="border-t border-slate-100"
                  data-testid={`ei-capex-row-${li.line_id}`}>
                <td className="py-1.5 px-2">
                  <input type="text"
                         value={li.label || ''}
                         onChange={e => editLine(i, { label: e.target.value })}
                         className="bg-transparent w-full focus:outline-none focus:bg-yellow-50 px-1"
                         data-testid={`ei-capex-label-${li.line_id}`} />
                  <div className="text-[10px] text-slate-400 truncate">{li.assumption}</div>
                </td>
                <td className="py-1.5 px-2">
                  <select value={li.uom || 'sqft'}
                          onChange={e => editLine(i, { uom: e.target.value })}
                          className="text-xs bg-transparent border border-transparent focus:border-slate-300 rounded px-1"
                          data-testid={`ei-capex-uom-${li.line_id}`}>
                    {CAPEX_UOMS.map(u => <option key={u} value={u}>{CAPEX_UOM_LABELS[u]}</option>)}
                  </select>
                </td>
                <td className="py-1.5 px-2">
                  <input type="number" value={li.quantity ?? ''}
                         onChange={e => editLine(i, { quantity: e.target.value })}
                         className="w-20 text-right font-mono bg-transparent focus:bg-yellow-50 px-1 rounded"
                         data-testid={`ei-capex-qty-${li.line_id}`} />
                </td>
                <td className="py-1.5 px-2">
                  <input type="number" value={li.rate_inr ?? ''}
                         onChange={e => editLine(i, { rate_inr: e.target.value })}
                         className="w-24 text-right font-mono bg-transparent focus:bg-yellow-50 px-1 rounded"
                         data-testid={`ei-capex-rate-${li.line_id}`} />
                </td>
                <td className="py-1.5 px-2 text-right font-mono font-medium"
                    data-testid={`ei-capex-amt-${li.line_id}`}>
                  ₹{((Number(li.quantity)||0) * (Number(li.rate_inr)||0)).toLocaleString('en-IN')}
                </td>
                <td className="py-1.5 px-2 text-right font-mono text-slate-500">
                  ₹{(li.benchmark_inr || 0).toLocaleString('en-IN')}
                  {li.user_override && (
                    <button onClick={() => resetLine(i)}
                            className="ml-1 text-[10px] text-blue-600 hover:underline"
                            data-testid={`ei-capex-reset-${li.line_id}`}>reset</button>
                  )}
                </td>
                <td className="py-1.5 px-2">
                  <PFRConfidenceChip conf={li.confidence} />
                  {li.user_override && (
                    <span className="ml-1 text-[9px] uppercase bg-blue-100 text-blue-700 px-1 rounded">edited</span>
                  )}
                </td>
                <td className="py-1.5 px-2">
                  {(li.is_custom || li.user_override) && (
                    <button onClick={() => deleteLine(i)}
                            className="text-[10px] text-rose-600 hover:underline"
                            data-testid={`ei-capex-del-${li.line_id}`}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50">
            <tr className="border-t border-slate-200">
              <td className="py-1.5 px-2 font-medium" colSpan={4}>Subtotal{dirty && <span className="ml-2 text-[9px] uppercase text-amber-700">draft preview</span>}</td>
              <td className="py-1.5 px-2 text-right font-mono font-medium" data-testid="ei-capex-subtotal">
                ₹{(dirty ? subtotal : (canonicalSubtotal ?? subtotal)).toLocaleString('en-IN')}
              </td>
              <td colSpan={3}></td>
            </tr>
            {/* ITER233 — Pre-opening Reserve and Franchise Fee rows REMOVED
                from the CAPEX Workspace. They now live in the Investment
                Inputs layer (Brief → Financial Viability → Investor
                Snapshot → PDF). The workspace is a pure CAPEX planning
                tool. */}
            <tr className="border-t border-slate-300 bg-slate-100">
              <td className="py-1.5 px-2 font-semibold" colSpan={4}>
                Required CAPEX
                {saveStatus === 'saving' && (
                  <span className="ml-2 text-[9px] uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-300">
                    SAVING…
                  </span>
                )}
                {saveStatus !== 'saving' && canonicalRequired != null && (
                  <span className="ml-2 text-[9px] uppercase bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                    Locked
                  </span>
                )}
                <span className="ml-2 text-[9px] uppercase text-slate-400" data-testid="ei-capex-sot-tag">
                  CAPEX line items only · investment inputs handled separately
                </span>
              </td>
              <td className="py-1.5 px-2 text-right font-mono font-semibold" data-testid="ei-capex-required">
                ₹{required.toLocaleString('en-IN')}
              </td>
              <td className="py-1.5 px-2 text-slate-600">Adequacy {adequacy ?? '—'}%</td>
              <td className="py-1.5 px-2 text-slate-600" colSpan={2}>
                Gap: {payload.funding_gap_inr ? '₹' + payload.funding_gap_inr.toLocaleString('en-IN') : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {payload.space_plan && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid="ei-pfr-capex-space-plan">
          Active space plan source: <strong>{payload.space_plan.source}</strong>
          {' · '}Total: {payload.space_plan.total_sqft} sqft (leased {payload.space_plan.leased_sqft} sqft, drift {payload.space_plan.tolerance_pct}%)
        </div>
      )}
    </div>
  );
}

function PFRConfidenceChip({ conf, testid }) {
  if (!conf) return null;
  const tone = conf === 'high' ? 'bg-emerald-100 text-emerald-700' :
               conf === 'medium' ? 'bg-amber-100 text-amber-700' :
                                    'bg-rose-100 text-rose-700';
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${tone}`}
          data-testid={testid}>
      {conf} confidence
    </span>
  );
}
function PFRInputsUsed({ inputs, testid }) {
  if (!inputs) return null;
  const rows = Object.entries(inputs).filter(([k, v]) => v !== null && v !== undefined && v !== '' && k !== 'analysed_location' && k !== 'space_allocation');
  if (!rows.length) return null;
  function fmtVal(k, v) {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (Array.isArray(v)) return v.join(', ') || '—';
    if (typeof v === 'object') {
      if (k === 'budget' && (v.min_inr || v.max_inr)) {
        return `₹${(v.min_inr || 0).toLocaleString('en-IN')} – ₹${(v.max_inr || 0).toLocaleString('en-IN')}`;
      }
      return JSON.stringify(v);
    }
    if (typeof v === 'number' && (k.includes('inr') || k.includes('rent') || k.includes('deposit') || k.includes('fee'))) {
      return '₹' + v.toLocaleString('en-IN');
    }
    return String(v).replace(/_/g, ' ');
  }
  return (
    <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Inputs used</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-xs">
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="text-slate-500 text-[10px]">{k.replace(/_/g, ' ').replace(/\bsqft\b/, '(sqft)').replace(/\bft\b/, '(ft)')}</div>
            <div className="text-slate-800 font-medium">{fmtVal(k, v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function PFRSection({ section, payload, capexHandlers }) {
  if (!payload) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4"
         data-testid={`ei-pfr-section-${section.k}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-sm font-medium text-slate-800">{section.l}</div>
        <div className="flex items-center gap-2">
          <PFRConfidenceChip conf={payload.confidence} testid={`ei-pfr-conf-${section.k}`} />
          {payload.evidence_sufficient === false && (
            <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              low-evidence
            </span>
          )}
          {payload.hard_blocker && (
            <span className="text-[10px] uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
              hard blocker
            </span>
          )}
          <span className="text-2xl font-semibold text-slate-900"
                data-testid={`ei-pfr-score-${section.k}`}>{payload.score}</span>
        </div>
      </div>
      <ScoreBar score={payload.score} band={payload.band} />
      <div className="text-xs text-slate-600 mt-2 leading-relaxed">{payload.reason}</div>

      {section.k === 'capex' && payload.line_items && payload.line_items.length > 0 && (
        <CapexWorkspace payload={payload} {...(capexHandlers || {})} />
      )}

      {section.k === 'property' && (payload.checks || []).length > 0 && (
        <div className="mt-3" data-testid="ei-pfr-property-checks">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="py-1.5 px-2">Check</th>
                <th className="py-1.5 px-2 text-right">Score</th>
                <th className="py-1.5 px-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {payload.checks.map((c, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5 px-2 capitalize">{c.key.replace(/_/g,' ')}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{c.score}</td>
                  <td className="py-1.5 px-2 text-slate-600">{c.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PFRInputsUsed inputs={payload.inputs_used} testid={`ei-pfr-inputs-used-${section.k}`} />

      <details className="mt-3 text-[11px]">
        <summary className="cursor-pointer text-slate-500 select-none">Formula</summary>
        <div className="mt-1 space-y-1">
          {payload.formula && <div className="text-slate-700">{payload.formula}</div>}
        </div>
      </details>
    </div>
  );
}
function ProjectFeasibilityTier({ data, brief, capexHandlers }) {
  const pfr = data?.project_feasibility;
  if (!pfr) {
    return (
      <Card data-testid="ei-pfr-empty"><CardContent className="p-8 text-center text-slate-600 space-y-2">
        <ShieldCheck className="w-8 h-8 text-slate-400 mx-auto" />
        <div className="text-base font-medium text-slate-800">Add a Project Brief to unlock Project Feasibility</div>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          PFR evaluates whether the project can realistically be built — capex,
          property, execution and regulatory feasibility. Add area, rent, deposit,
          seating and frontage in the brief to enable it.
        </p>
      </CardContent></Card>
    );
  }
  const v = pfr.verdict;
  const ic = pfr.inputs_consumed || {};
  const loc = pfr.analysed_location;
  return (
    <div className="space-y-6" data-testid="ei-pfr-tier">
      {/* Location header */}
      {loc && (
        <div className="rounded-md bg-slate-900 text-amber-300 px-4 py-2.5 text-sm" data-testid="ei-pfr-location-header">
          <span className="text-xs uppercase tracking-wide text-amber-200/70 mr-2">Location Analysed</span>
          {loc}
        </div>
      )}

      {/* Inputs Consumed — single source of truth at the top */}
      <Card data-testid="ei-pfr-inputs-consumed-card">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Inputs Consumed</h3>
          <div className="grid sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
            <PFRIRow label="Area"             value={ic.area_sqft ? `${ic.area_sqft} sqft` : '—'} />
            <PFRIRow label="Frontage"         value={ic.frontage_ft ? `${ic.frontage_ft} ft` : '—'} />
            <PFRIRow label="Seating"          value={ic.seating_capacity ?? '—'} />
            <PFRIRow label="Parking"          value={ic.parking_available === true ? 'Yes' : ic.parking_available === false ? 'No' : '—'} />
            <PFRIRow label="Veg only"         value={ic.veg_only === true ? 'Yes' : ic.veg_only === false ? 'No' : '—'} testid="ei-pfr-ic-veg" />
            <PFRIRow label="Alcohol required" value={ic.alcohol_required === true ? 'Yes' : ic.alcohol_required === false ? 'No' : '—'} testid="ei-pfr-ic-alcohol" />
            <PFRIRow label="Property status"  value={ic.property_status ? String(ic.property_status).replace(/_/g,' ') : '—'} />
            <PFRIRow label="Delivery mix"     value={ic.delivery_vs_dinein ? String(ic.delivery_vs_dinein).replace(/_/g,' ') : '—'} />
            <PFRIRow label="Format"           value={(ic.preferred_formats || []).join(', ') || '—'} />
            <PFRIRow label="Budget"           value={ic.budget ? `₹${(ic.budget.min_inr||0).toLocaleString('en-IN')} – ₹${(ic.budget.max_inr||0).toLocaleString('en-IN')}` : '—'} />
            <PFRIRow label="Rent / month"     value={ic.rent_inr_month ? '₹' + ic.rent_inr_month.toLocaleString('en-IN') : '—'} />
            <PFRIRow label="Deposit"          value={ic.deposit_inr ? '₹' + ic.deposit_inr.toLocaleString('en-IN') : '—'} />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="ei-pfr-verdict-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Project Feasibility verdict</div>
              <div className="flex items-center gap-3 mt-1">
                <PFRVerdictBadge decision={v.decision} testid="ei-pfr-verdict" />
                <div className="text-lg text-slate-800">
                  Aggregate: <span className="font-semibold" data-testid="ei-pfr-aggregate">{v.aggregate}</span>
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-slate-500">
              Weights: capex {Math.round(v.weights.capex*100)}% ·
              property {Math.round(v.weights.property*100)}% ·
              execution {Math.round(v.weights.execution*100)}% ·
              regulatory {Math.round(v.weights.regulatory*100)}%
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-3" data-testid="ei-pfr-rationale">{v.rationale}</p>
          {(pfr.missing_brief_fields || []).length > 0 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5"
                 data-testid="ei-pfr-missing">
              Missing brief fields (sections may fall back to neutral 50s):{' '}
              <strong>{pfr.missing_brief_fields.join(', ')}</strong>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        {PFR_SECTIONS.map(s => (
          <PFRSection key={s.k} section={s} payload={pfr.sections?.[s.k]}
                      capexHandlers={s.k === 'capex' ? capexHandlers : undefined} />
        ))}
      </div>

      <div className="text-xs text-slate-400 px-2" data-testid="ei-pfr-consumed-layers">
        Inputs: {pfr.consumed_layers?.join(' · ')} · {pfr.note}
      </div>
    </div>
  );
}
function PFRIRow({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 font-medium">{value}</div>
    </div>
  );
}
function VerdictBucket({ title, items, tone, testid }) {
  const cls = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber:   'border-amber-200 bg-amber-50',
    orange:  'border-orange-200 bg-orange-50',
    rose:    'border-rose-200 bg-rose-50',
  }[tone] || 'border-slate-200 bg-slate-50';
  return (
    <div className={`rounded-lg border ${cls} p-3`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-600 mb-1">{title}</div>
      {(!items || items.length === 0) && <div className="text-xs text-slate-400">—</div>}
      <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
        {(items || []).map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}

/* ─── FINANCIAL VIABILITY TIER (iter189) ──────────────────────────── */
function fmtINR(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + ' Cr';
  if (n >= 100000)   return '₹' + (n / 100000).toFixed(2) + ' L';
  return '₹' + Math.round(n).toLocaleString('en-IN');
}
function FVMVerdictBadge({ decision, testid }) {
  const map = {
    'Strong':   'bg-emerald-600',
    'Viable':   'bg-emerald-500',
    'Marginal': 'bg-amber-500',
    'Unviable': 'bg-rose-600',
  };
  const tone = map[decision] || 'bg-slate-500';
  return (
    <span className={`text-white text-sm font-semibold px-3 py-1.5 rounded-full ${tone}`}
          data-testid={testid}>{decision}</span>
  );
}

function FVMAssumptionsCard({ assumptions }) {
  if (!assumptions) return null;
  const apc = assumptions.apc || {};
  const occ = assumptions.occupancy || {};
  const cr  = assumptions.cost_ratios || {};
  const ad  = assumptions.anchor_density || {};
  // iter220 — Headline figures: Staff Cost (multiplied!), Rent, Bill Cuts.
  const sc    = assumptions.staff_count;
  const sa    = assumptions.avg_salary_per_staff_inr;
  const scm   = assumptions.staff_cost_monthly_inr;
  const rentM = assumptions.rent_inr_month;
  const bcd   = assumptions.bill_cuts_per_day;
  return (
    <Card data-testid="ei-fvm-assumptions">
      <CardContent className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">A · Assumptions (traceable inputs)</h3>

        {/* iter220 — Headline tiles: Bill Cuts · Staff Cost · Rent */}
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="rounded-md border-2 border-violet-300 bg-violet-50/40 p-3"
               data-testid="ei-fvm-bill-cuts-headline">
            <div className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold">Revenue Driver — Bill Cuts/Day</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1">{bcd ?? '—'}</div>
            <div className="text-[11px] text-slate-600 mt-1">{assumptions.bill_cuts_reason || 'Revenue = Bill Cuts × APC × Operating Days × ramp.'}</div>
          </div>
          <div className="rounded-md border-2 border-emerald-300 bg-emerald-50/40 p-3"
               data-testid="ei-fvm-staff-cost-headline">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Staff Cost (Headline)</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1">
              {scm ? fmtINR(scm) : '—'}<span className="text-xs text-slate-500"> / month</span>
            </div>
            <div className="text-[11px] text-slate-700 mt-1 font-mono">
              {(sc && sa)
                ? `${sc} staff × ${fmtINR(sa)}/mo = ${fmtINR(scm)}/mo`
                : 'Set Staff Count + Avg Salary in the brief.'}
            </div>
            <div className="text-[10px] text-slate-500">
              Annual labor: {scm ? fmtINR(scm * 12) : '—'}
            </div>
          </div>
          <div className="rounded-md border-2 border-amber-300 bg-amber-50/40 p-3"
               data-testid="ei-fvm-rent-headline">
            <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Rent (Independent Line)</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1">
              {rentM ? fmtINR(rentM) : '—'}<span className="text-xs text-slate-500"> / month</span>
            </div>
            <div className="text-[11px] text-slate-700 mt-1">
              Annual rent: <b>{rentM ? fmtINR(rentM * 12) : '—'}</b>
            </div>
            <div className="text-[10px] text-slate-500">Reported separately from Operating Overheads.</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 text-xs">
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase text-slate-500 tracking-wide mb-1">APC · Average Per Cover</div>
            <div className="text-lg font-semibold text-slate-900" data-testid="ei-fvm-apc">
              {apc.apc_inr ? fmtINR(apc.apc_inr) : '—'}
              {apc.band && <span className="ml-2 text-[10px] uppercase bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{apc.band} band</span>}
              {apc.source === 'price_for_two' && (
                <span className="ml-2 text-[10px] uppercase bg-violet-100 px-1.5 py-0.5 rounded text-violet-700">from Price For Two</span>
              )}
            </div>
            <p className="text-slate-600 mt-1">{apc.reason}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase text-slate-500 tracking-wide mb-1">Occupancy (fallback only)</div>
            <div className="text-lg font-semibold text-slate-900" data-testid="ei-fvm-occupancy">
              {occ.occupancy_mid_pct}%
              <span className="ml-2 text-[10px] uppercase bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                {occ.anchor_density_band}
              </span>
            </div>
            <p className="text-slate-600 mt-1">{occ.reason}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-3" data-testid="ei-fvm-cost-ratios">
            <div className="text-[11px] uppercase text-slate-500 tracking-wide mb-1">
              Cost ratios (% of revenue, excluding Rent &amp; direct Labor)
            </div>
            <div className="grid grid-cols-3 gap-2 text-slate-800">
              <div>Food <span className="font-mono">{(cr.food_cost_pct*100).toFixed(0)}%</span></div>
              <div className="text-slate-400 line-through" title="Replaced by Staff Count × Avg Salary">
                Labor <span className="font-mono">{(cr.labor_cost_pct*100).toFixed(0)}%</span>
              </div>
              <div>Ops <span className="font-mono">{(cr.ops_overhead_pct*100).toFixed(0)}%</span></div>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              Operating Overheads = <b>{(cr.ops_overhead_pct*100).toFixed(0)}%</b> of revenue, <em>excluding rent</em>.
              Labor is computed directly from Staff Count × Avg Salary above.
            </div>
          </div>
          <div className="rounded-md border border-slate-200 p-3" data-testid="ei-fvm-anchor-signal">
            <div className="text-[11px] uppercase text-slate-500 tracking-wide mb-1">Anchor density signal (from Market Intel)</div>
            <div className="text-slate-800">
              <span className="font-semibold capitalize">{ad.band}</span> · {ad.total_anchors} anchors
            </div>
            <div className="text-slate-500 mt-1">{ad.rule}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3 md:col-span-2">
            <div className="grid sm:grid-cols-2 gap-3 text-slate-800">
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wide">Operating days / yr</div>
                <div className="font-mono">{assumptions.operating_days_per_year}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wide">CAPEX required</div>
                <div className="font-mono">{fmtINR(assumptions.capex_required_inr)}
                  {!assumptions.capex_funded && (
                    <span className="ml-2 text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">funding gap</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* iter220 — EBITDA formula explainer band */}
          <div className="rounded-md border border-emerald-200 bg-emerald-50/30 p-3 md:col-span-2"
               data-testid="ei-fvm-ebitda-formula">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">EBITDA Formula</div>
            <div className="text-xs font-mono text-slate-800 leading-relaxed">
              EBITDA = Revenue − Food Cost − Labor (Staff Count × Avg Salary)
              − Rent (independent) − Operating Overheads (excluding rent)
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FVMRevenueCard({ revenue, cost }) {
  if (!revenue?.evidence_sufficient) {
    return (
      <Card><CardContent className="p-6 text-sm text-slate-600" data-testid="ei-fvm-revenue-insufficient">
        <strong>Revenue Model — insufficient evidence.</strong> {revenue?.reason || 'Complete the project brief to enable revenue projection.'}
      </CardContent></Card>
    );
  }
  // iter220 — Bill-cuts is the PRIMARY driver. Seats / occupancy shown only
  // as fallback context inside the disclosure.
  const billCuts   = revenue.bill_cuts_per_day ?? revenue.covers_per_day;
  const billSource = revenue.bill_cuts_source || revenue.covers_calc?.source || '—';
  const isUserBillCuts = billSource === 'brief.expected_bill_cuts_per_day';
  const cm = revenue.channel_mix || {};
  // Approximate monthly steady-state revenue (Y1 / 12 understates ramp; we
  // surface Y1 average for honesty + Y3 steady-state on a separate stat).
  const monthlyY1 = revenue.annual_revenue_y1 ? Math.round(revenue.annual_revenue_y1 / 12) : null;
  return (
    <Card data-testid="ei-fvm-revenue">
      <CardContent className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          B · Revenue Model
          <span className="ml-2 text-[10px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
            bill-cuts × APC × ramp
          </span>
        </h3>
        <div className="grid sm:grid-cols-4 gap-3 text-sm">
          <Stat
            label="Expected Bill Cuts / Day"
            value={billCuts ?? '—'}
            testid="ei-fvm-rev-bill-cuts"
            hint={isUserBillCuts ? 'User-supplied (primary driver)' : 'Fallback: seats × turns × occupancy'}
          />
          <Stat label="APC" value={fmtINR(revenue.apc_inr)} testid="ei-fvm-rev-apc"
                hint={revenue.apc_source === 'price_for_two' ? 'Derived from Price For Two' : revenue.apc_source}/>
          <Stat label="Monthly Revenue (Y1 avg)"
                value={monthlyY1 ? fmtINR(monthlyY1) : '—'} testid="ei-fvm-rev-monthly"/>
          <Stat label="Annual Revenue · Year 1"
                value={fmtINR(revenue.annual_revenue_y1)} testid="ei-fvm-rev-y1" />
          <Stat label="Annual Revenue · Year 3"
                value={fmtINR(revenue.annual_revenue_y3)} testid="ei-fvm-rev-y3" />
          <Stat label="Operating days / yr" value={revenue.operating_days_per_year} />
          {cm.dine_in_pct != null && (
            <>
              <Stat label="Dine-In share" value={`${cm.dine_in_pct}%`}
                    hint={`Y1 dine-in: ${fmtINR(revenue.y1_dine_in_revenue_inr)}`}/>
              <Stat label="Delivery + Takeaway share"
                    value={`${(cm.delivery_pct||0) + (cm.takeaway_pct||0)}%`}
                    hint={`Y1 delivery ${fmtINR(revenue.y1_delivery_revenue_inr)} · takeaway ${fmtINR(revenue.y1_takeaway_revenue_inr)}`}/>
            </>
          )}
        </div>
        <details className="mt-3 text-[11px]" open>
          <summary className="cursor-pointer text-slate-500 select-none">Formula &amp; covers fallback</summary>
          <div className="mt-2 space-y-1 text-slate-700">
            <div><strong>Driver:</strong> {revenue.formula}</div>
            <div className="text-slate-500">Fallback context (if Bill Cuts not supplied): {revenue.covers_calc?.reason}</div>
            {cost && <div><strong>Cost formula:</strong> {cost.formula}</div>}
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

function FVMYearTable({ years }) {
  if (!years?.length) return null;
  return (
    <Card data-testid="ei-fvm-year-table">
      <CardContent className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">C · P&amp;L Summary (Year 1 – 3)</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="py-2 px-2">Year</th>
                <th className="py-2 px-2 text-right">Revenue</th>
                <th className="py-2 px-2 text-right">Food</th>
                <th className="py-2 px-2 text-right">Labor</th>
                <th className="py-2 px-2 text-right">Ops</th>
                <th className="py-2 px-2 text-right">Rent</th>
                <th className="py-2 px-2 text-right">EBITDA</th>
                <th className="py-2 px-2 text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {years.map(y => (
                <tr key={y.year} className="border-t border-slate-100" data-testid={`ei-fvm-year-${y.year}`}>
                  <td className="py-2 px-2 font-medium">Y{y.year}</td>
                  <td className="py-2 px-2 text-right font-mono">{fmtINR(y.revenue_inr)}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-500">{fmtINR(y.food_inr)}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-500">{fmtINR(y.labor_inr)}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-500">{fmtINR(y.ops_inr)}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-500">{fmtINR(y.rent_inr)}</td>
                  <td className="py-2 px-2 text-right font-mono font-semibold">{fmtINR(y.ebitda_inr)}</td>
                  <td className="py-2 px-2 text-right font-mono">{y.ebitda_pct == null ? '—' : y.ebitda_pct + '%'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function FVMMonthlyChart({ rows }) {
  if (!rows?.length) return null;
  // iter220 — Plot Revenue + EBITDA together so the cashflow story is
  // visible without opening the PDF.
  const maxRev = Math.max(1, ...rows.map(r => r.revenue || 0));
  const maxAbsEb = Math.max(1, ...rows.map(r => Math.abs(r.ebitda || 0)));
  return (
    <Card data-testid="ei-fvm-monthly-chart">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-sm font-semibold text-slate-900">D · 36-Month Cashflow Curve</h3>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="inline-flex items-center gap-1 text-slate-700">
              <span className="inline-block w-3 h-2 rounded-sm bg-sky-400"/>Revenue
            </span>
            <span className="inline-flex items-center gap-1 text-slate-700">
              <span className="inline-block w-3 h-2 rounded-sm bg-emerald-400"/>EBITDA +
            </span>
            <span className="inline-flex items-center gap-1 text-slate-700">
              <span className="inline-block w-3 h-2 rounded-sm bg-rose-400"/>EBITDA −
            </span>
            <span className="inline-flex items-center gap-1 text-slate-600">
              <span className="inline-block w-3 h-px bg-amber-500"/>Cumulative EBITDA
            </span>
          </div>
        </div>
        <div className="space-y-1">
          {rows.map(r => {
            const revW = (r.revenue / maxRev) * 100;
            const ebW  = (Math.abs(r.ebitda) / maxAbsEb) * 100;
            const pos = r.ebitda >= 0;
            const tip = `Month ${r.month}\nRevenue: ${fmtINR(r.revenue)}\nEBITDA: ${fmtINR(r.ebitda)} (${pos ? '+' : '−'})\nCumulative EBITDA: ${fmtINR(r.cum_ebitda)}\nRamp: ${(r.ramp*100).toFixed(0)}%`;
            return (
              <div key={r.month} className="flex items-center gap-2 text-[10px]"
                   title={tip}
                   data-testid={`ei-fvm-cashflow-row-${r.month}`}>
                <span className="w-8 text-slate-500 font-mono">M{r.month}</span>
                {/* Revenue band (full-width, sky) */}
                <div className="flex-1 h-3 bg-slate-100 rounded-sm relative overflow-hidden">
                  <div className="h-full bg-sky-300"
                       style={{ width: `${Math.max(2, revW)}%` }}/>
                  {/* EBITDA overlay (positive → emerald, negative → rose) */}
                  <div className={`absolute top-0 left-0 h-full ${pos ? 'bg-emerald-500/70' : 'bg-rose-500/70'}`}
                       style={{ width: `${Math.max(2, ebW * 0.6)}%` }}/>
                </div>
                <span className="w-24 text-right font-mono text-sky-700"
                      data-testid={`ei-fvm-cashflow-rev-${r.month}`}>
                  {fmtINR(r.revenue)}
                </span>
                <span className="w-24 text-right font-mono text-slate-700">{fmtINR(r.ebitda)}</span>
                <span className="w-24 text-right font-mono text-amber-700">
                  cum {fmtINR(r.cum_ebitda)}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function FVMSensitivity({ scenarios }) {
  if (!scenarios?.length) return null;
  return (
    <Card data-testid="ei-fvm-sensitivity">
      <CardContent className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">E · Sensitivity Analysis (Year-1 EBITDA %)</h3>
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          {scenarios.map((s, i) => {
            const v = s.y1_ebitda_pct;
            const dp = s.delta_pp_vs_base;
            const isBase = s.scenario === 'Base';
            const tone = v == null ? 'bg-slate-100' :
                          v >= 20 ? 'bg-emerald-50 border-emerald-200' :
                          v >= 10 ? 'bg-amber-50 border-amber-200' :
                                    'bg-rose-50 border-rose-200';
            return (
              <div key={i} className={`rounded-md border p-2 flex items-center justify-between ${tone}`}
                   data-testid={`ei-fvm-sens-${i}`}>
                <span className={`${isBase ? 'font-semibold' : ''} text-slate-800`}>{s.scenario}</span>
                <span className="font-mono text-slate-900">
                  {v == null ? '—' : v + '%'}
                  {dp != null && !isBase && (
                    <span className={`ml-2 text-[10px] ${dp > 0 ? 'text-emerald-700' : dp < 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                      ({dp > 0 ? '+' : ''}{dp}pp)
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function InvestorSnapshotPanel({ fvm, brief, data }) {
  if (!fvm) return null;
  const v   = fvm.verdict || {};
  const y1  = fvm.year_summaries?.[0];
  const y2  = fvm.year_summaries?.[1];
  const y3  = fvm.year_summaries?.[2];
  const be  = fvm.break_even;
  const pb  = fvm.payback;
  const er  = data?.executive_recommendation_stage2?.reasoning
            || data?.executive_recommendation?.reasoning || {};
  const cx  = fvm.assumptions?.capex_total_final ?? fvm.assumptions?.capex_required_inr;
  // ITER227 P0-2 — resolved investment inputs (FV override → brief → 0).
  // Consumes ONLY the FVM engine output. No direct brief reads.
  const inv = fvm.assumptions?.investment_inputs || {};
  const _src = (s) => {
    if (!s) return '—';
    if (s === 'fv_override') return 'FV override';
    if (s === 'brief')       return 'From brief';
    if (s === 'default_zero') return 'Not provided';
    if (s === 'not_provided') return 'Not provided';
    if (typeof s === 'string' && s.startsWith('blanked_by_venture_type'))
      return 'N/A — venture type';
    return s;
  };
  const depositVal      = inv.deposit_inr;
  const franchiseVal    = inv.franchise_fee_inr;
  const franchiseHint   = _src(inv.franchise_fee_source);
  const workingCapital  = inv.working_capital_inr; // may be null (no default)
  const totalInv        = fvm.assumptions?.total_investment_inr;
  // Investor-side risk band derived from EBITDA% + payback.
  const ebPct = y1?.ebitda_pct ?? 0;
  const pbm   = pb?.months ?? 99;
  const risk  = (ebPct >= 20 && pbm <= 24) ? 'LOW'
              : (ebPct >= 10 && pbm <= 36) ? 'MEDIUM'
              : 'HIGH';
  const riskTone = risk === 'LOW' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                : risk === 'MEDIUM' ? 'bg-amber-100 text-amber-800 border-amber-300'
                : 'bg-rose-100 text-rose-800 border-rose-300';
  const interp = er.executive_verdict || er.management_recommendations
              || er.recommended_next_step;
  return (
    <Card data-testid="ei-fvm-investor-snapshot" className="border-2 border-slate-300 bg-gradient-to-br from-slate-50 to-white">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Investment Report</div>
            <h2 className="text-xl font-semibold text-slate-900 mt-0.5">Financial Viability — Investor Snapshot</h2>
          </div>
          <div className="flex items-center gap-2">
            <FVMVerdictBadge decision={v.decision} testid="ei-fvm-investor-verdict" />
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${riskTone}`}
                  data-testid="ei-fvm-risk-band">
              Risk · {risk}
            </span>
          </div>
        </div>

        {/* 1 — Investment Snapshot */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">1 · Investment Snapshot</div>
          <div className="grid sm:grid-cols-5 gap-3">
            <InvStat label="CAPEX required" v={fmtINR(cx)} testid="ei-inv-capex"/>
            <InvStat label="Security deposit"
                     v={depositVal ? fmtINR(depositVal) : '—'}
                     hint={_src(inv.deposit_source)}
                     testid="ei-inv-deposit"/>
            <InvStat label="Franchise fee"
                     v={franchiseVal ? fmtINR(franchiseVal) : '—'}
                     hint={franchiseHint}
                     testid="ei-inv-franchise"/>
            <InvStat label="Working capital"
                     v={workingCapital ? fmtINR(workingCapital) : '—'}
                     hint={_src(inv.working_capital_source)}
                     testid="ei-inv-working-capital"/>
            <InvStat label="Total investment"
                     v={totalInv ? fmtINR(totalInv) : '—'}
                     hint="CAPEX + Deposit + Franchise + WC"
                     testid="ei-inv-total"/>
          </div>
        </div>

        {/* 2 — Revenue Potential */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">2 · Revenue Potential</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <InvStat label="Year 1 revenue" v={fmtINR(y1?.revenue_inr)} testid="ei-inv-rev-y1"/>
            <InvStat label="Year 2 revenue" v={fmtINR(y2?.revenue_inr)} testid="ei-inv-rev-y2"/>
            <InvStat label="Year 3 revenue" v={fmtINR(y3?.revenue_inr)} testid="ei-inv-rev-y3"/>
          </div>
        </div>

        {/* 3 — Profitability */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">3 · Profitability</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <InvStat label="Year 1 EBITDA" v={fmtINR(y1?.ebitda_inr)} testid="ei-inv-ebitda-y1"
                     hint={`${y1?.ebitda_pct ?? '—'}% margin`}/>
            <InvStat label="Year 3 EBITDA" v={fmtINR(y3?.ebitda_inr)}
                     hint={`${y3?.ebitda_pct ?? '—'}% margin`}/>
            <InvStat label="Break-even month" v={be?.month ? `Month ${be.month}` : 'Not within 36 mo'}
                     testid="ei-inv-breakeven"/>
          </div>
        </div>

        {/* 4 — Returns */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">4 · Returns</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <InvStat label="Payback period" v={pb?.months ? `${pb.months} mo` : 'Not within 36 mo'}
                     testid="ei-inv-payback"/>
            <InvStat label="3-yr cumulative EBITDA"
                     v={fmtINR((y1?.ebitda_inr || 0) + (y2?.ebitda_inr || 0) + (y3?.ebitda_inr || 0))}/>
            <InvStat label="Implied 3-yr ROI on CAPEX"
                     v={cx ? `${Math.round(100 * ((y1?.ebitda_inr || 0) + (y2?.ebitda_inr || 0) + (y3?.ebitda_inr || 0)) / cx)}%` : '—'}
                     testid="ei-inv-roi"/>
          </div>
        </div>

        {/* 5 — Risk band rationale */}
        <div className="mb-4 rounded-md border border-slate-200 bg-white p-3"
             data-testid="ei-fvm-risk-rationale">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">5 · Risk Assessment</div>
          <div className="text-sm text-slate-700">
            Investment risk is rated <b className={
              risk === 'LOW' ? 'text-emerald-700' :
              risk === 'MEDIUM' ? 'text-amber-700' : 'text-rose-700'
            }>{risk}</b>{' '}
            based on Year-1 EBITDA margin of <b>{ebPct}%</b> and CAPEX payback of <b>{pbm < 99 ? `${pbm} months` : 'over 36 months'}</b>.
            Thresholds: Low (≥20% &amp; ≤24mo) · Medium (≥10% &amp; ≤36mo) · High (otherwise).
          </div>
        </div>

        {/* 6 — AI Financial Interpretation */}
        {interp && (
          <div className="rounded-md border-l-4 border-emerald-500 bg-emerald-50/40 p-3"
               data-testid="ei-fvm-ai-interpretation">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
              6 · AI Financial Interpretation
            </div>
            <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
              {typeof interp === 'string' ? interp
                : Array.isArray(interp) ? interp.join(' · ')
                : JSON.stringify(interp)}
            </div>
          </div>
        )}

        <div className="text-[10px] text-slate-400 italic mt-3">
          Detailed engine workings (assumption workspace, sensitivity, 36-month projection) appear below the verdict card.
        </div>
      </CardContent>
    </Card>
  );
}

function InvStat({ label, v, hint, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-base font-semibold text-slate-900 mt-0.5 truncate">{v ?? '—'}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}


function FinancialViabilityTier({ data, brief, fvmHandlers, recomputing }) {
  const fvm = data?.financial_viability;
  if (!fvm) {
    return (
      <Card data-testid="ei-fvm-empty"><CardContent className="p-8 text-center text-slate-600 space-y-2">
        <BarChart3 className="w-8 h-8 text-slate-400 mx-auto" />
        <div className="text-base font-medium text-slate-800">Add a Project Brief to unlock Financial Viability</div>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          FVM evaluates whether the project will make money — revenue, P&amp;L, EBITDA,
          break-even, payback and sensitivity. Provide format, seating, rent and
          budget in the brief to enable it.
        </p>
      </CardContent></Card>
    );
  }
  const v = fvm.verdict;
  const be = fvm.break_even;
  const pb = fvm.payback;
  const y1 = fvm.year_summaries?.[0];
  return (
    <div className={`space-y-6 ${recomputing ? 'opacity-80 pointer-events-none' : ''}`}
         data-testid="ei-fvm-tier"
         data-recomputing={recomputing ? 'true' : 'false'}>
      {recomputing && (
        <div className="rounded-md border-2 border-violet-300 bg-violet-50 px-4 py-3 flex items-center gap-3"
             data-testid="ei-fvm-recomputing-banner">
          <RefreshCw className="w-4 h-4 text-violet-700 animate-spin"/>
          <div className="text-sm text-violet-900">
            <strong>Recalculating Financial Viability…</strong> Revenue, EBITDA,
            break-even and payback below will refresh with the new assumption
            overrides as soon as the engine completes (typically 10–30s
            depending on Google Places cache hits).
          </div>
        </div>
      )}

      {/* iter220c — Investor Snapshot (top of page, investor-narrative view) */}
      <InvestorSnapshotPanel fvm={fvm} brief={brief} data={data} />

      {/* Verdict card */}
      <Card data-testid="ei-fvm-verdict-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Financial Viability verdict</div>
              <div className="flex items-center gap-3 mt-1">
                <FVMVerdictBadge decision={v.decision} testid="ei-fvm-verdict" />
                {y1 && y1.ebitda_pct != null && (
                  <div className="text-lg text-slate-800">
                    Y1 EBITDA: <span className="font-semibold" data-testid="ei-fvm-y1-margin">{y1.ebitda_pct}%</span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right text-xs text-slate-500">
              Thresholds (env-configurable):
              <div>Strong ≥{v.thresholds?.ebitda_pct_strong}% · Viable ≥{v.thresholds?.ebitda_pct_viable}% · Marginal ≥{v.thresholds?.ebitda_pct_marginal}%</div>
              <div>Payback Strong ≤{v.thresholds?.payback_strong_months}m · Viable ≤{v.thresholds?.payback_viable_months}m</div>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-3" data-testid="ei-fvm-rationale">{v.rationale}</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <Stat label="Break-even (cum EBITDA ≥ 0)" value={be?.month ? `Month ${be.month}` : '—'} testid="ei-fvm-breakeven" />
            <Stat label="CAPEX payback (months)" value={pb?.months ? `${pb.months} mo` : 'Not within 36 mo'} testid="ei-fvm-payback" />
            <Stat label="Year-1 EBITDA (₹)" value={y1 ? fmtINR(y1.ebitda_inr) : '—'} />
          </div>
          {(fvm.missing_brief_fields || []).length > 0 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5"
                 data-testid="ei-fvm-missing">
              Missing brief fields: <strong>{fvm.missing_brief_fields.join(', ')}</strong>
            </div>
          )}
        </CardContent>
      </Card>

      <FVMAssumptionsCard assumptions={fvm.assumptions} />
      <FVMAssumptionWorkspace workspace={data?.fvm_assumption_workspace}
                              brief={brief} {...(fvmHandlers || {})} />
      <FVMRevenueCard revenue={fvm.revenue_model} cost={fvm.cost_structure} />
      <FVMYearTable years={fvm.year_summaries} />
      <FVMMonthlyChart rows={fvm.monthly_projections} />
      <FVMSensitivity scenarios={fvm.sensitivity} />

      <div className="text-xs text-slate-400 px-2" data-testid="ei-fvm-consumed-layers">
        Inputs: {fvm.consumed_layers?.join(' · ')} · {fvm.note}
      </div>
    </div>
  );
}




function MarketDNAPanel({ dna }) {
  if (!dna) return null;
  const labels = {
    residential: 'Residential', corporate: 'Corporate',
    transit: 'Transit', tourism: 'Tourist / Leisure',
    education: 'Student / Education', industrial: 'Industrial',
  };
  const colors = {
    residential: 'bg-emerald-500',
    corporate:   'bg-blue-600',
    transit:     'bg-amber-500',
    tourism:     'bg-rose-500',
    education:   'bg-purple-500',
    industrial:  'bg-slate-600',
  };
  const order = ['residential', 'corporate', 'transit', 'tourism', 'education', 'industrial'];
  const mix = dna.mix_pct || {};
  const signalTone = dna.signal_quality === 'high' ? 'bg-emerald-100 text-emerald-700' :
                     dna.signal_quality === 'medium' ? 'bg-amber-100 text-amber-700' :
                                                       'bg-rose-100 text-rose-700';
  return (
    <Card data-testid="ei-market-dna-panel">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Market DNA</h3>
            <p className="text-xs text-slate-500">Evidence-weighted mix across 6 demand-generator buckets.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase text-slate-500">Dominant</span>
            <span className="text-sm font-semibold text-slate-900" data-testid="ei-market-dna-dominant">{dna.dominant}</span>
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${signalTone}`}
                  data-testid="ei-market-dna-signal">
              {dna.signal_quality || 'low'} signal
            </span>
          </div>
        </div>

        {/* Stacked bar */}
        <div className="flex h-3 rounded overflow-hidden border border-slate-200 mb-2"
             data-testid="ei-market-dna-bar">
          {order.map(b => mix[b] > 0 && (
            <div key={b} className={`${colors[b]} h-full`}
                 style={{ width: `${mix[b]}%` }}
                 title={`${labels[b]} ${mix[b]}%`} />
          ))}
        </div>

        {/* Bucket grid */}
        <div className="grid sm:grid-cols-3 gap-2 mb-3">
          {order.map(b => (
            <div key={b} className="flex items-center gap-2 text-xs"
                 data-testid={`ei-market-dna-bucket-${b}`}>
              <span className={`w-2.5 h-2.5 rounded-sm ${colors[b]}`} />
              <span className="text-slate-700 flex-1">{labels[b]}</span>
              <span className="font-mono text-slate-900 font-semibold">{mix[b] || 0}%</span>
              <span className="text-[10px] text-slate-400">{(dna.place_count || {})[b] || 0} places</span>
            </div>
          ))}
        </div>

        {/* Blend labels */}
        {(dna.blend_labels || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ei-market-dna-blend">
            {dna.blend_labels.map((b, i) => (
              <span key={i} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{b}</span>
            ))}
          </div>
        )}

        {/* Per-bucket evidence drill-down */}
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 select-none">Evidence behind each bucket</summary>
          <div className="mt-2 space-y-2">
            {order.map(b => {
              const ev = (dna.evidence || {})[b] || [];
              if (!ev.length) return null;
              return (
                <div key={b} data-testid={`ei-market-dna-evidence-${b}`}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    {labels[b]} · {ev.length} top contributor{ev.length === 1 ? '' : 's'}
                  </div>
                  <table className="min-w-full text-[11px] mt-1">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-slate-500">
                        <th className="py-1 px-2">Place</th>
                        <th className="py-1 px-2 text-right">Dist</th>
                        <th className="py-1 px-2 text-right">★</th>
                        <th className="py-1 px-2 text-right">Reviews</th>
                        <th className="py-1 px-2 text-right">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ev.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1 px-2 text-slate-700">{e.name}</td>
                          <td className="py-1 px-2 text-right font-mono text-slate-500">{e.distance_m} m</td>
                          <td className="py-1 px-2 text-right font-mono text-slate-500">{e.rating ?? '—'}</td>
                          <td className="py-1 px-2 text-right font-mono text-slate-500">{e.user_ratings ?? '—'}</td>
                          <td className="py-1 px-2 text-right font-mono text-slate-800">{e.weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </details>
        <div className="mt-3 text-[10px] text-slate-400" data-testid="ei-market-dna-formula">
          Formula: {dna.formula}
        </div>
      </CardContent>
    </Card>
  );
}

function MarketTier({ data, projectId }) {
  const comp   = data.competitors || {};
  const places = comp.places || [];
  const cd     = data.cuisine_density || {};
  const anchors= data.anchors?.buckets || {};

  // iter216 — Restaurant Intelligence overlay.
  const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;
  const { byPlaceId, progress, status, error, enrichedAt, start,
          apifyByPlaceId, apifyEnrichedAt, apifyMatchedCount,
          apifyUnmatchedCount, enrichmentMap, enrichmentHistory } = useEnrichStream({
    apiBase: API, token, projectId,
  });

  // Compute Influence Score for all visible places + sort by it DESC.
  const enrichedPlaces = useMemo(() => {
    if (!places.length) return [];
    const scored = places.map(p => ({
      ...p,
      _influence: computeInfluenceScore(p, places),
      _enrichment: byPlaceId[p.place_id],
      _apify: apifyByPlaceId[p.place_id] || null,
    }));
    return scored.sort((a, b) => (b._influence ?? 0) - (a._influence ?? 0));
  }, [places, byPlaceId, apifyByPlaceId]);

  const [deepDive, setDeepDive] = useState(null); // { place, enrichment, score }
  const [intelOpenFor, setIntelOpenFor] = useState(null); // place_id whose intel card is open
  const [compTab, setCompTab] = useState('all'); // iter225: 'all' | 'enriched'

  return (
    <div className="space-y-6" data-testid="ei-tier-market-panel">
      <MarketDNAPanel dna={data.market_dna} />
      <Card><CardContent className="p-6">
        <div className="text-sm text-slate-600 mb-3">
          <strong>Lat:</strong> <span className="font-mono">{data.coordinates?.lat?.toFixed?.(6)}</span>{' '}
          <strong className="ml-4">Lng:</strong> <span className="font-mono">{data.coordinates?.lng?.toFixed?.(6)}</span>
          {data.geocode?.formatted_address && <> {' · '} {data.geocode.formatted_address}</>}
        </div>
        <h3 className="text-base font-semibold text-slate-900 mb-2">Cuisine density</h3>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <Stat label="F&B competitors" value={cd.fnb_in_radius ?? 0} testid="ei-cd-fnb"/>
          <Stat label="Distinct cuisines" value={cd.cuisine_count ?? 0}/>
          <Stat label="HHI (concentration)" value={cd.hhi ?? '—'} hint="0 = fragmented · 1 = monopolised"/>
          <Stat label="Mean rating" value={cd.ratings?.mean ?? '—'}/>
          <Stat label="Reviews · sum" value={cd.review_counts?.sum ?? 0}/>
          <Stat label="Operational" value={`${cd.operational ?? 0} / ${cd.fnb_in_radius ?? 0}`}/>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <MiniTable title="Cuisine breakdown" headers={['Cuisine','n','Share']}
            rows={(cd.cuisines || []).map(c => [c.cuisine, c.count, `${(c.share*100).toFixed(1)}%`])} testid="ei-cd-cuisines"/>
          <MiniTable title="Format breakdown" headers={['Format','n']}
            rows={(cd.formats || []).map(f => [f.format, f.count])}/>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Competitor intelligence · {enrichedPlaces.length} places
              <span className="ml-2 text-xs font-normal text-slate-500">
                sorted by Influence Score · ({comp.attribution})
              </span>
            </h3>
            {enrichedAt && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                Last enriched: {new Date(enrichedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* iter224 P0-2 — Apify enrichment summary chip */}
            {(apifyMatchedCount > 0 || apifyEnrichedAt) && (
              <Pill tone="ok" testid="ei-apify-enrich-summary">
                <CheckCircle2 className="w-3 h-3"/>
                Apify enriched: {apifyMatchedCount}
                {apifyUnmatchedCount > 0 ? ` · ${apifyUnmatchedCount} unmatched` : ''}
              </Pill>
            )}
            {status === 'streaming' && (
              <Pill tone="warn" testid="ei-enrich-progress">
                <RefreshCw className="w-3 h-3 animate-spin"/>
                Enriching {progress.completed}/{progress.total}
              </Pill>
            )}
            {status === 'done' && progress.total > 0 && (
              <Pill tone="ok"><CheckCircle2 className="w-3 h-3"/>Enriched {progress.completed}</Pill>
            )}
            {status === 'error' && (
              <Pill tone="err"><AlertTriangle className="w-3 h-3"/>Stream error</Pill>
            )}
            <Button size="sm" variant="outline"
                    disabled={!projectId || status === 'streaming'}
                    onClick={start}
                    data-testid="ei-enrich-start-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1"/>
              {status === 'done' ? 'Re-enrich' : 'Stream enrichment'}
            </Button>
          </div>
        </div>
        {!projectId && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 mb-3">
            Save the project (Section 1 Brief) to enable restaurant enrichment.
          </div>
        )}
        {error && (
          <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-xs text-rose-800 mb-3">
            {error}
          </div>
        )}

        {/* iter225 — Sub-tab toggle: All Competitors | Enriched Only */}
        <div className="flex items-center gap-1 mb-3 border-b border-slate-200"
             data-testid="ei-comp-subtabs">
          <button
            onClick={() => setCompTab('all')}
            data-testid="ei-comp-subtab-all"
            className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
              compTab === 'all'
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            All Competitors · {enrichedPlaces.length}
          </button>
          <button
            onClick={() => setCompTab('enriched')}
            data-testid="ei-comp-subtab-enriched"
            className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
              compTab === 'enriched'
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            Enriched Only · {apifyMatchedCount}
          </button>
        </div>

        {compTab === 'enriched' ? (
          <EnrichedCompetitorsView
            enrichmentMap={enrichmentMap}
            places={enrichedPlaces}
            enrichmentHistory={enrichmentHistory}
            byPlaceId={byPlaceId}
          />
        ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50"><tr className="text-left text-slate-500">
              <th className="py-2 px-2">Influence</th>
              <th className="py-2 px-2">Distance</th>
              <th className="py-2 px-2">Name</th>
              <th className="py-2 px-2">Cuisine</th>
              <th className="py-2 px-2">Format</th>
              <th className="py-2 px-2">Rating</th>
              <th className="py-2 px-2">Reviews</th>
              <th className="py-2 px-2">Price for Two</th>
              <th className="py-2 px-2">Delivery</th>
              <th className="py-2 px-2">Platform</th>
              <th className="py-2 px-2 text-right">Menu</th>
              <th className="py-2 px-2">Enrichment</th>
            </tr></thead>
            <tbody data-testid="ei-competitors-body">
              {enrichedPlaces.map(p => {
                const e = p._enrichment;
                const ap = p._apify;
                const z = e?.zomato_enrichment || {};
                const g = e?.google_details   || {};
                const p4t = ap?.price_for_two
                  ? `₹${Math.round(ap.price_for_two)}`
                  : (z.price_range
                    || (g.price_level === 'PRICE_LEVEL_MODERATE' ? '₹500-1500'
                        : g.price_level === 'PRICE_LEVEL_EXPENSIVE' ? '₹1500-3000'
                        : g.price_level === 'PRICE_LEVEL_VERY_EXPENSIVE' ? '₹3000+'
                        : g.price_level === 'PRICE_LEVEL_INEXPENSIVE' ? 'Budget'
                        : (p.price_level ?? '—')));
                const deliveryCell = ap
                  ? (ap.delivery_supported
                      ? <span className="text-emerald-700 font-medium">Yes</span>
                      : <span className="text-slate-500">No</span>)
                  : (g.service_flags?.delivery == null
                      ? '—'
                      : g.service_flags.delivery
                        ? <span className="text-emerald-700">Yes</span>
                        : <span className="text-slate-500">No</span>);
                const platformCell = ap?.source_platforms?.length
                  ? (ap.source_platforms.length === 2
                      ? 'Both'
                      : ap.source_platforms[0].charAt(0).toUpperCase()
                        + ap.source_platforms[0].slice(1))
                  : '—';
                const menuCell = ap?.menu_item_count ?? '—';
                const isOpen = intelOpenFor === p.place_id;
                return (
                  <CompetitorIntelCard
                    key={p.place_id}
                    place={p}
                    enrichment={e}
                    apifyEnrichment={ap}
                    score={p._influence}
                    open={isOpen}
                    onOpenChange={(o) => setIntelOpenFor(o ? p.place_id : null)}
                    onOpenDeepDive={() => setDeepDive({ place: p, enrichment: e, apifyEnrichment: ap, score: p._influence })}
                  >
                    <tr className={`border-t border-slate-100 cursor-pointer transition-colors ${
                            isOpen ? 'bg-violet-50' : 'hover:bg-violet-50/40'}`}
                        onClick={() => setIntelOpenFor(p.place_id)}
                        data-testid={`comp-row-${p.place_id}`}>
                      <td className="py-1.5 px-2">
                        <span className="inline-flex items-center gap-1 text-violet-700 font-semibold"
                              data-testid={`comp-influence-${p.place_id}`}>
                          <Sparkles className="w-3 h-3"/>{p._influence}
                          <InfluenceSparkline place={p} score={p._influence}/>
                        </span>
                      </td>
                      <td className="py-1.5 px-2 font-mono text-slate-700">
                        {typeof p.distance_m === 'number' ? `${p.distance_m} m` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-slate-800 font-medium">
                        {p.name}
                        {!p.is_fnb && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">non-F&B</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2">{p.derived_cuisine}</td>
                      <td className="py-1.5 px-2">{p.derived_format}</td>
                      <td className="py-1.5 px-2">{p.rating ?? '—'}</td>
                      <td className="py-1.5 px-2">{p.user_rating_count?.toLocaleString?.() ?? p.user_rating_count ?? '—'}</td>
                      <td className="py-1.5 px-2"
                          data-testid={`comp-p4t-${p.place_id}`}>{p4t}</td>
                      <td className="py-1.5 px-2"
                          data-testid={`comp-delivery-${p.place_id}`}>{deliveryCell}</td>
                      <td className="py-1.5 px-2 capitalize"
                          data-testid={`comp-platform-${p.place_id}`}>{platformCell}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-slate-700"
                          data-testid={`comp-menucount-${p.place_id}`}>{menuCell}</td>
                      <td className="py-1.5 px-2"
                          data-testid={`comp-enrich-status-${p.place_id}`}>
                        {ap ? (
                          <span className="inline-flex flex-col">
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                              <CheckCircle2 className="w-3 h-3"/>Enriched
                            </span>
                            {ap.enriched_at && (
                              <span className="text-[10px] text-slate-500"
                                    data-testid={`comp-enrich-time-${p.place_id}`}>
                                {new Date(ap.enriched_at).toLocaleString()}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-400"
                                data-testid={`comp-enrich-pending-${p.place_id}`}>
                            <span className="w-2 h-2 rounded-full bg-slate-300"/>
                            Not enriched
                          </span>
                        )}
                      </td>
                    </tr>
                  </CompetitorIntelCard>
                );
              })}
              {enrichedPlaces.length === 0 && (
                <tr><td colSpan={12} className="py-4 text-center text-slate-500">No competitors returned.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </CardContent></Card>

      <CompetitorDeepDive
        open={!!deepDive}
        onOpenChange={(o) => !o && setDeepDive(null)}
        place={deepDive?.place}
        enrichment={deepDive?.enrichment}
        apifyEnrichment={deepDive?.apifyEnrichment}
        score={deepDive?.score}
      />

      <Card><CardContent className="p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2">Anchor dataset (per bucket)</h3>
        <div className="grid md:grid-cols-2 gap-3" data-testid="ei-anchors-grid">
          {Object.entries(anchors).map(([bucket, payload]) => (
            <div key={bucket} className="rounded-lg border border-slate-200 p-3">
              <div className="flex justify-between items-baseline">
                <h4 className="font-medium text-slate-800 capitalize">{bucket.replace('_', ' ')}</h4>
                <span className="text-xs text-slate-500">{payload.count} found{payload.from_cache ? ' (cached)' : ''}</span>
              </div>
              <ul className="mt-2 text-xs text-slate-700 space-y-1 max-h-56 overflow-y-auto">
                {(payload.places || []).slice(0, 12).map(p => (
                  <li key={p.place_id} className="flex justify-between gap-2 border-b border-slate-50 pb-1">
                    <span className="truncate" title={p.name}>{p.name}</span>
                    <span className="text-slate-500 whitespace-nowrap font-mono">{p.primary_type}</span>
                  </li>
                ))}
                {(payload.places || []).length === 0 && <li className="text-slate-400">none in radius</li>}
              </ul>
            </div>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ─── EVIDENCE TIER (connectivity + ledger) ─────────────────────────── */
function EvidenceTier({ data, gHealth, healthLoading, refreshHealth, sources, refreshSources }) {
  const geoOk    = gHealth?.geocoding?.reachable;
  const placesOk = gHealth?.places?.reachable;
  const diag     = data?.competitors?.diagnostics;
  return (
    <div className="space-y-6" data-testid="ei-tier-evidence-panel">
      <BriefTraceabilityPanel data={data} />
      {diag && <AcquisitionDiagnosticsCard diag={diag} totalCount={data?.competitors?.count} />}
      <Card data-testid="ei-google-health-card"><CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-base font-semibold text-slate-900">Google Maps Platform — connectivity</h3>
          <Button variant="outline" size="sm" onClick={refreshHealth} disabled={healthLoading} data-testid="ei-refresh-health">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${healthLoading ? 'animate-spin' : ''}`}/> Re-probe
          </Button>
        </div>
        {gHealth ? (
          <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
            <div className={`rounded-lg border p-3 ${geoOk ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
              <div className="flex items-center gap-2 font-medium text-slate-800">
                {geoOk ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <AlertTriangle className="w-4 h-4 text-rose-600"/>}
                Geocoding API
              </div>
              <dl className="mt-2 space-y-1 text-slate-600">
                <div className="flex justify-between"><dt>Reachable</dt><dd data-testid="ei-geo-reachable">{String(!!geoOk)}</dd></div>
                <div className="flex justify-between"><dt>Status</dt><dd>{gHealth.geocoding?.status || '—'}</dd></div>
                {gHealth.geocoding?.error_message && (
                  <div className="text-rose-700 mt-1 text-xs">{gHealth.geocoding.error_message}</div>
                )}
              </dl>
            </div>
            <div className={`rounded-lg border p-3 ${placesOk ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
              <div className="flex items-center gap-2 font-medium text-slate-800">
                {placesOk ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <AlertTriangle className="w-4 h-4 text-rose-600"/>}
                Places API (New)
              </div>
              <dl className="mt-2 space-y-1 text-slate-600">
                <div className="flex justify-between"><dt>Reachable</dt><dd data-testid="ei-places-reachable">{String(!!placesOk)}</dd></div>
                <div className="flex justify-between"><dt>HTTP status</dt><dd>{gHealth.places?.status_code || '—'}</dd></div>
                {gHealth.places?.error_message && (
                  <div className="text-rose-700 mt-1 text-xs">{gHealth.places.error_message}</div>
                )}
              </dl>
            </div>
            <div className="sm:col-span-2 text-xs text-slate-500">
              Last validation: {gHealth.last_validation_at_utc || '—'} ·
              Daily usage {gHealth.daily_usage?.total ?? 0} / {gHealth.daily_usage?.cap ?? 0} ·
              Remaining {gHealth.daily_usage?.remaining ?? 0}
            </div>
          </div>
        ) : <p className="text-sm text-slate-500 mt-3">Probing…</p>}
      </CardContent></Card>

      <Card data-testid="ei-source-ledger-card"><CardContent className="p-6">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-slate-500"/> Source Ledger ({sources.length} latest rows)
          </h3>
          <Button variant="outline" size="sm" onClick={refreshSources} data-testid="ei-refresh-sources">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-3">Fetched (IST)</th><th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Endpoint</th><th className="py-2 pr-3">Query</th>
              <th className="py-2 pr-3">Items</th><th className="py-2 pr-3">Conf</th>
              <th className="py-2 pr-3">Used by</th><th className="py-2 pr-3">Attribution</th>
            </tr></thead>
            <tbody data-testid="ei-source-ledger-body">
              {sources.map(s => (
                <tr key={s.id} className="border-b border-slate-100 align-top">
                  <td className="py-1.5 pr-3 text-slate-600 whitespace-nowrap">{s.fetched_at_ist}</td>
                  <td className="py-1.5 pr-3 text-slate-800 font-medium">{s.source_kind}</td>
                  <td className="py-1.5 pr-3 text-slate-600 font-mono">{s.source_endpoint}</td>
                  <td className="py-1.5 pr-3 text-slate-600 font-mono max-w-md truncate" title={JSON.stringify(s.query)}>
                    {JSON.stringify(s.query)}
                  </td>
                  <td className="py-1.5 pr-3 text-slate-800">{s.extracted_count ?? '—'}</td>
                  <td className="py-1.5 pr-3">{s.confidence}{s.degraded && ' ⚠'}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{(s.used_by_scores || []).join(', ')}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{s.attribution}</td>
                </tr>
              ))}
              {sources.length === 0 && (
                <tr><td colSpan={8} className="py-4 text-center text-slate-500">No ledger rows yet — run an analyse above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

function Stat({ label, value, hint, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900 mt-0.5">{String(value)}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}
function MiniTable({ title, headers, rows, testid }) {
  return (
    <div className="rounded-lg border border-slate-200" data-testid={testid}>
      <div className="px-3 py-1.5 border-b text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <table className="min-w-full text-xs">
        <thead><tr className="text-left text-slate-500">
          {headers.map(h => <th key={h} className="px-3 py-1.5 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {r.map((cell, j) => <td key={j} className="px-3 py-1">{cell}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={headers.length} className="px-3 py-2 text-slate-400">—</td></tr>}
        </tbody>
      </table>
    </div>
  );
}



/* ─── REPORT CONTEXT + BRIEF TRACEABILITY (iter189) ─────────────────── */
function ReportContextHeader({ data, brief }) {
  const ctx = data?.analysis_context;
  if (!ctx) return null;
  const briefUpdated = ctx.brief_updated_at_utc ? new Date(ctx.brief_updated_at_utc).getTime() : 0;
  const analysedAt   = ctx.analysed_at_utc ? new Date(ctx.analysed_at_utc).getTime() : 0;
  const stale = briefUpdated && analysedAt && briefUpdated > analysedAt;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2"
         data-testid="ei-report-context-header">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span><strong className="text-slate-800">Location:</strong> {ctx.address || '—'}</span>
        <span><strong className="text-slate-800">Catchment:</strong> {ctx.fnb_radius_m}m F&amp;B · {ctx.anchor_radius_m}m anchors</span>
        {/* ITER251 — Surface the discovery mode used for this analysis so
            the client knows whether the dense (paid) path or the default
            cheap path was run. */}
        {ctx.dense_mode != null && (
          <span data-testid="ei-ctx-dense-mode">
            <strong className="text-slate-800">Discovery:</strong>{' '}
            {ctx.dense_mode ? (
              <span className="text-emerald-700 font-medium">Dense scan</span>
            ) : (
              <span className="text-slate-600">Standard scan</span>
            )}
          </span>
        )}
        <span><strong className="text-slate-800">Analysed:</strong> {ctx.analysed_at_utc ? new Date(ctx.analysed_at_utc).toLocaleString() : '—'}</span>
        {brief?.business_objective && (
          <span><strong className="text-slate-800">Project:</strong> {brief.business_objective.substring(0, 60)}{brief.business_objective.length > 60 ? '…' : ''}</span>
        )}
        {ctx.brief_status && (
          <span><strong className="text-slate-800">Brief:</strong> {ctx.brief_status}</span>
        )}
      </div>
      {stale && (
        <span className="text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded text-[11px]"
              data-testid="ei-stale-warning">
          Brief edited after this analysis — re-run analyse for fresh scores
        </span>
      )}
    </div>
  );
}

const TRACE_FIELDS = [
  { k: 'business_objective',     l: 'Objective' },
  { k: 'venture_type',           l: 'Venture type' },
  { k: 'preferred_cuisines',     l: 'Cuisines' },
  { k: 'preferred_formats',      l: 'Formats' },
  { k: 'delivery_vs_dinein',     l: 'Delivery vs Dine-In' },
  { k: 'veg_only',               l: 'Veg only' },
  { k: 'alcohol_required',       l: 'Alcohol required' },
  { k: 'budget',                 l: 'Budget' },
  { k: 'area_sqft',              l: 'Area (sqft)' },
  { k: 'frontage_ft',            l: 'Frontage (ft)' },
  { k: 'seating_capacity',       l: 'Seating' },
  { k: 'rent_inr_month',         l: 'Rent / month' },
  { k: 'deposit_inr',            l: 'Deposit' },
  { k: 'parking_available',      l: 'Parking' },
  { k: 'property_status',        l: 'Property status' },
  { k: 'franchise_fee_inr',      l: 'Franchise fee' },
  { k: 'competitive_advantages', l: 'USP selections' },
  { k: 'usp_description',        l: 'USP narrative' },
  { k: 'customer_choice_reason', l: 'Why customers will choose us' },
  { k: 'notes',                  l: 'Notes' },
];
function _fmt(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') {
    if ('min_inr' in v || 'max_inr' in v) {
      const f = (n) => n == null ? '?' : '₹' + Number(n).toLocaleString('en-IN');
      return `${f(v.min_inr)} – ${f(v.max_inr)}`;
    }
    return JSON.stringify(v);
  }
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return String(v);
}
function BriefTraceabilityPanel({ data }) {
  const consumed = data?.brief_consumed;
  if (!consumed) return null;
  return (
    <Card data-testid="ei-brief-traceability-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-900">Project Brief Traceability</h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Exact snapshot of every brief field the scoring engines consumed during this analysis.
          If any value here doesn&rsquo;t match what you see in the brief form, re-save the brief and re-run analyse.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-1.5 pr-3">Field</th>
              <th className="py-1.5 pr-3">Consumed value</th>
              <th className="py-1.5">Status</th>
            </tr></thead>
            <tbody data-testid="ei-brief-traceability-body">
              {TRACE_FIELDS.map(f => {
                const v = consumed[f.k];
                const declared = v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length);
                return (
                  <tr key={f.k} className="border-b border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-700 font-medium">{f.l}</td>
                    <td className="py-1.5 pr-3 text-slate-800 font-mono" data-testid={`ei-trace-${f.k}`}>
                      {_fmt(v)}
                    </td>
                    <td className="py-1.5">
                      {declared
                        ? <span className="text-emerald-700">OK</span>
                        : <span className="text-amber-700">Not declared (engine uses neutral fallback, flagged low-evidence)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <details className="mt-3 text-[11px]">
          <summary className="cursor-pointer text-slate-500 select-none">Raw consumed snapshot JSON</summary>
          <pre className="mt-1 bg-slate-50 rounded p-2 max-h-72 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(consumed, null, 2)}
          </pre>
        </details>
      </CardContent>
    </Card>
  );
}


function AcquisitionDiagnosticsCard({ diag, totalCount }) {
  if (!diag) return null;
  const campus = diag.campus || {};
  const counts = diag.raw_counts || {};
  return (
    <Card data-testid="ei-acquisition-diag-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">
              Evidence-Layer acquisition diagnostics
            </h3>
          </div>
          <Pill tone="ok" testid="ei-acq-merged-count">
            {totalCount ?? diag.merged_count ?? 0} competitors acquired
          </Pill>
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Every analyse runs a multi-pass acquisition designed to avoid under-counting
          operators inside high streets, malls, plazas and business parks.
        </p>

        <div className="grid sm:grid-cols-4 gap-3 mt-4 text-sm">
          <PassPill label="Popularity"    n={counts.popularity}     testid="ei-pass-popularity" />
          <PassPill label="Distance ≤300m" n={counts.distance_tight} testid="ei-pass-distance" />
          <PassPill label="Campus text"   n={counts.campus ?? '—'}  testid="ei-pass-campus" />
          <PassPill label="Broad food types" n={counts.broad_types}  testid="ei-pass-broad" />
        </div>

        {/* ITER251 — Dense-mode evidence: grid + brief-text passes,
            search terms actually used, and the per-report Google API
            call count + cost. Hidden when the run was the default
            cheap mode (passes_run does not contain "grid"). */}
        {diag.dense_mode && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm space-y-3"
               data-testid="ei-dense-mode-block">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-700" />
              <strong className="text-slate-800">Dense competitor scan ran</strong>
              <Pill tone="ok" testid="ei-dense-mode-on">ON</Pill>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <PassPill label="Grid tiles" n={counts.grid_tiles ?? 0} testid="ei-pass-grid-tiles" />
              <PassPill label="Grid adds"  n={counts.grid ?? 0}       testid="ei-pass-grid" />
              <PassPill label="Brief-text adds" n={counts.brief_text ?? 0} testid="ei-pass-brief-text" />
              <PassPill label="API calls"  n={diag.api_calls_made ?? '—'} testid="ei-api-calls" />
            </div>
            <div className="text-xs text-slate-600">
              Estimated Google cost: <strong>${diag.estimated_cost_usd?.toFixed(3) ?? '—'}</strong>
            </div>
            {(diag.search_terms_used?.derived?.length > 0 || diag.search_terms_used?.custom?.length > 0) && (
              <div className="text-xs text-slate-700" data-testid="ei-search-terms-used">
                <div className="font-medium text-slate-800 mb-1">Text-search terms used</div>
                {diag.search_terms_used?.derived?.length > 0 && (
                  <div className="mb-1">
                    <span className="uppercase tracking-wide text-[10px] text-slate-500">Derived from brief: </span>
                    {diag.search_terms_used.derived.map((t, i) => (
                      <span key={i} className="inline-block bg-white border border-slate-200 rounded px-1.5 py-0.5 mx-0.5 text-[11px]">{t}</span>
                    ))}
                  </div>
                )}
                {diag.search_terms_used?.custom?.length > 0 && (
                  <div>
                    <span className="uppercase tracking-wide text-[10px] text-slate-500">Custom (analyst): </span>
                    {diag.search_terms_used.custom.map((t, i) => (
                      <span key={i} className="inline-block bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 mx-0.5 text-[11px] text-emerald-900">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
          <Neighbour label="Within 50m"  n={diag.neighbours_within_50m}  testid="ei-neighbours-50" />
          <Neighbour label="Within 100m" n={diag.neighbours_within_100m} testid="ei-neighbours-100" />
          <Neighbour label="Within 200m" n={diag.neighbours_within_200m} testid="ei-neighbours-200" />
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="ei-campus-block">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-500" />
            <strong className="text-slate-800">Campus detection</strong>
            <Pill tone={campus.detected ? 'ok' : 'warn'} testid="ei-campus-detected">
              {campus.detected ? 'Detected' : 'Not detected'}
            </Pill>
          </div>
          {campus.detected && (
            <div className="text-xs text-slate-600 mt-2 space-y-0.5">
              <div>Property name: <strong>{campus.property_name || '—'}</strong></div>
              <div>Trigger token: <code className="text-slate-700">{campus.trigger_token || campus.establishment || '—'}</code></div>
              <div className="text-emerald-700">→ Secondary text-search acquisition was triggered automatically.</div>
            </div>
          )}
        </div>

        {(diag.warnings || []).length > 0 && (
          <div className="mt-4 space-y-1" data-testid="ei-acq-warnings">
            {diag.warnings.map((w, i) => (
              <div key={i} className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {w}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PassPill({ label, n, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-0.5">{n ?? '—'}</div>
      <div className="text-xs text-slate-400">new places added</div>
    </div>
  );
}
function Neighbour({ label, n, testid }) {
  const tone = n >= 5 ? 'text-emerald-700' : n >= 1 ? 'text-amber-700' : 'text-rose-700';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${tone}`}>{n ?? '—'}</div>
      <div className="text-xs text-slate-400">F&amp;B neighbours</div>
    </div>
  );
}



/* ─── PROJECT BRIEF INTAKE (W4.2) ────────────────────────────────────── */
// ITER283c — Sprint 1c: legacy CUISINES chip list retired. Cuisines
// are now supplied via the shared BriefRankedFields component with a
// broad concept list mapped to canonical aliases (see
// /app/frontend/src/config/cuisineChoices.js).
const FORMATS  = ['Fine Dine', 'Casual Dine / Other', 'Cafe', 'QSR',
  'Bar', 'Bakery', 'Dessert', 'Cloud Kitchen'];
// ITER283c — Target segments (mirrors CityIntelligence.js). Engine reads
// `target_segments` as an ORDERED list; rank-1 = index 0.
const TARGET_SEGMENTS = [
  { v: 'students',            l: 'Students' },
  { v: 'young_professionals', l: 'Young professionals' },
  { v: 'corporate_lunch',     l: 'Corporate lunch crowd' },
  { v: 'business_class',      l: 'Business / executives' },
  { v: 'families',            l: 'Families' },
  { v: 'affluent',            l: 'Affluent diners' },
  { v: 'value_seekers',       l: 'Value seekers' },
  { v: 'late_night_diners',   l: 'Late-night diners' },
  { v: 'nightlife_bar_crowd', l: 'Nightlife / bar crowd' },
  { v: 'health_conscious',    l: 'Health-conscious' },
  { v: 'tourists',            l: 'Tourists' },
];
const VENTURE  = [
  // ITER227 — canonical vocabulary (legacy values still accepted by backend
  // validator which maps new_brand → new_concept, existing_brand_expansion
  // → expansion_of_existing_brand).
  { v: 'franchise',                   l: 'Franchise' },
  { v: 'new_concept',                 l: 'New concept' },
  { v: 'expansion_of_existing_brand', l: 'Expansion of existing brand' },
];
const DELIVERY = [
  { v: 'dinein_only',   l: 'Dine-in only' },
  { v: 'dinein_led',    l: 'Dine-in led' },
  { v: 'balanced',      l: 'Balanced' },
  { v: 'delivery_led',  l: 'Delivery led' },
  { v: 'cloud_kitchen', l: 'Cloud kitchen' },
];
const PROPERTY = [
  { v: 'none',         l: 'Not yet scouting' },
  { v: 'scouting',     l: 'Scouting' },
  { v: 'shortlisted',  l: 'Shortlisted' },
  { v: 'loi_signed',   l: 'LOI signed' },
  { v: 'leased',       l: 'Leased' },
  { v: 'owned',        l: 'Owned' },
];
// iter210 — Property TYPE (independent of property status).
const PROPERTY_TYPE = [
  { v: 'standalone',         l: 'Standalone building' },
  { v: 'high_street',        l: 'High street' },
  { v: 'mall',               l: 'Mall / food court' },
  { v: 'mixed_use',          l: 'Mixed-use development' },
  { v: 'commercial_complex', l: 'Commercial complex' },
  { v: 'residential_block',  l: 'Residential block / society' },
  { v: 'hotel_inhouse',      l: 'In-hotel / hospitality' },
  { v: 'cloud_only',         l: 'Cloud kitchen unit' },
];
const USPS = [
  'Lowest Price', 'Premium Experience', 'Fast Service',
  'Authentic Cuisine', 'Family Dining', 'Kids Friendly',
  'Pure Vegetarian', 'Jain Friendly', 'Corporate Lunch',
  'Delivery Focused', 'Cloud Kitchen', 'Health Focused',
  'Entertainment Driven', 'Regional Specialty',
  'Product Innovation', 'Other',
];

function ProjectBriefCard({ brief, headers, onSaved, recomputing, lastAnalyzedAt, address }) {
  const [editing, setEditing] = useState(!brief);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [form,    setForm]    = useState(() => seedForm(brief));

  // iter200 — Brief readiness state + quick-start templates
  const [readiness, setReadiness] = useState(null);
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    if (!API) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/expansion-intelligence/brief/readiness?address=${encodeURIComponent(address || '')}`,
          { headers });
        if (!cancelled) setReadiness(r.data.data);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [brief, address, form]);  // refetch when brief or live form changes
  useEffect(() => {
    if (!API || templates.length) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/expansion-intelligence/brief/quick-start-templates`,
          { headers });
        setTemplates(r.data.data.templates || []);
      } catch (e) { /* ignore */ }
    })();
  }, [templates.length]);

  async function applyTemplate(templateId, label) {
    if (!API) return;
    if (!window.confirm(`Apply the "${label}" template? This will overwrite your current brief draft (you can still edit any field after).`)) return;
    try {
      const r = await axios.post(
        `${API}/api/expansion-intelligence/brief/apply-template`,
        { template_id: templateId }, { headers });
      const newBrief = r.data.data;
      setForm(seedForm(newBrief));
      setEditing(true);
      if (onSaved) onSaved(newBrief);
    } catch (e) {
      alert(`Could not apply template: ${e?.response?.data?.detail || e.message}`);
    }
  }

  function seedForm(b) {
    return {
      business_objective:  b?.business_objective || '',
      concept_name:        b?.concept_name || '',
      venture_type:        b?.venture_type || '',
      preferred_cuisines:  b?.preferred_cuisines || [],
      preferred_formats:   b?.preferred_formats || [],
      // ITER283c — Ranked list of target segments (index 0 = rank 1).
      target_segments:     b?.target_segments || [],
      delivery_vs_dinein:  b?.delivery_vs_dinein || '',
      property_status:     b?.property_status || '',
      property_type:       b?.property_type || '',
      // ITER227 — Mall Marketplace Mode flags
      is_inside_mall:      b?.is_inside_mall ?? null,
      mall_name:           b?.mall_name || '',
      // ITER227 — Working capital (no hidden default)
      working_capital_inr: b?.working_capital_inr ?? '',
      budget_min:          b?.budget?.min_inr ?? '',
      budget_max:          b?.budget?.max_inr ?? '',
      capex_budget:        b?.capex_budget ?? '',
      veg_only:            b?.veg_only ?? null,
      alcohol_required:    b?.alcohol_required ?? null,
      competitive_advantages: b?.competitive_advantages || [],
      usp_description:     b?.usp_description || '',
      // ITER251 — Optional analyst-supplied local-vocab competitor terms.
      competitor_search_terms: b?.competitor_search_terms || [],
      customer_choice_reason: b?.customer_choice_reason || '',
      price_for_two:       b?.price_for_two ?? '',
      area_sqft:           b?.area_sqft ?? '',
      rent_inr_month:      b?.rent_inr_month ?? '',
      deposit_inr:         b?.deposit_inr ?? '',
      seating_capacity:    b?.seating_capacity ?? '',
      staff_count:         b?.staff_count ?? '',
      avg_salary:          b?.avg_salary ?? '',
      // iter220 — Bill-cuts revenue driver
      expected_bill_cuts_per_day: b?.expected_bill_cuts_per_day ?? '',
      franchise_fee_inr:   b?.franchise_fee_inr ?? '',
      frontage_ft:         b?.frontage_ft ?? '',
      parking_available:   b?.parking_available ?? null,
      space_allocation:    b?.space_allocation || null,
      notes:               b?.notes || '',
      status:              b?.status || 'DRAFT',
    };
  }
  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function toggle(field, value) {
    setForm(f => {
      const cur = new Set(f[field] || []);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      return { ...f, [field]: Array.from(cur) };
    });
  }
  async function save(markComplete = false) {
    setSaving(true); setError(null);
    try {
      const body = {
        business_objective: form.business_objective,
        concept_name:       form.concept_name || null,
        venture_type:       form.venture_type || null,
        preferred_cuisines: (form.preferred_cuisines || [])
          .map(c => (c || '').trim())
          .filter(c => c && c.toLowerCase() !== 'other'),
        preferred_formats:  form.preferred_formats,
        // ITER283c — Persist ranked target segments (order = rank).
        target_segments:    (form.target_segments || [])
                              .filter(s => s && String(s).trim()),
        delivery_vs_dinein: form.delivery_vs_dinein || null,
        property_status:    form.property_status || null,
        property_type:      form.property_type || null,
        // ITER227 — Mall Marketplace Mode flags
        is_inside_mall:     form.is_inside_mall,
        mall_name:          form.mall_name || null,
        // ITER227 — Working capital (no hidden default)
        working_capital_inr: form.working_capital_inr === '' ? null : Number(form.working_capital_inr),
        budget: {
          min_inr: form.budget_min === '' ? null : Number(form.budget_min),
          max_inr: form.budget_max === '' ? null : Number(form.budget_max),
        },
        capex_budget:      form.capex_budget === '' ? null : Number(form.capex_budget),
        veg_only:         form.veg_only,
        alcohol_required: form.alcohol_required,
        competitive_advantages: form.competitive_advantages,
        usp_description:  form.usp_description || null,
        // ITER251 — Persist analyst-supplied competitor search terms.
        competitor_search_terms: (form.competitor_search_terms || [])
          .map(t => (t || '').trim()).filter(Boolean),
        customer_choice_reason: form.customer_choice_reason || null,
        price_for_two:    form.price_for_two === '' ? null : Number(form.price_for_two),
        area_sqft:         form.area_sqft === '' ? null : Number(form.area_sqft),
        rent_inr_month:    form.rent_inr_month === '' ? null : Number(form.rent_inr_month),
        deposit_inr:       form.deposit_inr === '' ? null : Number(form.deposit_inr),
        seating_capacity:  form.seating_capacity === '' ? null : Number(form.seating_capacity),
        staff_count:       form.staff_count === '' ? null : Number(form.staff_count),
        avg_salary:        form.avg_salary === '' ? null : Number(form.avg_salary),
        // iter220 — Bill-cuts revenue driver
        expected_bill_cuts_per_day: form.expected_bill_cuts_per_day === ''
                                     ? null : Number(form.expected_bill_cuts_per_day),
        franchise_fee_inr: form.franchise_fee_inr === '' ? null : Number(form.franchise_fee_inr),
        frontage_ft:       form.frontage_ft === '' ? null : Number(form.frontage_ft),
        parking_available: form.parking_available,
        space_allocation:  form.space_allocation || null,
        notes:            form.notes || null,
        status:           markComplete ? 'INTAKE_COMPLETE' : 'DRAFT',
      };
      const r = await axios.post(`${API}/api/expansion-intelligence/brief`, body, { headers });
      onSaved(r.data.data);
      setEditing(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : JSON.stringify(d || e.message));
    } finally { setSaving(false); }
  }

  if (!editing && brief) {
    return (
      <Card data-testid="ei-brief-card-summary">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-900">Project Brief</h2>
              <Pill tone={brief.status === 'INTAKE_COMPLETE' ? 'ok' : 'warn'}
                    testid="ei-brief-status">{brief.status}</Pill>
              {recomputing && (
                <Pill tone="warn" testid="ei-brief-recomputing">
                  <RefreshCw className="w-3 h-3 animate-spin mr-1" /> Recomputing…
                </Pill>
              )}
              {lastAnalyzedAt && !recomputing && (
                <span className="text-xs text-slate-500" data-testid="ei-brief-last-analyzed">
                  Last analysed {lastAnalyzedAt.toLocaleString('en-IN', { hour12: false })}
                </span>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}
                    data-testid="ei-brief-edit">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 mt-3 text-sm">
            <BriefRow label="Objective"     value={brief.business_objective} />
            <BriefRow label="Concept name"  value={brief.concept_name || '—'} />
            <BriefRow label="Venture"       value={prettify(brief.venture_type)} />
            <BriefRow label="Cuisines"      value={(brief.preferred_cuisines || []).join(', ') || '—'} />
            <BriefRow label="Formats"       value={(brief.preferred_formats  || []).join(', ') || '—'} />
            <BriefRow label="Target segments" value={(brief.target_segments  || []).join(', ') || '—'} />
            <BriefRow label="Delivery mix"  value={prettify(brief.delivery_vs_dinein)} />
            <BriefRow label="Price for two" value={brief.price_for_two ? '₹' + Number(brief.price_for_two).toLocaleString('en-IN') : '—'} />
            <BriefRow label="Property"      value={prettify(brief.property_status)} />
            <BriefRow label="Property type" value={prettify(brief.property_type)} />
            {/* ITER227 — Mall Marketplace Mode */}
            <BriefRow label="Inside a mall?" value={brief.is_inside_mall === true ? 'Yes' : brief.is_inside_mall === false ? 'No' : '—'} />
            {brief.is_inside_mall && (
              <BriefRow label="Mall name" value={brief.mall_name || '—'} className="md:col-span-2" />
            )}
            <BriefRow label="Budget (INR)"  value={fmtBudget(brief.budget)} />
            <BriefRow label="CAPEX budget"  value={brief.capex_budget ? '₹' + Number(brief.capex_budget).toLocaleString('en-IN') : '—'} />
            <BriefRow label="Veg only"      value={brief.veg_only === true ? 'Yes' : brief.veg_only === false ? 'No' : '—'} />
            <BriefRow label="Alcohol req’d" value={brief.alcohol_required === true ? 'Yes' : brief.alcohol_required === false ? 'No' : '—'} />
            <BriefRow label="USPs"          value={(brief.competitive_advantages || []).join(', ') || '—'} className="md:col-span-2" />
            <BriefRow label="USP narrative" value={brief.usp_description || '—'} className="md:col-span-2" />
            <BriefRow label="Why customers will choose us" value={brief.customer_choice_reason || '—'} className="md:col-span-2" />
            <BriefRow label="Area (sqft)"    value={brief.area_sqft ?? '—'} />
            <BriefRow label="Frontage (ft)"  value={brief.frontage_ft ?? '—'} />
            <BriefRow label="Seating"        value={brief.seating_capacity ?? '—'} />
            <BriefRow label="Staff count"    value={brief.staff_count ?? '—'} />
            <BriefRow label="Avg salary/staff" value={brief.avg_salary ? '₹' + Number(brief.avg_salary).toLocaleString('en-IN') : '—'} />
            <BriefRow label="Staff cost / month" value={(brief.staff_count && brief.avg_salary) ? '₹' + (Number(brief.staff_count) * Number(brief.avg_salary)).toLocaleString('en-IN') : '—'} />
            <BriefRow label="Rent/month"     value={brief.rent_inr_month ? '₹' + brief.rent_inr_month.toLocaleString('en-IN') : '—'} />
            <BriefRow label="Deposit"        value={brief.deposit_inr ? '₹' + brief.deposit_inr.toLocaleString('en-IN') : '—'} />
            <BriefRow label="Working capital" value={brief.working_capital_inr ? '₹' + brief.working_capital_inr.toLocaleString('en-IN') : '—'} />
            <BriefRow label="Parking"        value={brief.parking_available === true ? 'Yes' : brief.parking_available === false ? 'No' : '—'} />
            <BriefRow label="Notes"         value={brief.notes || '—'} className="md:col-span-2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ei-brief-card-form">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">
              {brief ? 'Edit Project Brief' : 'Add Project Brief'}
            </h2>
          </div>
          {brief && (
            <Button variant="ghost" size="sm" onClick={() => { setForm(seedForm(brief)); setEditing(false); }}
                    data-testid="ei-brief-cancel">
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Capture investor intent before scoring. The catchment engine reads this and flags
          mismatches (e.g. dry-state vs alcohol-required) in the Executive tier.
        </p>

        {/* iter200 — Report Readiness Meter. Gates analysis below 60 %. */}
        {readiness && (
          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3"
               data-testid="ei-brief-readiness">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Report Readiness Meter
              </div>
              <div className={`text-xs font-semibold px-2 py-0.5 rounded ${
                readiness.band === 'consultant_grade' ? 'bg-emerald-100 text-emerald-800' :
                readiness.band === 'ready'            ? 'bg-emerald-50 text-emerald-700' :
                readiness.band === 'warning'          ? 'bg-amber-100 text-amber-800' :
                                                        'bg-rose-100 text-rose-800'
              }`} data-testid="ei-brief-readiness-band">
                {readiness.score}% · {readiness.label}
              </div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${
                readiness.band === 'consultant_grade' ? 'bg-emerald-600' :
                readiness.band === 'ready'            ? 'bg-emerald-500' :
                readiness.band === 'warning'          ? 'bg-amber-500' :
                                                        'bg-rose-500'
              }`} style={{ width: `${readiness.score}%` }} />
            </div>
            <div className="text-xs text-slate-600 mt-2">{readiness.advice}</div>
            {readiness.missing && readiness.missing.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ei-brief-readiness-missing">
                {readiness.missing.map(m => (
                  <span key={m.key}
                        className="text-[10px] uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5">
                    Add: {m.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* iter200 — Quick-start templates. One-click consultant-grade brief. */}
        {templates.length > 0 && (!form.business_objective || form.business_objective.length < 30) && (
          <div className="mt-3" data-testid="ei-brief-quick-start">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">
              Quick-start template
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {templates.map(t => (
                <button type="button" key={t.id}
                        onClick={() => applyTemplate(t.id, t.label)}
                        className="text-left rounded-md border border-slate-200 bg-white px-3 py-2 hover:border-slate-400 hover:bg-amber-50/40 transition-colors"
                        data-testid={`ei-brief-quick-start-${t.id}`}>
                  <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{t.headline}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(t.tags || []).map(tag => (
                      <span key={tag} className="text-[9px] uppercase tracking-wide bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{tag}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 space-y-5">
          {/* ────────────────────────────────────────────────────────
              SECTION 1 — CONCEPT & MARKET INPUTS
              Feeds: Market Intelligence · Market DNA · Concept ↔ Market Fit
                     · USP Intelligence · Executive Summary
          ──────────────────────────────────────────────────────── */}
          <div className="rounded-lg border-2 border-emerald-100 bg-emerald-50/30 p-4 space-y-4"
               data-testid="ei-brief-section-concept">
            <div className="flex items-center gap-2 pb-2 border-b border-emerald-100">
              <Sparkles className="w-4 h-4 text-emerald-700" />
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-emerald-800">Section 1</div>
                <div className="text-sm font-semibold text-slate-900">Concept & Market Inputs</div>
              </div>
              <div className="ml-auto text-[10px] uppercase tracking-wider text-emerald-700/70">
                Powers Market Intelligence · Concept Fit · Executive Summary
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Concept name</label>
                <Input value={form.concept_name}
                       onChange={e => setField('concept_name', e.target.value)}
                       placeholder="e.g. Crust & Co Pizza Bar"
                       data-testid="ei-brief-concept-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Business objective *</label>
                <Input value={form.business_objective}
                       onChange={e => setField('business_objective', e.target.value)}
                       placeholder="e.g. Open a casual-dine concept in Indiranagar, Bengaluru"
                       data-testid="ei-brief-objective" />
              </div>
            </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Venture type</label>
              <select value={form.venture_type} onChange={e => setField('venture_type', e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="ei-brief-venture">
                <option value="">—</option>
                {VENTURE.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Delivery vs Dine-in</label>
              <select value={form.delivery_vs_dinein} onChange={e => setField('delivery_vs_dinein', e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="ei-brief-delivery">
                <option value="">—</option>
                {DELIVERY.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>

          {/* ITER283c — Cuisines + Formats + Segments consolidated into
              the shared ranked-brief component. Broad cuisine list,
              pick-then-order top-5 for cuisines and segments,
              format multi-select (max 2), free-text custom cuisines. */}
          <div data-testid="ei-brief-ranked-fields">
            <BriefRankedFieldsAdapter
              form={form}
              setForm={setForm}
              formatOptions={FORMATS.map(f => ({ id: f, label: f }))}
              segmentOptions={TARGET_SEGMENTS.map(s => ({ id: s.v, label: s.l }))}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Veg-only concept?</label>
              <select value={form.veg_only === null ? '' : String(form.veg_only)}
                      onChange={e => setField('veg_only', e.target.value === '' ? null : e.target.value === 'true')}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="ei-brief-veg">
                <option value="">—</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Alcohol required?</label>
              <select value={form.alcohol_required === null ? '' : String(form.alcohol_required)}
                      onChange={e => setField('alcohol_required', e.target.value === '' ? null : e.target.value === 'true')}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="ei-brief-alcohol">
                <option value="">—</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Price for two (INR)</label>
              <Input type="number" value={form.price_for_two}
                     onChange={e => setField('price_for_two', e.target.value)}
                     placeholder="e.g. 1200" data-testid="ei-brief-price-for-two" />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-slate-500" />
              <label className="text-sm font-medium text-slate-800">Competitive Advantage / USP</label>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Multi-select what differentiates your concept. The engine evaluates fit, defensibility and replicability against the catchment.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="ei-brief-usps">
              {USPS.map(u => {
                const on = (form.competitive_advantages || []).includes(u);
                return (
                  <button type="button" key={u}
                          onClick={() => toggle('competitive_advantages', u)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            on ? 'bg-slate-900 text-amber-300 border-slate-900'
                               : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
                    {u}
                  </button>
                );
              })}
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-slate-500">Describe your USP</label>
              <textarea value={form.usp_description}
                        onChange={e => setField('usp_description', e.target.value)}
                        rows={3}
                        placeholder="e.g. Chef-led Pan-Asian with live sushi counter and seasonal omakase menu"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        data-testid="ei-brief-usp-description" />
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-slate-500">
                Why will customers choose you over existing competitors?
              </label>
              <textarea value={form.customer_choice_reason}
                        onChange={e => setField('customer_choice_reason', e.target.value)}
                        rows={3}
                        placeholder="Concrete reason — not 'better food'. e.g. 'No chef-led Pan-Asian exists within 5km' or 'Office crowd wants ₹350 lunch combos which only one competitor delivers.'"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        data-testid="ei-brief-customer-choice-reason" />
            </div>
            {/* ITER251 — Persisted local-vocab competitor search terms.
                Used only when Dense competitor scan is enabled at analyse
                time. Helps the consultant pre-seed local F&B vocabulary
                that the structured cuisine/format fields can't express. */}
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-slate-500">
                Local competitor vocabulary (optional)
              </label>
              <Input
                value={(form.competitor_search_terms || []).join(', ')}
                onChange={e => setField('competitor_search_terms',
                  e.target.value.split(/[,\n]/).map(s => s.trim()).filter(Boolean))}
                placeholder="darshini, sagar, tiffin centre, military hotel"
                data-testid="ei-brief-competitor-search-terms"
              />
              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                Comma-separated local terms (e.g. <em>darshini</em>, <em>sagar</em>) used by the
                Dense scan to surface small-format venues the structured cuisine
                fields can&apos;t express.
              </p>
            </div>
          </div>

          </div>
          {/* ─── END SECTION 1 ─────────────────────────────────── */}

          {/* ────────────────────────────────────────────────────────
              SECTION 2 — PROJECT & FEASIBILITY INPUTS
              Feeds: Project Feasibility · Space Allocation · CAPEX Engine
                     · Financial Viability
          ──────────────────────────────────────────────────────── */}
          <div className="rounded-lg border-2 border-amber-100 bg-amber-50/30 p-4 space-y-4"
               data-testid="ei-brief-section-feasibility">
            <div className="flex items-center gap-2 pb-2 border-b border-amber-100">
              <ShieldCheck className="w-4 h-4 text-amber-700" />
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-amber-800">Section 2</div>
                <div className="text-sm font-semibold text-slate-900">Project & Feasibility Inputs</div>
              </div>
              <div className="ml-auto text-[10px] uppercase tracking-wider text-amber-700/70">
                Powers Feasibility · CAPEX · Financial Viability
              </div>
            </div>

            <p className="text-xs text-slate-500">
              Leave blank if unknown — the engine falls back gracefully. Direct numeric inputs replace
              percentage placeholders for <span className="font-semibold">Rent</span> and
              <span className="font-semibold"> Staff Cost</span> (Staff Count × Avg Salary).
            </p>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Property status</label>
                <select value={form.property_status} onChange={e => setField('property_status', e.target.value)}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="ei-brief-property">
                  <option value="">—</option>
                  {PROPERTY.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Property type</label>
                <select value={form.property_type} onChange={e => setField('property_type', e.target.value)}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="ei-brief-property-type">
                  <option value="">—</option>
                  {PROPERTY_TYPE.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </div>

            {/* ITER227 P0-4 — Mall Marketplace Mode flags */}
            <div className="grid md:grid-cols-3 gap-3 p-3 rounded-md bg-indigo-50/40 border border-indigo-100">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Inside a mall?</label>
                <select
                  value={form.is_inside_mall === null || form.is_inside_mall === undefined ? '' : (form.is_inside_mall ? 'yes' : 'no')}
                  onChange={e => setField('is_inside_mall',
                    e.target.value === '' ? null : (e.target.value === 'yes'))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="ei-brief-is-inside-mall">
                  <option value="">—</option>
                  <option value="yes">Yes — inside a mall</option>
                  <option value="no">No — standalone / high-street</option>
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Triggers Marketplace Mode (70% internal mall tenants / 30% external catchment).</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wide text-slate-500">
                  Mall name {form.is_inside_mall && <span className="text-rose-600">*</span>}
                </label>
                <Input type="text" value={form.mall_name}
                       onChange={e => setField('mall_name', e.target.value)}
                       placeholder='e.g. "Phoenix Marketcity, Bengaluru"'
                       data-testid="ei-brief-mall-name"
                       disabled={!form.is_inside_mall} />
                <p className="text-[10px] text-slate-500 mt-1">
                  Used to auto-discover internal mall tenants for Marketplace Mode.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Area (sqft)</label>
                <Input type="number" value={form.area_sqft}
                       onChange={e => setField('area_sqft', e.target.value)}
                       placeholder="e.g. 1800" data-testid="ei-brief-area-sqft" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Frontage (ft)</label>
                <Input type="number" value={form.frontage_ft}
                       onChange={e => setField('frontage_ft', e.target.value)}
                       placeholder="e.g. 18" data-testid="ei-brief-frontage-ft" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Seating capacity</label>
                <Input type="number" value={form.seating_capacity}
                       onChange={e => setField('seating_capacity', e.target.value)}
                       placeholder="e.g. 80" data-testid="ei-brief-seating" />
              </div>
            </div>

            {/* iter220 — Bill-cuts revenue driver (PRIMARY) */}
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">
                  Expected Bill Cuts / Day
                  <span className="ml-1 text-[10px] bg-violet-100 text-violet-700 px-1 rounded">revenue driver</span>
                </label>
                <Input type="number" value={form.expected_bill_cuts_per_day}
                       onChange={e => setField('expected_bill_cuts_per_day', e.target.value)}
                       placeholder="e.g. 280"
                       data-testid="ei-brief-bill-cuts" />
                <p className="text-[10px] text-slate-500 mt-1">
                  Revenue = Bill Cuts × APC × Operating Days × ramp curve. If
                  blank, falls back to seats × turns × occupancy.
                </p>
              </div>
              <div className="md:col-span-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3"
                   data-testid="ei-brief-bill-cuts-help">
                <strong>Why this matters.</strong> Modern QSR / café / delivery-led
                concepts are not seat-driven. Set Expected Bill Cuts/Day to make
                the model reflect your real transaction volume. Channel mix
                (Dine-In / Delivery / Takeaway) only redistributes the same
                total — it does <em>not</em> inflate revenue.
              </div>
            </div>

            {/* iter210 — Direct staff-cost inputs (replaces %-of-revenue labor) */}
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Staff count</label>
                <Input type="number" value={form.staff_count}
                       onChange={e => setField('staff_count', e.target.value)}
                       placeholder="e.g. 18" data-testid="ei-brief-staff-count" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Avg salary / staff (INR)</label>
                <Input type="number" value={form.avg_salary}
                       onChange={e => setField('avg_salary', e.target.value)}
                       placeholder="e.g. 22000" data-testid="ei-brief-avg-salary" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Staff cost / month</label>
                <div className="px-3 py-2 text-sm border border-dashed border-slate-300 rounded-md bg-white text-slate-700"
                     data-testid="ei-brief-staff-cost-computed">
                  {(form.staff_count !== '' && form.avg_salary !== '' &&
                    Number(form.staff_count) > 0 && Number(form.avg_salary) > 0)
                    ? '₹' + (Number(form.staff_count) * Number(form.avg_salary)).toLocaleString('en-IN')
                    : <span className="text-slate-400">auto · count × salary</span>}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Rent / month (INR)</label>
                <Input type="number" value={form.rent_inr_month}
                       onChange={e => setField('rent_inr_month', e.target.value)}
                       placeholder="e.g. 250000" data-testid="ei-brief-rent" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Security deposit (INR)</label>
                <Input type="number" value={form.deposit_inr}
                       onChange={e => setField('deposit_inr', e.target.value)}
                       placeholder="e.g. 1500000" data-testid="ei-brief-deposit" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">
                  Franchise fee (INR)
                  {(form.venture_type === 'new_concept' || form.venture_type === 'expansion_of_existing_brand'
                    || form.venture_type === 'new_brand' || form.venture_type === 'existing_brand_expansion') && (
                    <span className="ml-2 text-[9px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">N/A — venture type</span>
                  )}
                </label>
                <Input type="number"
                       value={(form.venture_type === 'new_concept' || form.venture_type === 'expansion_of_existing_brand'
                               || form.venture_type === 'new_brand' || form.venture_type === 'existing_brand_expansion') ? '' : form.franchise_fee_inr}
                       onChange={e => setField('franchise_fee_inr', e.target.value)}
                       placeholder="e.g. 2500000"
                       data-testid="ei-brief-franchise-fee"
                       disabled={form.venture_type === 'new_concept' || form.venture_type === 'expansion_of_existing_brand'
                                 || form.venture_type === 'new_brand' || form.venture_type === 'existing_brand_expansion'} />
              </div>
            </div>

            {/* ITER227 P0-2 — Working capital (no hidden default) */}
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Working capital (INR)</label>
                <Input type="number" value={form.working_capital_inr}
                       onChange={e => setField('working_capital_inr', e.target.value)}
                       placeholder="leave blank if not budgeted"
                       data-testid="ei-brief-working-capital" />
                <p className="text-[10px] text-slate-500 mt-1">
                  Months 1-3 cash buffer for salaries, raw material, marketing.
                  <strong> No hidden defaults</strong> — left blank if not entered.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Budget min (INR)</label>
                <Input type="number" value={form.budget_min}
                       onChange={e => setField('budget_min', e.target.value)}
                       placeholder="e.g. 5000000" data-testid="ei-brief-budget-min" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Budget max (INR)</label>
                <Input type="number" value={form.budget_max}
                       onChange={e => setField('budget_max', e.target.value)}
                       placeholder="e.g. 12000000" data-testid="ei-brief-budget-max" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">CAPEX budget (INR)</label>
                <Input type="number" value={form.capex_budget}
                       onChange={e => setField('capex_budget', e.target.value)}
                       placeholder="e.g. 10000000" data-testid="ei-brief-capex-budget" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500">Parking available?</label>
                <select value={form.parking_available === null ? '' : String(form.parking_available)}
                        onChange={e => setField('parking_available', e.target.value === '' ? null : e.target.value === 'true')}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="ei-brief-parking">
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            <SpaceAllocationEditor
              areaSqft={form.area_sqft === '' ? null : Number(form.area_sqft)}
              preferredFormats={form.preferred_formats}
              value={form.space_allocation}
              onChange={(v) => setField('space_allocation', v)}
            />
          </div>
          {/* ─── END SECTION 2 ─────────────────────────────────── */}

          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Notes</label>
            <textarea value={form.notes} onChange={e => setField('notes', e.target.value)}
                      rows={3} placeholder="Anything else the engine should weigh — partners, timeline, constraints…"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                      data-testid="ei-brief-notes" />
          </div>

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
                 data-testid="ei-brief-error">{error}</div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save(false)} disabled={saving || !form.business_objective.trim()}
                    data-testid="ei-brief-save">
              <Save className="w-3.5 h-3.5 mr-1" /> Save draft
            </Button>
            <Button onClick={() => save(true)} disabled={saving || !form.business_objective.trim()}
                    variant="outline" data-testid="ei-brief-mark-complete">
              Mark intake complete
            </Button>
            {/* ITER283c-fix BUG 3 — Explicit clear. Wipes every field
                on the current draft to empty. Does NOT delete the
                persisted brief server-side (that stays until the
                next Save). */}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (!window.confirm(
                  'Reset the brief draft to empty? All unsaved edits will be lost.'
                )) return;
                setForm(seedForm(null));
              }}
              disabled={saving}
              data-testid="ei-brief-clear"
              className="ml-auto text-slate-600">
              Reset draft
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BriefRow({ label, value, className = '' }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-800">{value || '—'}</div>
    </div>
  );
}
function prettify(v) {
  if (!v) return '—';
  return String(v).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function fmtBudget(b) {
  if (!b) return '—';
  const mn = b.min_inr, mx = b.max_inr;
  if (mn == null && mx == null) return '—';
  const fmt = (x) => x == null ? '?' : new Intl.NumberFormat('en-IN').format(x);
  return `₹${fmt(mn)} – ₹${fmt(mx)}`;
}

/* ─── TAB CONTEXT HEADER (iter193) ───────────────────────────────── */
function TabContextHeader({ analysis, lastAnalyzedAt, recomputing, onOpenGlossary, onRecalculate, activeProjectId, onDownloadExcel, onDownloadPdf, onDownloadMemo }) {
  const loc = analysis?.geocode?.formatted_address || analysis?.geocode?.address || '—';
  const sig = analysis?.market_dna?.signal_quality;
  const conf = sig === 'high' ? 'High Confidence' : sig === 'medium' ? 'Medium Confidence' : 'Low Confidence';
  const tone = sig === 'high' ? 'bg-emerald-100 text-emerald-700' :
               sig === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-rose-100 text-rose-700';
  return (
    <div className="rounded-md bg-slate-900 text-slate-100 px-4 py-2.5 text-sm mb-4 flex flex-wrap items-center gap-x-4 gap-y-1"
         data-testid="ei-tab-context-header">
      <div>
        <span className="text-xs uppercase tracking-wide text-amber-300/70 mr-2">Location Analysed</span>
        <span data-testid="ei-tab-loc">{loc}</span>
      </div>
      {lastAnalyzedAt && (
        <div data-testid="ei-tab-timestamp">
          <span className="text-xs uppercase tracking-wide text-amber-300/70 mr-2">Analysed</span>
          {lastAnalyzedAt.toLocaleString('en-IN', { hour12: false })}
        </div>
      )}
      {sig && (
        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${tone}`}
              data-testid="ei-tab-confidence">{conf}</span>
      )}
      {recomputing && (
        <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
              data-testid="ei-tab-recomputing">
          <RefreshCw className="w-3 h-3 animate-spin" /> Recomputing
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {onRecalculate && (
          <Button size="sm" variant="outline" className="text-slate-100 border-slate-700 hover:bg-slate-800"
                  onClick={onRecalculate} disabled={recomputing}
                  data-testid="ei-tab-recalculate">
            <RefreshCw className="w-4 h-4 mr-1" /> Recalculate
          </Button>
        )}
        {onDownloadExcel && (
          <Button size="sm" variant="outline"
                  className="text-emerald-300 border-emerald-700 hover:bg-emerald-900/40 disabled:opacity-50"
                  onClick={onDownloadExcel}
                  disabled={!activeProjectId || recomputing}
                  title={activeProjectId
                    ? 'Download the 11-sheet Excel workbook for this saved project.'
                    : 'Save the project first — exports are tied to a saved project.'}
                  data-testid="ei-tab-export-excel">
            <FileText className="w-4 h-4 mr-1" /> Download Excel
          </Button>
        )}
        {onDownloadPdf && (
          <Button size="sm" variant="outline"
                  className="text-emerald-300 border-emerald-700 hover:bg-emerald-900/40 disabled:opacity-50"
                  onClick={() => onDownloadPdf({ bumpMajor: false })}
                  disabled={!activeProjectId || recomputing}
                  title={activeProjectId
                    ? 'Generate the full 18-section PDF report. Each download freezes a versioned snapshot (V1.0, V1.1, V2.0 …) for audit trail.'
                    : 'Save the project first — PDFs are tied to a saved project.'}
                  data-testid="ei-tab-export-pdf">
            <FileText className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        )}
        {onDownloadMemo && (
          <Button size="sm" variant="ghost"
                  className="text-amber-300 hover:bg-slate-800 disabled:opacity-50"
                  onClick={onDownloadMemo}
                  disabled={!activeProjectId || recomputing}
                  title={activeProjectId
                    ? 'Single-page Investor Cover Memo — forwardable directly to investors.'
                    : 'Save the project first.'}
                  data-testid="ei-tab-export-memo">
            <FileText className="w-4 h-4 mr-1" /> Memo
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-amber-300 hover:bg-slate-800"
                onClick={onOpenGlossary} data-testid="ei-tab-glossary-btn">
          <FileText className="w-4 h-4 mr-1" /> Glossary
        </Button>
      </div>
    </div>
  );
}

/* ─── GLOSSARY DRAWER (iter193) ──────────────────────────────────── */
function GlossaryDrawer({ open, onClose, API, headers }) {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (!open || data || !API) return;
    axios.get(`${API}/api/expansion-intelligence/glossary`, { headers })
      .then(r => setData(r.data.data))
      .catch(() => {});
  }, [open, data, API, headers]);
  if (!open) return null;
  const terms = (data?.terms || []).filter(t =>
    !query || (t.term.toLowerCase().includes(query.toLowerCase())
              || t.definition.toLowerCase().includes(query.toLowerCase())));
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ei-glossary-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-white overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-slate-900 text-white p-4 flex items-center gap-3">
          <FileText className="w-5 h-5 text-amber-300" />
          <h2 className="text-lg font-semibold">Glossary & Formula Library</h2>
          <span className="text-[10px] text-slate-400 ml-2">{data?.version}</span>
          <Button size="sm" variant="ghost" className="ml-auto text-slate-100"
                  onClick={onClose} data-testid="ei-glossary-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4">
          <Input type="text" value={query} onChange={e => setQuery(e.target.value)}
                 placeholder="Search definitions, formulas…"
                 data-testid="ei-glossary-search" className="mb-4" />
          {!data && <div className="text-sm text-slate-500">Loading…</div>}
          <div className="space-y-4">
            {terms.map((t, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3"
                   data-testid={`ei-glossary-term-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-semibold text-slate-900">{t.term}</h3>
                  <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                    {t.category}
                  </span>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">{t.definition}</p>
                <div className="mt-2 text-[11px] text-slate-600 space-y-1">
                  <div><strong className="text-slate-800">Why it matters:</strong> {t.why_it_matters}</div>
                  {t.formula && t.formula !== '—' && (
                    <div><strong className="text-slate-800">Formula:</strong>{' '}
                      <code className="bg-slate-50 px-1 rounded">{t.formula}</code>
                    </div>
                  )}
                  <div><strong className="text-slate-800">Interpretation:</strong> {t.interpretation}</div>
                </div>
              </div>
            ))}
            {terms.length === 0 && data && (
              <div className="text-sm text-slate-500">No terms match &ldquo;{query}&rdquo;.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SPACE ALLOCATION EDITOR (iter190) ───────────────────────────── */
const SPACE_CATEGORIES = [
  { key: 'kitchen_sqft',          label: 'Kitchen' },
  { key: 'dining_sqft',           label: 'Dining' },
  { key: 'storage_sqft',          label: 'Storage' },
  { key: 'washrooms_sqft',        label: 'Washrooms' },
  { key: 'office_sqft',           label: 'Office / Admin' },
  { key: 'waiting_sqft',          label: 'Waiting' },
  { key: 'delivery_counter_sqft', label: 'Delivery Counter' },
  { key: 'other_sqft',            label: 'Other' },
];
const SPACE_DEFAULT_RATIOS = {
  fine_dine:     { kitchen: 0.25, dining: 0.50, storage: 0.06, washrooms: 0.05, office: 0.04, waiting: 0.06, delivery_counter: 0.00, other: 0.04 },
  bar:           { kitchen: 0.18, dining: 0.55, storage: 0.06, washrooms: 0.06, office: 0.04, waiting: 0.07, delivery_counter: 0.00, other: 0.04 },
  casual_dine:   { kitchen: 0.28, dining: 0.48, storage: 0.08, washrooms: 0.05, office: 0.03, waiting: 0.04, delivery_counter: 0.02, other: 0.02 },
  cafe:          { kitchen: 0.22, dining: 0.52, storage: 0.06, washrooms: 0.05, office: 0.03, waiting: 0.05, delivery_counter: 0.05, other: 0.02 },
  qsr:           { kitchen: 0.32, dining: 0.40, storage: 0.08, washrooms: 0.05, office: 0.03, waiting: 0.04, delivery_counter: 0.06, other: 0.02 },
  bakery:        { kitchen: 0.40, dining: 0.30, storage: 0.10, washrooms: 0.05, office: 0.03, waiting: 0.04, delivery_counter: 0.06, other: 0.02 },
  dessert:       { kitchen: 0.30, dining: 0.40, storage: 0.08, washrooms: 0.05, office: 0.03, waiting: 0.04, delivery_counter: 0.08, other: 0.02 },
  cloud_kitchen: { kitchen: 0.55, dining: 0.00, storage: 0.15, washrooms: 0.05, office: 0.06, waiting: 0.00, delivery_counter: 0.15, other: 0.04 },
};
function normFormat(f) {
  if (!f) return '';
  const s = String(f).trim().toLowerCase();
  const aliases = {
    'fine dine':'fine_dine','fine dining':'fine_dine','fine-dining':'fine_dine',
    'casual dine':'casual_dine','casual dining':'casual_dine',
    'qsr':'qsr','quick service':'qsr','cafe':'cafe','café':'cafe',
    'bar':'bar','pub':'bar','lounge':'bar','bakery':'bakery','dessert':'dessert',
    'cloud kitchen':'cloud_kitchen','delivery only':'cloud_kitchen',
  };
  return aliases[s] || s.replace(/\s+/g, '_');
}
function SpaceAllocationEditor({ areaSqft, preferredFormats, value, onChange }) {
  const fmt = normFormat((preferredFormats || [])[0]);
  const defaults = SPACE_DEFAULT_RATIOS[fmt] || SPACE_DEFAULT_RATIOS.casual_dine;
  const v = value || {};
  const total = SPACE_CATEGORIES.reduce((s, c) => s + (Number(v[c.key]) || 0), 0);
  const drift = areaSqft ? (total - areaSqft) : 0;
  const driftPct = areaSqft ? Math.abs(drift) / areaSqft * 100 : 0;
  const withinTol = !areaSqft || driftPct <= 5;

  function applyDefaults() {
    if (!areaSqft) return;
    const next = {};
    SPACE_CATEGORIES.forEach(c => {
      const k = c.key.replace('_sqft', '');
      next[c.key] = Math.round(areaSqft * (defaults[k] || 0));
    });
    // Re-balance drift into 'other'
    const sum = SPACE_CATEGORIES.reduce((s, c) => s + (next[c.key] || 0), 0);
    next.other_sqft = (next.other_sqft || 0) + (areaSqft - sum);
    onChange(next);
  }
  function clear() { onChange(null); }

  return (
    <div className="border-t border-slate-200 pt-4" data-testid="ei-brief-space-allocation">
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-slate-500" />
        <label className="text-sm font-medium text-slate-800">Space Allocation (editable)</label>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Allocate leased area across 8 categories. CAPEX line items use these
        sqft × per-category ₹/sqft. Total must match leased area within ±5%.
        Click &ldquo;Apply format defaults&rdquo; to seed, then override any cell.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <Button size="sm" variant="outline" onClick={applyDefaults}
                disabled={!areaSqft || !fmt}
                data-testid="ei-brief-space-apply-defaults">
          Apply format defaults {fmt ? `(${fmt})` : ''}
        </Button>
        {value && (
          <Button size="sm" variant="ghost" onClick={clear} data-testid="ei-brief-space-clear">
            Reset to system defaults
          </Button>
        )}
        <div className={`ml-auto text-xs font-medium ${withinTol ? 'text-emerald-700' : 'text-rose-700'}`}
             data-testid="ei-brief-space-total">
          Allocated: {total.toLocaleString('en-IN')} sqft
          {areaSqft != null && (
            <> / Leased: {areaSqft.toLocaleString('en-IN')} sqft
              <span className="ml-1">({drift >= 0 ? '+' : ''}{drift} sqft · {driftPct.toFixed(1)}%)</span>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-500">
              <th className="py-2 px-2">Category</th>
              <th className="py-2 px-2 text-right">Default %</th>
              <th className="py-2 px-2 text-right">Default sqft</th>
              <th className="py-2 px-2 text-right">Your allocation (sqft)</th>
            </tr>
          </thead>
          <tbody>
            {SPACE_CATEGORIES.map(c => {
              const ratioKey = c.key.replace('_sqft', '');
              const ratio = defaults[ratioKey] || 0;
              const defaultSqft = areaSqft ? Math.round(areaSqft * ratio) : '—';
              return (
                <tr key={c.key} className="border-t border-slate-100">
                  <td className="py-2 px-2">{c.label}</td>
                  <td className="py-2 px-2 text-right text-slate-500 font-mono">{(ratio * 100).toFixed(0)}%</td>
                  <td className="py-2 px-2 text-right text-slate-500 font-mono">{defaultSqft}</td>
                  <td className="py-2 px-2 text-right">
                    <Input type="number" value={v[c.key] ?? ''}
                           onChange={e => onChange({ ...(value || {}), [c.key]: e.target.value === '' ? null : Number(e.target.value) })}
                           placeholder="—"
                           className="text-right text-xs"
                           data-testid={`ei-brief-space-${ratioKey}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!withinTol && (
        <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
          Allocation total differs from leased area by {driftPct.toFixed(1)}% — must be ≤ ±5%.
        </div>
      )}
    </div>
  );
}

